import React, { useState, useEffect, useMemo } from 'react';
import { useProperty } from '../context/PropertyContext.js';
import { printTicket } from '../utils/printers/PrinterManager.js';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { parse, format } from 'date-fns';
import { IP } from '../configuracion.js';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tooltip,
  Collapse,
  Divider,
   Grid  // ← Agrega Grid aquí
} from '@mui/material';
import {
  Print,
  ArrowBack,
  DateRange,
  Summarize,
  People,
  AttachMoney,
  Receipt,
  ExpandMore,
  ExpandLess,
  FilterList,
  Warning,
  MonetizationOn,
  Analytics,
  Adjust,
  AccessTime,
  Assessment
} from '@mui/icons-material';
import AjusteChequeForm from './AjusteChequeForm.js';
import '../styles/CorteDeCaja.css';
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend
} from 'recharts';
import CategoryIcon from '@mui/icons-material/Category';
import GroupIcon from '@mui/icons-material/Group';
import StorefrontIcon from '@mui/icons-material/Storefront';
import jsPDF from 'jspdf';
// No importes autoTable ni html2canvas si no los usas

// Colores para las gráficas
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300'];
const chartColors = COLORS;
const BASE_API_JSON = `${IP}/tavpos/backend/data`;
const BASE_API_URL = `${IP}/tavpos/backend`;

const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("POSDatabase", 1);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      db.createObjectStore("data", { keyPath: "key" });
    };
    request.onsuccess = (event) => resolve(event.target.result);
    request.onerror = (event) => reject(event.target.error);
  });
};

const saveToIndexedDB = async (key, data) => {
  try {
    const db = await openDB();
    const tx = db.transaction("data", "readwrite");
    const store = tx.objectStore("data");
    await store.put({ key, data });
    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (error) {
    console.error(`Error saving to IndexedDB for ${key}:`, error);
  }
};

const getFromIndexedDB = async (key) => {
  try {
    const db = await openDB();
    const tx = db.transaction("data", "readonly");
    const store = tx.objectStore("data");
    const request = await store.get(key);
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result?.data);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error(`Error getting from IndexedDB for ${key}:`, error);
    return null;
  }
};

const fetchData = async (baseDataUrl, property, fileName, errorMessage) => {
  const cacheKey = `${property}/${fileName}`;
  console.log(`[fetchData] Intentando cargar ${fileName} para propiedad: ${property}, cacheKey: ${cacheKey}`);

  const cachedData = await getFromIndexedDB(cacheKey);
  if (cachedData && cachedData.devices?.length > 0) {
    console.log(`[fetchData] Datos cargados de IndexedDB:`, cachedData);
    return cachedData;
  }

  const url = `${baseDataUrl}/${property}/${fileName}`;
  console.log(`[fetchData] Solicitando URL: ${url}`);

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => "No response body");
      throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
    }

    const text = await response.text();
    if (!text.trim() || text.trim().startsWith("<!DOCTYPE") || text.trim().startsWith("<html")) {
      throw new Error("Respuesta inválida: HTML o vacía");
    }

    let data;
    try {
      data = JSON.parse(text);
    } catch (jsonError) {
      console.error(`[fetchData] Error al parsear JSON:`, jsonError);
      throw new Error("Formato JSON inválido");
    }

    if (fileName === "devices.json") {
      const allDevices = Array.isArray(data) ? data : data.devices || [];
      const filteredDevices = allDevices.filter(
        (device) => device.Propiedad && device.Propiedad.toLowerCase() === property.toLowerCase()
      );
      const result = { devices: filteredDevices };
      console.log(`[fetchData] Dispositivos filtrados:`, result);
      if (filteredDevices.length === 0) {
        console.warn(`[fetchData] No se encontraron dispositivos para ${property}`);
      }
      await saveToIndexedDB(cacheKey, result);
      return result;
    }

    await saveToIndexedDB(cacheKey, data);
    console.log(`[fetchData] ${fileName} cargado y almacenado en caché`);
    return data;
  } catch (error) {
    console.error(`[fetchData] Error al cargar ${fileName}:`, error);
    throw new Error(errorMessage || `Error cargando ${fileName}: ${error.message}`);
  }
};

// ==============================================
// FUNCIÓN PARA CALCULAR TOTAL DE UN ITEM (como OrderSummary.js)
// ==============================================
const calculateItemTotal = (precio, cantidad, descuento, tasaImpuesto, tasaCS) => {
  const subtotalBase = precio * cantidad;
  const totalConDescuento = Math.max(0, subtotalBase - descuento);
  
  if (totalConDescuento <= 0) {
    return {
      subtotalSinImpuestos: 0,
      impuesto: 0,
      cargoServicio: 0,
      total: 0,
      subtotalBase: subtotalBase,
      descuentoAplicado: descuento
    };
  }
  
  const factorTotal = 1 + (tasaImpuesto / 100) + (tasaCS / 100);
  const subtotalSinImpuestos = totalConDescuento / factorTotal;
  const impuesto = subtotalSinImpuestos * (tasaImpuesto / 100);
  const cargoServicio = subtotalSinImpuestos * (tasaCS / 100);
  const totalFinal = subtotalSinImpuestos + impuesto + cargoServicio;
  
  return {
    subtotalSinImpuestos: subtotalSinImpuestos,
    impuesto: impuesto,
    cargoServicio: cargoServicio,
    total: totalFinal,
    subtotalBase: subtotalBase,
    descuentoAplicado: descuento
  };
};

const CorteDeCaja = ({ show, onClose, t }) => {
  const { property } = useProperty();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiData, setApiData] = useState(null);
  const [salesData, setSalesData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [numberFormat, setNumberFormat] = useState('es-MX');
  const [currency, setCurrency] = useState('MXN');
  const [printStatus, setPrintStatus] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [activeTab, setActiveTab] = useState('corte');
  const [filterText, setFilterText] = useState('');
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');
  const [deviceConfig, setDeviceConfig] = useState(null);
  const [devices, setDevices] = useState([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [impresora, setImpresora] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [dateError, setDateError] = useState(null);
  const [expandedPaymentMethod, setExpandedPaymentMethod] = useState(null);
  const [expandedRevenue, setExpandedRevenue] = useState(null);
  const [expandedCheque, setExpandedCheque] = useState(null);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [selectedCheque, setSelectedCheque] = useState(null);
  const [timezoneConfig, setTimezoneConfig] = useState([]);
  const [revenueDeptos, setRevenueDeptos] = useState({});
  const [productsByRevenue, setProductsByRevenue] = useState({});
  const [fechaApertura, setFechaApertura] = useState(null);
  const [consolidatedTab, setConsolidatedTab] = useState('productos');
const [selectedRevenueForCharts, setSelectedRevenueForCharts] = useState(null);

// Función para obtener/calcular la categoría de un producto
const obtenerCategoriaProducto = (revenue, nombreProducto, productoData = null) => {
  // 1. Si el producto tiene categoría directa, usarla
  if (productoData && (productoData.category || productoData.categoria)) {
    return productoData.category || productoData.categoria;
  }
  
  // 2. Clasificación automática por nombre del producto
  const nombreLower = nombreProducto.toLowerCase();
  
  // Bebidas
  if (nombreLower.includes('agua') || nombreLower.includes('cerveza') || 
      nombreLower.includes('vino') || nombreLower.includes('refresco') ||
      nombreLower.includes('gaseosa') || nombreLower.includes('jugo') ||
      nombreLower.includes('café') || nombreLower.includes('te') ||
      nombreLower.includes('whisky') || nombreLower.includes('vodka') ||
      nombreLower.includes('ron') || nombreLower.includes('cocktail') ||
      nombreLower.includes('modelo') || nombreLower.includes('negra')) {
    return 'Bebidas';
  }
  
  // Alimentos
  if (nombreLower.includes('arrachera') || nombreLower.includes('pollo') ||
      nombreLower.includes('salmon') || nombreLower.includes('hamburguesa') ||
      nombreLower.includes('ensalada') || nombreLower.includes('sopa') ||
      nombreLower.includes('pizza') || nombreLower.includes('pasta') ||
      nombreLower.includes('ceviche') || nombreLower.includes('bowl') ||
      nombreLower.includes('huevos') || nombreLower.includes('desayuno') ||
      nombreLower.includes('sandwich') || nombreLower.includes('taco') ||
      nombreLower.includes('quesadilla') || nombreLower.includes('milanesa')) {
    return 'Alimentos';
  }
  
  // Postres
  if (nombreLower.includes('pastel') || nombreLower.includes('helado') ||
      nombreLower.includes('crema') || nombreLower.includes('flan') ||
      nombreLower.includes('fruta') || nombreLower.includes('volteada')) {
    return 'Postres';
  }
  
  // Por defecto
  return 'Otros';
};

// Función para preparar datos de gráfica con agregación de productos menores
const prepareChartData = (data, topCount = 8) => {
  if (!data || Object.keys(data).length === 0) {
    return [];
  }
  
  // Convertir a array y ordenar por total
  const items = Object.entries(data)
    .map(([name, item]) => ({
      name: name.length > 25 ? name.substring(0, 22) + '...' : name,
      value: item.total || 0,
      cantidad: item.cantidad || 0
    }))
    .sort((a, b) => b.value - a.value);
  
  // Tomar top N y agrupar el resto en "Otros"
  const topItems = items.slice(0, topCount);
  const otherItems = items.slice(topCount);
  
  if (otherItems.length === 0) {
    return topItems;
  }
  
  const othersTotal = otherItems.reduce((sum, item) => sum + item.value, 0);
  const othersCantidad = otherItems.reduce((sum, item) => sum + item.cantidad, 0);
  
  return [
    ...topItems,
    { name: 'Otros', value: othersTotal, cantidad: othersCantidad }
  ];
};

// Función para colores con más variedad
const getChartColors = (dataLength) => {
  const baseColors = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#dc3912', '#ff9900', '#109618', '#990099'];
  return dataLength <= baseColors.length ? baseColors.slice(0, dataLength) : 
    [...baseColors, ...Array(dataLength - baseColors.length).fill('#AAAAAA')];
};

  useEffect(() => {
    if (t) {
      console.log('Received t prop:', t);
      if (t.data) {
        console.log('t.data:', t.data);
        if (!Array.isArray(t.data)) {
          console.warn('t.data is not an array:', typeof t.data, t.data);
        }
      }
    }
  }, [t]);

  useEffect(() => {
    if (show) {
      const savedConfig = localStorage.getItem("currentDeviceConfig");
      if (savedConfig) {
        try {
          const config = JSON.parse(savedConfig);
          setDeviceConfig(config);
          console.log('Configuración de dispositivo cargada:', config);
        } catch (error) {
          console.error("Error parsing device config:", error);
          setError("Error al cargar configuración del dispositivo");
        }
      } else {
        setError("Configuración de dispositivo no encontrada");
      }

      const savedUser = localStorage.getItem("currentUser");
      if (savedUser) {
        try {
          setCurrentUser(JSON.parse(savedUser));
          console.log('Usuario cargado:', currentUser);
        } catch (error) {
          console.error("Error parsing current user:", error);
          setError("Error al cargar datos del usuario");
        }
      }
      
      fetchTimezoneConfig();
    } else {
      setApiData(null);
      setSalesData(null);
      setSelectedDate(null);
      setAvailableDates([]);
      setError(null);
      setPrintStatus({});
      setFilterText('');
      setActiveTab('corte');
      setDevices([]);
      setImpresora(null);
      setExpandedUser(null);
      setUserTypeFilter('all');
      setShowFilters(false);
      setDateError(null);
      setExpandedPaymentMethod(null);
      setExpandedRevenue(null);
      setExpandedCheque(null);
      setAdjustDialogOpen(false);
      setSelectedCheque(null);
      setTimezoneConfig([]);
    }
  }, [show]);

  // ==============================================
  // CARGAR CONFIGURACIÓN DE TIMEZONES
  // ==============================================
  const fetchTimezoneConfig = async () => {
    if (!property) return;
    
    try {
      const configUrl = `${BASE_API_JSON}/${property}/config.json`;
      const response = await fetch(configUrl, {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "include",
      });
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const configData = await response.json();
      console.log('Config.json cargado:', configData);
      
      // ✅ Guardar fecha_apertura
      if (configData.fecha_apertura) {
        setFechaApertura(configData.fecha_apertura);
        console.log('📅 Fecha de apertura cargada:', configData.fecha_apertura);
      } else {
        console.warn('⚠️ No se encontró fecha_apertura en config.json');
      }
      
      // Cargar timezones (TODOS, sin filtrar por hotel)
      if (configData.ifac_hdepto && Array.isArray(configData.ifac_hdepto) && configData.ifac_hdepto.length > 0) {
        const timezones = configData.ifac_hdepto.map(tz => ({
          id: tz.modoid,
          nombre: tz.modo,
          hora_inicio: tz.inicio || '00:00:00',
          hora_fin: tz.fin || '23:59:59',
          depto: tz.depto,
          orden: parseInt(tz.modoid) || 1
        }));
        
        // Eliminar duplicados por nombre
        const uniqueTimezones = [];
        const seenNames = new Set();
        for (const tz of timezones) {
          if (!seenNames.has(tz.nombre)) {
            seenNames.add(tz.nombre);
            uniqueTimezones.push(tz);
          }
        }
        
        // Ordenar por hora de inicio
        uniqueTimezones.sort((a, b) => {
          const horaA = parseInt(a.hora_inicio.split(':')[0]) || 0;
          const horaB = parseInt(b.hora_inicio.split(':')[0]) || 0;
          return horaA - horaB;
        });
        
        setTimezoneConfig(uniqueTimezones);
        console.log('✅ Timezones cargados:', uniqueTimezones);
      } else {
        console.warn('⚠️ No se encontraron timezones en config.json, usando valores por defecto');
        // Timezones por defecto
        setTimezoneConfig([
          { id: 1, nombre: 'Desayunos', hora_inicio: '06:00:00', hora_fin: '11:59:59', orden: 1 },
          { id: 2, nombre: 'Comidas', hora_inicio: '12:00:00', hora_fin: '17:59:59', orden: 2 },
          { id: 3, nombre: 'Cenas', hora_inicio: '18:00:00', hora_fin: '23:59:59', orden: 3 }
        ]);
      }
    } catch (error) {
      console.error('Error cargando config.json:', error);
      // Timezones por defecto en caso de error
      setTimezoneConfig([
        { id: 1, nombre: 'Desayunos', hora_inicio: '06:00:00', hora_fin: '11:59:59', orden: 1 },
        { id: 2, nombre: 'Comidas', hora_inicio: '12:00:00', hora_fin: '17:59:59', orden: 2 },
        { id: 3, nombre: 'Cenas', hora_inicio: '18:00:00', hora_fin: '23:59:59', orden: 3 }
      ]);
    }
  };

  // ==============================================
  // CARGAR DISPOSITIVOS - useEffect SEPARADO
  // ==============================================
  useEffect(() => {
    const fetchDevices = async () => {
      if (!show || !property) {
        console.log('Skipping device fetch: show or property missing', { show, property });
        return;
      }

      setIsLoadingDevices(true);
      setError(null);

      try {
        const data = await fetchData(
          BASE_API_JSON,
          property,
          "devices.json",
          "Error cargando dispositivos"
        );
        const loadedDevices = data.devices || [];
        console.log("Dispositivos cargados:", loadedDevices.map(d => ({
          name: d.name,
          rol: d.Rol,
          prntt: d.PRNTT || "N/A",
          tipo: d.Config?.[0]?.Tipo || "No definido"
        })));

        setDevices(loadedDevices);

        const printer = loadedDevices.find(d => d.Rol === "PRNT");
        if (!printer) {
          const errorMsg = "No se encontró ninguna impresora con rol PRNT";
          console.warn(errorMsg);
          setError(errorMsg);
          setImpresora(null);
        } else {
          setImpresora(printer);
          console.log('Selected printer:', {
            name: printer.name,
            rol: printer.Rol,
            tipo: printer.Config?.[0]?.Tipo || "No definido",
            prntt: printer.PRNTT || "N/A"
          });
        }

        if (loadedDevices.length === 0) {
          setError("No hay dispositivos configurados para esta propiedad");
        } else {
          const printers = loadedDevices.filter(d => d.Rol === "PRNT");
          if (printers.length === 0) {
            console.warn("No se encontraron impresoras para la propiedad:", property);
            setError("Advertencia: No hay impresoras configuradas");
          }
          printers.forEach(printer => {
            if (!printer.Config?.[0]?.Tipo) {
              console.warn(`Impresora ${printer.name} no tiene tipo definido`);
              setError(`Advertencia: La impresora ${printer.name} no tiene tipo definido`);
            }
          });
        }
      } catch (error) {
        console.error("Error al cargar dispositivos:", error);
        setError(`Error al cargar lista de dispositivos: ${error.message}`);
        setImpresora(null);
      } finally {
        setIsLoadingDevices(false);
      }
    };

    if (show) fetchDevices();
  }, [show, property]);

  // ==============================================
  // CARGAR DATOS DE CORTE - useEffect SEPARADO
  // ==============================================
  useEffect(() => {
    const fetchCorteData = async () => {
      if (!show || !property) return;

      setLoading(true);
      setError(null);
      setDateError(null);

      try {
        const [corteResponse, salesResponse] = await Promise.all([
          fetch(`${BASE_API_URL}/lectura_cheques_corte.php?property=${encodeURIComponent(property)}`),
          fetch(`${BASE_API_URL}/lectura_cheques.php?property=${encodeURIComponent(property)}`)
        ]);

        if (!corteResponse.ok) throw new Error(`Error ${corteResponse.status} loading corte data`);
        if (!salesResponse.ok) throw new Error(`Error ${salesResponse.status} loading sales data`);

        const corteData = await corteResponse.json();
        const salesData = await salesResponse.json();

        console.log('API Response (lectura_cheques_corte):', corteData);
        console.log('API Response (lectura_cheques):', salesData);

        if (corteData.success && salesData.success) {
          if (!corteData.data || typeof corteData.data !== 'object') {
            throw new Error('Invalid corte data structure: missing or invalid data object');
          }

          corteData.data.cheques = Array.isArray(corteData.data.cheques) ?
            corteData.data.cheques.map(cheque => {
              if (!cheque.fecha_control) {
                console.warn('Cheque sin fecha_control:', cheque);
                return { ...cheque, fecha_control: '0000-00-00' };
              }
              try {
                const parsedDate = parse(cheque.fecha_control, 'yyyy-MM-dd', new Date());
                const formattedDate = format(parsedDate, 'yyyy-MM-dd');
                console.log(`Normalizing corte date: ${cheque.fecha_control} -> ${formattedDate}`);
                return { ...cheque, fecha_control: formattedDate };
              } catch (error) {
                console.error(`Error normalizing corte date ${cheque.fecha_control}:`, error);
                return { ...cheque, fecha_control: '0000-00-00' };
              }
            }) : [];
          
          corteData.data.vendedores = Array.isArray(corteData.data.vendedores) ? corteData.data.vendedores : [];
          corteData.data.resumen = corteData.data.resumen && typeof corteData.data.resumen === 'object' ? corteData.data.resumen : {};

          if (!salesData.data || !Array.isArray(salesData.data)) {
            throw new Error('Invalid sales data structure: missing or invalid data array');
          }

          salesData.data = salesData.data.map(cheque => {
            if (!cheque.fecha_control) {
              console.warn('Sales cheque sin fecha_control:', cheque);
              return { ...cheque, fecha_control: '0000-00-00' };
            }
            try {
              const parsedDate = parse(cheque.fecha_control, 'yyyy-MM-dd', new Date());
              const formattedDate = format(parsedDate, 'yyyy-MM-dd');
              console.log(`Normalizing sales date: ${cheque.fecha_control} -> ${formattedDate}`);
              return { ...cheque, fecha_control: formattedDate };
            } catch (error) {
              console.error(`Error normalizing sales date ${cheque.fecha_control}:`, error);
              return { ...cheque, fecha_control: '0000-00-00' };
            }
          });

          const corteDateCounts = corteData.data.cheques.reduce((acc, cheque) => {
            if (cheque.fecha_control && cheque.fecha_control !== '0000-00-00') {
              acc[cheque.fecha_control] = (acc[cheque.fecha_control] || 0) + 1;
            }
            return acc;
          }, {});

          const salesDateCounts = salesData.data.reduce((acc, cheque) => {
            if (cheque.fecha_control && cheque.fecha_control !== '0000-00-00') {
              acc[cheque.fecha_control] = (acc[cheque.fecha_control] || 0) + 1;
            }
            return acc;
          }, {});

          const uniqueDates = [...new Set([
            ...Object.keys(corteDateCounts),
            ...Object.keys(salesDateCounts)
          ])].sort((a, b) => new Date(b) - new Date(a))
            .filter(date => (corteDateCounts[date] || 0) > 0 || (salesDateCounts[date] || 0) > 0);

          console.log('Fechas disponibles con conteo:', 
            uniqueDates.map(date => ({
              date,
              corteCount: corteDateCounts[date] || 0,
              salesCount: salesDateCounts[date] || 0
            }))
          );

          setAvailableDates(uniqueDates);
          setApiData(corteData);
          setSalesData(salesData);

          if (uniqueDates.length > 0) {
            setSelectedDate(uniqueDates[0]);
            console.log(`Mostrando datos para ${uniqueDates[0]} con ${corteDateCounts[uniqueDates[0]] || 0} cheques corte y ${salesDateCounts[uniqueDates[0]] || 0} cheques ventas`);
          } else {
            setDateError("No hay datos disponibles para ninguna fecha");
          }
        } else {
          throw new Error(corteData.message || salesData.message || 'Error in received data');
        }
      } catch (error) {
        console.error('Error loading data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (show) fetchCorteData();
  }, [show, property]);

  // En el useEffect que carga productsByRevenue
  useEffect(() => {
  const loadProducts = async () => {
    if (!property || !salesData) return;
    
    const revenues = [...new Set(salesData.data.map(c => c.revenue).filter(Boolean))];
    console.log('loadProducts - Revenues encontrados:', revenues);
    
    const productsMap = {};
    
    for (const revenue of revenues) {
      // Buscar el deptoId desde config.json
      try {
        const configRes = await fetch(`${BASE_API_JSON}/${property}/config.json`);
        const config = await configRes.json();
        
        let deptoId = null;
        if (config.menu) {
          const menuItem = config.menu.find(m => m.nombre === revenue);
          if (menuItem) deptoId = menuItem.depto;
        }
        
        if (!deptoId) {
          console.log(`⚠️ No se encontró depto para revenue: ${revenue}`);
          // Intentar buscar por coincidencia parcial
          if (config.menu) {
            const match = config.menu.find(m => 
              revenue.toLowerCase().includes(m.nombre.toLowerCase()) ||
              m.nombre.toLowerCase().includes(revenue.toLowerCase())
            );
            if (match) {
              deptoId = match.depto;
              console.log(`   ✅ Match parcial encontrado: ${match.nombre} -> depto ${deptoId}`);
            }
          }
        }
        
        if (!deptoId) {
          console.log(`⚠️ No se pudo encontrar deptoId para revenue: ${revenue}`);
          continue;
        }
        
        const url = `${BASE_API_JSON}/${property}/products_${deptoId}.json`;
        console.log(`📦 Cargando productos para ${revenue} desde: ${url}`);
        
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          const key = `products_${deptoId}`;
          const items = data[key] || data.products || [];
          
          productsMap[revenue] = { deptoId, items };
          console.log(`✅ ${revenue}: ${items.length} productos cargados`);
          console.log(`   Primeros 3 productos:`, items.slice(0, 3).map(p => ({ 
            nombre: p.nombre || p.name,
            categoria: p.category || p.categoria 
          })));
        } else {
          console.log(`❌ No se pudo cargar ${url}, status: ${res.status}`);
        }
      } catch (e) {
        console.warn(`Error cargando productos para ${revenue}:`, e);
      }
    }
    
    console.log('📊 productsMap final:', productsMap);
    setProductsByRevenue(productsMap);
  };
  
  loadProducts();
}, [property, salesData]);

  const getChequeTimezone = (cheque) => {
    if (!fechaApertura) {
      console.warn('⚠️ fecha_apertura no disponible, usando lógica simple por hora');
      return getChequeTimezoneSimple(cheque);
    }
    
    // Obtener la fecha/hora del cheque
    let chequeDateTime = null;
    
    // Priorizar diferentes campos donde puede estar la fecha/hora
    if (cheque.fecha_hora_creacion) {
      chequeDateTime = new Date(cheque.fecha_hora_creacion);
    } else if (cheque.fecha_creacion && cheque.hora_creacion) {
      chequeDateTime = new Date(`${cheque.fecha_creacion} ${cheque.hora_creacion}`);
    } else if (cheque.fecha_control && cheque.hora_creacion) {
      chequeDateTime = new Date(`${cheque.fecha_control} ${cheque.hora_creacion}`);
    } else if (cheque.fecha_control) {
      // Si solo tenemos fecha, asumimos que es del día de apertura
      chequeDateTime = new Date(`${cheque.fecha_control} 00:00:00`);
    }
    
    if (!chequeDateTime || isNaN(chequeDateTime.getTime())) {
      console.warn('⚠️ No se pudo determinar fecha/hora del cheque:', cheque.id);
      return 'Otros';
    }
    
    // Parsear fecha_apertura
    const aperturaDate = new Date(fechaApertura);
    if (isNaN(aperturaDate.getTime())) {
      console.warn('⚠️ fecha_apertura inválida:', fechaApertura);
      return getChequeTimezoneSimple(cheque);
    }
    
    // Calcular diferencia en días desde la apertura
    const diffDays = Math.floor((chequeDateTime - aperturaDate) / (1000 * 60 * 60 * 24));
    
    // Determinar el "día de operación" basado en la apertura
    // Si diffDays = 0: mismo día que apertura
    // Si diffDays = 1: día siguiente, etc.
    const operationDay = diffDays >= 0 ? diffDays : 0;
    
    // Obtener hora del cheque (independiente del día)
    const chequeTime = chequeDateTime.toTimeString().slice(0, 8); // HH:MM:SS
    
    // Función para convertir hora a segundos
    const getSeconds = (timeStr) => {
      const parts = timeStr.split(':');
      const horas = parseInt(parts[0]) || 0;
      const minutos = parseInt(parts[1]) || 0;
      const segundos = parseInt(parts[2]) || 0;
      return (horas * 3600) + (minutos * 60) + segundos;
    };
    
    const chequeSeconds = getSeconds(chequeTime);
    
    // Buscar el timezone que corresponde a esta hora
    for (const tz of timezoneConfig) {
      const inicioSeconds = getSeconds(tz.hora_inicio);
      let finSeconds = getSeconds(tz.hora_fin);
      
      // Manejar rangos que cruzan la medianoche (ej: 18:00 a 05:59)
      if (finSeconds < inicioSeconds) {
        // Rango que cruza medianoche
        if (chequeSeconds >= inicioSeconds || chequeSeconds < finSeconds) {
          return tz.nombre;
        }
      } else {
        // Rango normal
        if (chequeSeconds >= inicioSeconds && chequeSeconds < finSeconds) {
          return tz.nombre;
        }
      }
    }
    
    return 'Otros';
  };

  // Función de respaldo (lógica simple por hora, sin fecha_apertura)
  const getChequeTimezoneSimple = (cheque) => {
    let horaCreacion = cheque.hora_creacion || cheque.hora || cheque.fecha_hora;
    
    if (!horaCreacion) {
      return 'Otros';
    }
    
    let horaStr = horaCreacion;
    if (horaCreacion.includes(' ')) {
      horaStr = horaCreacion.split(' ')[1];
    }
    
    const getSeconds = (timeStr) => {
      const parts = timeStr.split(':');
      const horas = parseInt(parts[0]) || 0;
      const minutos = parseInt(parts[1]) || 0;
      const segundos = parseInt(parts[2]) || 0;
      return (horas * 3600) + (minutos * 60) + segundos;
    };
    
    const horaSegundos = getSeconds(horaStr);
    
    for (const tz of timezoneConfig) {
      const inicioSegundos = getSeconds(tz.hora_inicio);
      let finSegundos = getSeconds(tz.hora_fin);
      
      if (finSegundos < inicioSegundos) {
        if (horaSegundos >= inicioSegundos || horaSegundos < finSegundos) {
          return tz.nombre;
        }
      } else {
        if (horaSegundos >= inicioSegundos && horaSegundos < finSegundos) {
          return tz.nombre;
        }
      }
    }
    
    return 'Otros';
  };

  const corteData = useMemo(() => {
    if (!apiData || !apiData.data || !selectedDate || !salesData || !salesData.data) {
      console.log('No data to process: apiData, salesData or selectedDate missing', { apiData, salesData, selectedDate });
      return { 
        users: [], 
        totals: { 
          totalVentas: 0, 
          totalSinImpuestos: 0, 
          totalImpuestos: 0, 
          totalCobros: 0, 
          totalPropinas: 0, 
          totalCambio: 0, 
          totalEnCaja: 0, 
          totalCheques: 0, 
          totalDescuentos: 0,
          totalVentasSinDescuento: 0,
          totalFueraDeBalance: 0,
          totalCargoServicio: 0,
          unbalancedCheques: []
        }, 
        paymentMethods: {}, 
        filteredCheques: [],
        ingresos: {
          byRevenue: {},
          byProduct: {},
          totalVentas: 0,
          totalImpuestos: 0,
          totalDescuentos: 0,
          totalVentasSinDescuento: 0,
          totalCargoServicio: 0
        },
        analisis: []
      };
    }

    const { cheques = [], vendedores = [], resumen = {} } = apiData.data;
    const filteredCheques = cheques.filter(cheque => cheque.fecha_control === selectedDate);
    const filteredSales = salesData.data.filter(cheque => cheque.fecha_control === selectedDate);

    console.log('VENDEDORES ORIGINALES:', vendedores);
    console.log(`Procesando ${filteredCheques.length} cheques corte y ${filteredSales.length} cheques ventas para ${selectedDate}`);

    const userMap = {};
    const paymentMethodsMap = {};
    const totals = {
      totalVentas: 0,
      totalVentasSinDescuento: 0,
      totalSinImpuestos: 0,
      totalImpuestos: 0,
      totalCobros: 0,
      totalPropinas: 0,
      totalCambio: 0,
      totalEnCaja: 0,
      totalCheques: filteredCheques.length,
      totalDescuentos: 0,
      totalFueraDeBalance: 0,
      totalCargoServicio: 0,
      unbalancedCheques: []
    };

    const ingresos = {
      byRevenue: {},
      byProduct: {},
      totalVentas: 0,
      totalImpuestos: 0,
      totalDescuentos: 0,
      totalVentasSinDescuento: 0,
      totalCargoServicio: 0
    };

    const analisis = [];
    const cajerosMap = new Map();

    const vendedorIdToNombre = {};
    vendedores.forEach(vendedor => {
      if (vendedor.id_vendedor) {
        vendedorIdToNombre[vendedor.id_vendedor] = vendedor.nombre_vendedor || 'Sin nombre';
      }
    });

    vendedores.forEach(vendedor => {
      if (vendedor.id_vendedor) {
        userMap[vendedor.id_vendedor] = {
          id: vendedor.id_vendedor,
          nombre: vendedor.nombre_vendedor || 'Sin nombre',
          tipo: 'Vendedor',
          ventas: 0,
          ventasSinImpuestos: 0,
          ventasSinDescuento: 0,
          impuestos: 0,
          propinas: 0,
          cargoServicio: 0,
          chequesVentas: [],
          chequesCobros: [],
          cobros: 0,
          cambio: 0,
          enCaja: 0,
          descuentos: 0,
          fueraDeBalance: 0,
          paymentDetails: {}
        };
      }
    });

    const ventasMap = new Map();
    let totalDescuentosCalculado = 0;
    let totalVentasSinDescuentoCalculado = 0;
    let totalCargoServicioCalculado = 0;
    
    filteredSales.forEach(cheque => {
      let impuestosCheque = 0;
      let netoCheque = 0;
      let descuentosCheque = 0;
      let ventasSinDescuentoCheque = 0;
      let cargoServicioCheque = 0;
      let brutoCheque = parseFloat(cheque.total_cheque) || 0;
      
      const pedidos = Array.isArray(cheque.pedidos) ? cheque.pedidos : [];
      
      pedidos.forEach(pedido => {
        const prods = Array.isArray(pedido.productos) ? pedido.productos : [];
        prods.forEach(producto => {
          if (producto.estado === 'void') return;
          
          const precio = parseFloat(producto.precio) || 0;
          const cantidad = parseFloat(producto.cantidad) || 0;
          const tasaImpuesto = (parseFloat(producto.impuesto) || 0) / 100;
          
          let tasaCS = 0;
          if (producto.cs) tasaCS = parseFloat(producto.cs) / 100;
          else if (producto.cargo_servicio) tasaCS = parseFloat(producto.cargo_servicio) / 100;
          else if (producto.cargo) tasaCS = parseFloat(producto.cargo) / 100;
          
          const descuentoProducto = parseFloat(producto.monto_descuento) || 0;
          
          let totalModificadores = 0;
          let descuentoModificadores = 0;
          const modificadores = Array.isArray(producto.modificadores) ? producto.modificadores : [];
          
          modificadores.forEach(mod => {
            totalModificadores += (parseFloat(mod.precio) || 0) * (parseFloat(mod.cantidad) || 1);
            descuentoModificadores += parseFloat(mod.monto_descuento) || 0;
          });
          
          const ventaSinDescuento = precio * cantidad + totalModificadores;
          ventasSinDescuentoCheque += ventaSinDescuento;
          
          const descuentoTotal = descuentoProducto + descuentoModificadores;
          descuentosCheque += descuentoTotal;
          
          const calculos = calculateItemTotal(precio, cantidad, descuentoTotal, tasaImpuesto * 100, tasaCS * 100);
          
          netoCheque += calculos.subtotalSinImpuestos;
          impuestosCheque += calculos.impuesto;
          cargoServicioCheque += calculos.cargoServicio;
        });
      });
      
      ventasMap.set(cheque.id, {
        id: cheque.id,
        revenue: cheque.revenue,
        bruto: brutoCheque,
        neto: netoCheque,
        impuestos: impuestosCheque,
        descuentos: descuentosCheque,
        ventasSinDescuento: ventasSinDescuentoCheque,
        cargoServicio: cargoServicioCheque,
        pedidos: cheque.pedidos,
        tieneVenta: true
      });
      
      totalDescuentosCalculado += descuentosCheque;
      totalVentasSinDescuentoCalculado += ventasSinDescuentoCheque;
      totalCargoServicioCalculado += cargoServicioCheque;
    });

    totals.totalDescuentos = totalDescuentosCalculado;
    totals.totalVentasSinDescuento = totalVentasSinDescuentoCalculado;
    totals.totalCargoServicio = totalCargoServicioCalculado;

    let totalPropinasFiltradas = 0;
    let totalCargoServicioFiltrado = 0;
    
    filteredCheques.forEach((cheque) => {
      const vendedorId = cheque.id_vendedor;
      const cajero = cheque.nombre_cajero || 'Sin cajero';
      const timezone = getChequeTimezone(cheque);

      if (!userMap[cajero] && cajero !== 'Sin cajero') {
        userMap[cajero] = {
          id: cajero,
          nombre: cajero,
          tipo: 'Cajero',
          ventas: 0,
          ventasSinImpuestos: 0,
          ventasSinDescuento: 0,
          impuestos: 0,
          propinas: 0,
          cargoServicio: 0,
          chequesVentas: [],
          chequesCobros: [],
          cobros: 0,
          cambio: 0,
          enCaja: 0,
          descuentos: 0,
          fueraDeBalance: 0,
          paymentDetails: {}
        };
        cajerosMap.set(cajero, true);
      }

      if (vendedorId && !userMap[vendedorId] && cheque.nombre_vendedor) {
        console.log('Creando vendedor no listado en vendedores.json:', {
          id: vendedorId,
          nombre: cheque.nombre_vendedor
        });
        userMap[vendedorId] = {
          id: vendedorId,
          nombre: cheque.nombre_vendedor,
          tipo: 'Vendedor',
          ventas: 0,
          ventasSinImpuestos: 0,
          ventasSinDescuento: 0,
          impuestos: 0,
          propinas: 0,
          cargoServicio: 0,
          chequesVentas: [],
          chequesCobros: [],
          cobros: 0,
          cambio: 0,
          enCaja: 0,
          descuentos: 0,
          fueraDeBalance: 0,
          paymentDetails: {}
        };
      }

      const importe = parseFloat(cheque.total_cheque) || 0;
      
      const ventaData = ventasMap.get(cheque.id);
      
      let impuestos = 0;
      let sinImpuestos = importe;
      let descuentos = 0;
      let ventasSinDescuento = importe;
      let cargoServicio = 0;
      let tieneVenta = false;
      
      if (ventaData) {
        sinImpuestos = ventaData.neto;
        impuestos = ventaData.impuestos;
        descuentos = ventaData.descuentos;
        ventasSinDescuento = ventaData.ventasSinDescuento;
        cargoServicio = ventaData.cargoServicio;
        tieneVenta = true;
      }

      const pagos = Array.isArray(cheque.pagos) ? cheque.pagos : [];
      
      let propinasCheque = 0;
      pagos.forEach(pago => {
        propinasCheque += parseFloat(pago.monto_propina) || 0;
      });
      totalPropinasFiltradas += propinasCheque;
      totalCargoServicioFiltrado += cargoServicio;
      
      const totalPayments = pagos.reduce((sum, pago) => {
        const montoTotal = parseFloat(pago.monto_total) || 0;
        const montoPropina = parseFloat(pago.monto_propina) || 0;
        const montoCambio = parseFloat(pago.monto_cambio) || 0;
        return sum + (montoTotal - montoPropina - montoCambio);
      }, 0);
      const fueraDeBalance = importe - totalPayments;

      const chequeWithBalance = {
        ...cheque,
        timezone,
        impuestos,
        sinImpuestos,
        descuentos,
        ventasSinDescuento,
        propinas: propinasCheque,
        cargoServicio,
        fueraDeBalance,
        isBalanced: Math.abs(fueraDeBalance) < 0.01,
        tieneVenta,
        ventaData
      };

      if (userMap[vendedorId]) {
        userMap[vendedorId].ventas += importe;
        userMap[vendedorId].ventasSinImpuestos += sinImpuestos;
        userMap[vendedorId].ventasSinDescuento += ventasSinDescuento;
        userMap[vendedorId].impuestos += impuestos;
        userMap[vendedorId].descuentos += descuentos;
        userMap[vendedorId].propinas += propinasCheque;
        userMap[vendedorId].cargoServicio += cargoServicio;
        userMap[vendedorId].chequesVentas.push(chequeWithBalance);
        userMap[vendedorId].fueraDeBalance += fueraDeBalance;
      } else if (vendedorId && vendedorId !== 'unknown' && vendedorId !== null && cheque.nombre_vendedor) {
        userMap[vendedorId] = {
          id: vendedorId,
          nombre: cheque.nombre_vendedor,
          tipo: 'Vendedor',
          ventas: importe,
          ventasSinImpuestos: sinImpuestos,
          ventasSinDescuento: ventasSinDescuento,
          impuestos: impuestos,
          propinas: propinasCheque,
          cargoServicio: cargoServicio,
          chequesVentas: [chequeWithBalance],
          chequesCobros: [],
          cobros: 0,
          cambio: 0,
          enCaja: 0,
          descuentos: descuentos,
          fueraDeBalance: fueraDeBalance,
          paymentDetails: {}
        };
      }

      if (userMap[cajero]) {
        userMap[cajero].chequesCobros.push(chequeWithBalance);
        userMap[cajero].fueraDeBalance += fueraDeBalance;
      }

      totals.totalVentas += importe;
      totals.totalVentasSinDescuento += ventasSinDescuento;
      totals.totalSinImpuestos += sinImpuestos;
      totals.totalImpuestos += impuestos;
      totals.totalFueraDeBalance += fueraDeBalance;
      totals.totalCargoServicio += cargoServicio;

      if (Math.abs(fueraDeBalance) >= 0.01) {
        totals.unbalancedCheques.push({
          id: cheque.id,
          fueraDeBalance,
          nombre_vendedor: cheque.nombre_vendedor,
          nombre_cajero: cheque.nombre_cajero
        });
      }

      pagos.forEach((pago) => {
        const montoTotal = parseFloat(pago.monto_total) || 0;
        const montoPropina = parseFloat(pago.monto_propina) || 0;
        const montoCambio = parseFloat(pago.monto_cambio) || 0;
        const montoNeto = montoTotal - montoPropina - montoCambio;
        const paymentMethod = pago.nombre_pago || 'Desconocido';

        if (userMap[cajero]) {
          userMap[cajero].cobros += montoTotal;
          userMap[cajero].cambio += montoCambio;
          userMap[cajero].enCaja += (montoTotal - montoCambio);

          if (!userMap[cajero].paymentDetails[paymentMethod]) {
            userMap[cajero].paymentDetails[paymentMethod] = {
              cobros: 0,
              propinas: 0,
              count: 0,
              montoNeto: 0,
              cheques: []
            };
          }
          userMap[cajero].paymentDetails[paymentMethod].cobros += montoTotal;
          userMap[cajero].paymentDetails[paymentMethod].propinas += montoPropina;
          userMap[cajero].paymentDetails[paymentMethod].montoNeto += montoNeto;
          userMap[cajero].paymentDetails[paymentMethod].count += 1;
          userMap[cajero].paymentDetails[paymentMethod].cheques.push(chequeWithBalance);
        }

        if (!paymentMethodsMap[paymentMethod]) {
          paymentMethodsMap[paymentMethod] = {
            cobros: 0,
            propinas: 0,
            count: 0,
            montoNeto: 0,
            cheques: []
          };
        }
        paymentMethodsMap[paymentMethod].cobros += montoTotal;
        paymentMethodsMap[paymentMethod].propinas += montoPropina;
        paymentMethodsMap[paymentMethod].montoNeto += montoNeto;
        paymentMethodsMap[paymentMethod].count += 1;
        paymentMethodsMap[paymentMethod].cheques.push(chequeWithBalance);
        
        totals.totalCobros += montoTotal;
        totals.totalCambio += montoCambio;
      });
    });

    totals.totalPropinas = totalPropinasFiltradas;
    totals.totalEnCaja = totals.totalCobros - totals.totalCambio;
    ingresos.totalCargoServicio = totalCargoServicioFiltrado;

    filteredSales.forEach(cheque => {
      const ventaData = ventasMap.get(cheque.id);
      if (!ventaData) return;
      
      const revenue = cheque.revenue || 'Desconocido';
      const numClientes = parseInt(cheque.cliente) || 1; // ← LEER CLIENTES
      const importe = parseFloat(cheque.total_cheque) || 0;
      const pedidos = Array.isArray(cheque.pedidos) ? cheque.pedidos : [];
      let chequeDescuentos = 0;
      let chequeImpuestos = 0;
      let chequeSinImpuestos = 0;
      let chequeVentasSinDescuento = 0;
      let chequeCargoServicio = 0;

      if (!ingresos.byRevenue[revenue]) {
        ingresos.byRevenue[revenue] = {
          total: 0,
          ventasSinDescuento: 0,
          impuestos: 0,
          descuentos: 0,
          cargoServicio: 0,
          productos: {},
          cheques: []
        };
      }

      let productos = [];
      let totalSinImpuestos = 0;
      let totalImpuestos = 0;
      let totalDescuentos = 0;
      let totalVentasSinDescuento = 0;
      let totalCargoServicioRevenue = 0;

      pedidos.forEach(pedido => {
        const prods = Array.isArray(pedido.productos) ? pedido.productos : [];
        prods.forEach(producto => {
          if (producto.estado === 'void') return;

          const precio = parseFloat(producto.precio) || 0;
          const cantidad = parseFloat(producto.cantidad) || 0;
          const tasaImpuesto = (parseFloat(producto.impuesto) || 0) / 100;
          
          let tasaCS = 0;
          if (producto.cs) tasaCS = parseFloat(producto.cs) / 100;
          else if (producto.cargo_servicio) tasaCS = parseFloat(producto.cargo_servicio) / 100;
          else if (producto.cargo) tasaCS = parseFloat(producto.cargo) / 100;
          
          const descuentoProducto = parseFloat(producto.monto_descuento) || 0;
          
          let totalModificadores = 0;
          let descuentoModificadores = 0;
          const modificadores = Array.isArray(producto.modificadores) ? producto.modificadores : [];
          
          modificadores.forEach(mod => {
            totalModificadores += (parseFloat(mod.precio) || 0) * (parseFloat(mod.cantidad) || 1);
            descuentoModificadores += parseFloat(mod.monto_descuento) || 0;
          });
          
          const descuentoTotal = descuentoProducto + descuentoModificadores;
          const ventaSinDescuento = precio * cantidad + totalModificadores;
          
          const calculos = calculateItemTotal(precio, cantidad, descuentoTotal, tasaImpuesto * 100, tasaCS * 100);

          productos.push({
            nombre: producto.nombre,
            categoria: producto.categoria || 'General',  // ← DEBE ESTAR
            grupo: producto.grupo || 'General',          // ← DEBE ESTAR
            cantidad,
            sinImpuestos: calculos.subtotalSinImpuestos,
            impuestos: calculos.impuesto,
            descuentos: descuentoTotal,
            cargoServicio: calculos.cargoServicio,
            ventaSinDescuento,
            modificadores: modificadores.map(m => ({
              nombre: m.nombre,
              precio: m.precio,
              cantidad: m.cantidad,
              descuento: m.monto_descuento
            }))
          });

          if (!ingresos.byRevenue[revenue].productos[producto.nombre]) {
            ingresos.byRevenue[revenue].productos[producto.nombre] = {
              cantidad: 0,
              total: 0,
              ventasSinDescuento: 0,
              impuestos: 0,
              descuentos: 0,
              cargoServicio: 0
            };
          }

          ingresos.byRevenue[revenue].productos[producto.nombre].cantidad += cantidad;
          ingresos.byRevenue[revenue].productos[producto.nombre].total += calculos.total;
          ingresos.byRevenue[revenue].productos[producto.nombre].ventasSinDescuento += ventaSinDescuento;
          ingresos.byRevenue[revenue].productos[producto.nombre].impuestos += calculos.impuesto;
          ingresos.byRevenue[revenue].productos[producto.nombre].descuentos += descuentoTotal;
          ingresos.byRevenue[revenue].productos[producto.nombre].cargoServicio += calculos.cargoServicio;

          if (!ingresos.byProduct[producto.nombre]) {
            ingresos.byProduct[producto.nombre] = {
              cantidad: 0,
              total: 0,
              ventasSinDescuento: 0,
              impuestos: 0,
              descuentos: 0,
              cargoServicio: 0,
              revenue
            };
          }

          ingresos.byProduct[producto.nombre].cantidad += cantidad;
          ingresos.byProduct[producto.nombre].total += calculos.total;
          ingresos.byProduct[producto.nombre].ventasSinDescuento += ventaSinDescuento;
          ingresos.byProduct[producto.nombre].impuestos += calculos.impuesto;
          ingresos.byProduct[producto.nombre].descuentos += descuentoTotal;
          ingresos.byProduct[producto.nombre].cargoServicio += calculos.cargoServicio;

          ingresos.totalVentas += calculos.total;
          ingresos.totalImpuestos += calculos.impuesto;
          ingresos.totalDescuentos += descuentoTotal;
          ingresos.totalVentasSinDescuento += ventaSinDescuento;
          ingresos.totalCargoServicio += calculos.cargoServicio;
          
          chequeDescuentos += descuentoTotal;
          chequeImpuestos += calculos.impuesto;
          chequeSinImpuestos += calculos.subtotalSinImpuestos;
          chequeVentasSinDescuento += ventaSinDescuento;
          chequeCargoServicio += calculos.cargoServicio;
          totalSinImpuestos += calculos.subtotalSinImpuestos;
          totalImpuestos += calculos.impuesto;
          totalDescuentos += descuentoTotal;
          totalVentasSinDescuento += ventaSinDescuento;
          totalCargoServicioRevenue += calculos.cargoServicio;
        });
      });

      ingresos.byRevenue[revenue].total += importe;
      ingresos.byRevenue[revenue].ventasSinDescuento += chequeVentasSinDescuento;
      ingresos.byRevenue[revenue].impuestos += ventaData.impuestos;
      ingresos.byRevenue[revenue].descuentos += ventaData.descuentos;
      ingresos.byRevenue[revenue].cargoServicio += chequeCargoServicio;
      ingresos.byRevenue[revenue].cheques.push(cheque);

      const corteCheque = filteredCheques.find(c => c.id === cheque.id);
      const pagos = corteCheque ? (Array.isArray(corteCheque.pagos) ? corteCheque.pagos : []) : [];
      const pagosSinPropinas = pagos.map(pago => ({
        nombre_pago: pago.nombre_pago || 'Desconocido',
        monto: (parseFloat(pago.monto_total) || 0) - (parseFloat(pago.monto_propina) || 0) - (parseFloat(pago.monto_cambio) || 0)
      }));

      const totalPagosSinPropinas = pagosSinPropinas.reduce((sum, pago) => sum + pago.monto, 0);
      const fueraDeBalance = importe - totalPagosSinPropinas;

      analisis.push({
        id: cheque.id,
        revenue,
        numClientes, // ← NUEVO
        timezone: getChequeTimezone(cheque),
        productos,
        ventas: {
          total: importe,
          sinImpuestos: totalSinImpuestos,
          impuestos: totalImpuestos,
          descuentos: totalDescuentos,
          ventasSinDescuento: totalVentasSinDescuento,
          cargoServicio: totalCargoServicioRevenue
        },
        pagos: pagosSinPropinas,
        fueraDeBalance,
        isBalanced: Math.abs(fueraDeBalance) < 0.01
      });
    });

    console.log('VENDEDORES FINALES:', Object.values(userMap).filter(u => u.tipo === 'Vendedor').map(v => ({
      id: v.id,
      nombre: v.nombre,
      ventas: v.ventas,
      chequesVentas: v.chequesVentas.length
    })));

    console.log('CAJEROS FINALES:', Object.values(userMap).filter(u => u.tipo === 'Cajero').map(c => ({
      nombre: c.nombre,
      cobros: c.cobros,
      chequesCobros: c.chequesCobros.length
    })));

    return {
      users: Object.values(userMap),
      totals,
      paymentMethods: paymentMethodsMap,
      filteredCheques,
      ingresos,
      analisis
    };
  }, [apiData, salesData, selectedDate, timezoneConfig, obtenerCategoriaProducto]);

  const timezoneData = useMemo(() => {
    if (!corteData.filteredCheques || corteData.filteredCheques.length === 0) {
      return [];
    }

    const timezoneMap = new Map();

    corteData.filteredCheques.forEach(cheque => {
      const timezone = cheque.timezone || getChequeTimezone(cheque) || 'Otros';
      
      if (!timezoneMap.has(timezone)) {
        timezoneMap.set(timezone, {
          name: timezone,
          ventas: 0,
          ventasSinDescuento: 0,
          descuentos: 0,
          subtotal: 0,
          impuestos: 0,
          cargoServicio: 0,
          propinas: 0,
          total: 0,
          clientes: new Set(),
          cheques: []
        });
      }

      const tzData = timezoneMap.get(timezone);
      
      const importe = parseFloat(cheque.total_cheque) || 0;
      const sinImpuestos = cheque.sinImpuestos || importe;
      const impuestos = cheque.impuestos || 0;
      const descuentos = cheque.descuentos || 0;
      const ventasSinDescuento = cheque.ventasSinDescuento || importe;
      const cargoServicio = cheque.cargoServicio || 0;
      const propinas = cheque.propinas || 0;
      
      const total = importe + cargoServicio + propinas;
      
      tzData.ventas += importe;
      tzData.ventasSinDescuento += ventasSinDescuento;
      tzData.descuentos += descuentos;
      tzData.subtotal += sinImpuestos;
      tzData.impuestos += impuestos;
      tzData.cargoServicio += cargoServicio;
      tzData.propinas += propinas;
      tzData.total += total;
      
      if (cheque.client_number) {
        tzData.clientes.add(cheque.client_number);
      } else if (cheque.mesa) {
        tzData.clientes.add(cheque.mesa);
      } else {
        tzData.clientes.add('Sin cliente');
      }
      
      tzData.cheques.push(cheque);
    });

    const result = Array.from(timezoneMap.values()).map(tz => ({
      ...tz,
      clientes: tz.clientes.size,
      chequePromedio: tz.cheques.length > 0 ? tz.total / tz.cheques.length : 0
    }));

    return result.sort((a, b) => a.name.localeCompare(b.name));
  }, [corteData.filteredCheques]);

  const timezoneTotals = useMemo(() => {
    if (timezoneData.length === 0) {
      return {
        ventas: 0,
        ventasSinDescuento: 0,
        descuentos: 0,
        subtotal: 0,
        impuestos: 0,
        cargoServicio: 0,
        propinas: 0,
        total: 0,
        clientes: 0,
        chequePromedio: 0
      };
    }

    const totals = timezoneData.reduce((acc, tz) => ({
      ventas: acc.ventas + tz.ventas,
      ventasSinDescuento: acc.ventasSinDescuento + tz.ventasSinDescuento,
      descuentos: acc.descuentos + tz.descuentos,
      subtotal: acc.subtotal + tz.subtotal,
      impuestos: acc.impuestos + tz.impuestos,
      cargoServicio: acc.cargoServicio + tz.cargoServicio,
      propinas: acc.propinas + tz.propinas,
      total: acc.total + tz.total,
      clientes: acc.clientes + tz.clientes,
      chequePromedio: 0
    }), {
      ventas: 0, ventasSinDescuento: 0, descuentos: 0,
      subtotal: 0, impuestos: 0, cargoServicio: 0,
      propinas: 0, total: 0, clientes: 0, chequePromedio: 0
    });

    const totalCheques = timezoneData.reduce((sum, tz) => sum + tz.cheques.length, 0);
    totals.chequePromedio = totalCheques > 0 ? totals.total / totalCheques : 0;

    return totals;
  }, [timezoneData]);

const daybookMatrixData = useMemo(() => {
  console.log('🔵 DAYBOOK MATRIX - Iniciando procesamiento');

  if (!salesData?.data || salesData.data.length === 0) {
    return { 
      matrix: {}, 
      sortedModos: [],
      resumenGeneral: {
        totalClientes: 0,
        totalCheques: 0,
        totalVentasBrutas: 0,
        totalDescuentos: 0,
        totalGanancia: 0,
        totalImpuestos: 0,
        totalCargoServicio: 0,
        totalFinal: 0,
        chequePromedio: 0,
        totalAlimentos: 0,
        totalBebidas: 0
      },
      resumenPorModo: [],
      clientesPorModo: {},
      chequesPorModo: {},
      clientesPorRevenueCategoria: new Map(),
      // NUEVAS ESTRUCTURAS CORREGIDAS
      clientesPorRevenue: new Map(),
      clientesPorTurnoRevenue: new Map(),
      clientesPorTurnoGlobal: new Map()
    };
  }

  const ventasDelDia = salesData.data.filter(venta => venta.fecha_control === selectedDate);
  
  if (ventasDelDia.length === 0) {
    return { 
      matrix: {}, 
      sortedModos: [],
      resumenGeneral: {
        totalClientes: 0,
        totalCheques: 0,
        totalVentasBrutas: 0,
        totalDescuentos: 0,
        totalGanancia: 0,
        totalImpuestos: 0,
        totalCargoServicio: 0,
        totalFinal: 0,
        chequePromedio: 0,
        totalAlimentos: 0,
        totalBebidas: 0
      },
      resumenPorModo: [],
      clientesPorModo: {},
      chequesPorModo: {},
      clientesPorRevenueCategoria: new Map(),
      // NUEVAS ESTRUCTURAS CORREGIDAS
      clientesPorRevenue: new Map(),
      clientesPorTurnoRevenue: new Map(),
      clientesPorTurnoGlobal: new Map()
    };
  }

  // ==============================================
  // 1. OBTENER MODOS ÚNICOS
  // ==============================================
  const modosSet = new Set();
  ventasDelDia.forEach(venta => {
    if (venta.modo && venta.modo !== 'null' && venta.modo !== 'undefined') {
      modosSet.add(venta.modo);
    }
  });
  
  const sortedModos = Array.from(modosSet).sort();

  // ==============================================
  // 2. ESTRUCTURAS PARA CONTAR CLIENTES SIN DUPLICAR
  // ==============================================
  
  // ✅ CLIENTES POR REVENUE (sin duplicar) - Usando Sets
  const clientesSetPorRevenue = new Map(); // revenue -> Set de chequeIds
  const clientesSetPorTurnoRevenue = new Map(); // `${revenue}|${modo}` -> Set de chequeIds
  const clientesSetPorTurnoGlobal = new Map(); // modo -> Set de chequeIds
  
  // clientesPorModo: suma de clientes (campo "cliente") por modo (PARA REFERENCIA, no para promedios)
  const clientesPorModo = {};
  sortedModos.forEach(modo => {
    clientesPorModo[modo] = 0;
  });
  clientesPorModo['Totales'] = 0;

  // chequesPorModo: conteo de cheques por modo
  const chequesPorModo = {};
  sortedModos.forEach(modo => {
    chequesPorModo[modo] = 0;
  });
  chequesPorModo['Totales'] = 0;

  // clientesPorRevenueCategoria: PARA REFERENCIA SOLO (puede tener duplicados)
  const clientesPorRevenueCategoria = new Map();

  // Guardar datos básicos por cheque
  const revenuePorCheque = new Map(); // key: chequeId, value: revenue
  const modoPorCheque = new Map();    // key: chequeId, value: modo
  const clientesPorCheque = new Map(); // key: chequeId, value: numeroClientes

  // ==============================================
  // 3. PRIMERA PASADA: RECOLECTAR DATOS DE CHEQUES Y CLIENTES ÚNICOS
  // ==============================================
  ventasDelDia.forEach(venta => {
    const chequeId = venta.id;
    const revenue = venta.revenue || venta.centro_ingresos || 'Sin Revenue';
    const modo = venta.modo || 'Otros';
    
    // LEER EL CAMPO "cliente" (número de comensales)
    const numClientes = parseInt(venta.cliente) || 1;
    
    revenuePorCheque.set(chequeId, revenue);
    modoPorCheque.set(chequeId, modo);
    clientesPorCheque.set(chequeId, numClientes);
    
    // ✅ CLIENTES ÚNICOS POR REVENUE (sin duplicar)
    if (!clientesSetPorRevenue.has(revenue)) {
      clientesSetPorRevenue.set(revenue, new Set());
    }
    // Agregamos el chequeId, NO la cantidad de clientes
    // Esto asegura que un cheque cuenta UNA SOLA VEZ por revenue
    clientesSetPorRevenue.get(revenue).add(chequeId);
    
    // ✅ CLIENTES ÚNICOS POR REVENUE + TURNO
    const revenueTurnoKey = `${revenue}|${modo}`;
    if (!clientesSetPorTurnoRevenue.has(revenueTurnoKey)) {
      clientesSetPorTurnoRevenue.set(revenueTurnoKey, new Set());
    }
    clientesSetPorTurnoRevenue.get(revenueTurnoKey).add(chequeId);
    
    // ✅ CLIENTES ÚNICOS POR TURNO GLOBAL
    if (!clientesSetPorTurnoGlobal.has(modo)) {
      clientesSetPorTurnoGlobal.set(modo, new Set());
    }
    clientesSetPorTurnoGlobal.get(modo).add(chequeId);
    
    // Sumar clientes por modo (PARA REFERENCIA - puede tener duplicados si un cheque tiene múltiples categorías)
    if (clientesPorModo[modo] !== undefined) {
      clientesPorModo[modo] += numClientes;
    } else {
      clientesPorModo[modo] = numClientes;
    }
    clientesPorModo['Totales'] += numClientes;
    
    // Contar cheques por modo
    if (chequesPorModo[modo] !== undefined) {
      chequesPorModo[modo]++;
    } else {
      chequesPorModo[modo] = 1;
    }
    chequesPorModo['Totales']++;
  });

  // ✅ CONVERTIR SETS A NÚMEROS DE CLIENTES (multiplicando por los comensales reales)
  // IMPORTANTE: Cada cheque tiene X comensales, debemos sumar los comensales, no solo contar cheques
  const clientesPorRevenue = new Map();
  for (const [revenue, chequeSet] of clientesSetPorRevenue) {
    let totalComensales = 0;
    for (const chequeId of chequeSet) {
      totalComensales += clientesPorCheque.get(chequeId) || 1;
    }
    clientesPorRevenue.set(revenue, totalComensales);
  }
  
  const clientesPorTurnoRevenue = new Map();
  for (const [key, chequeSet] of clientesSetPorTurnoRevenue) {
    let totalComensales = 0;
    for (const chequeId of chequeSet) {
      totalComensales += clientesPorCheque.get(chequeId) || 1;
    }
    clientesPorTurnoRevenue.set(key, totalComensales);
  }
  
  const clientesPorTurnoGlobal = new Map();
  for (const [modo, chequeSet] of clientesSetPorTurnoGlobal) {
    let totalComensales = 0;
    for (const chequeId of chequeSet) {
      totalComensales += clientesPorCheque.get(chequeId) || 1;
    }
    clientesPorTurnoGlobal.set(modo, totalComensales);
  }
  
  // TOTAL CLIENTES ÚNICOS GLOBAL (sin duplicar)
  // Para el total global, usamos un Set de todos los cheques
  const todosLosCheques = new Set();
  ventasDelDia.forEach(venta => {
    todosLosCheques.add(venta.id);
  });
  let totalClientesUnicosGlobal = 0;
  for (const chequeId of todosLosCheques) {
    totalClientesUnicosGlobal += clientesPorCheque.get(chequeId) || 1;
  }

  // ==============================================
  // 4. SEGUNDA PASADA: CONSTRUIR MATRIZ
  // ==============================================
  const matrix = {};
  
  ventasDelDia.forEach(venta => {
    const revenue = venta.revenue || venta.centro_ingresos || 'Sin Revenue';
    const modo = venta.modo || 'Otros';
    const chequeId = venta.id;
    const numClientes = clientesPorCheque.get(chequeId) || 1;
    
    // Extraer productos de la venta
    const productos = [];
    if (venta.pedidos && Array.isArray(venta.pedidos)) {
      venta.pedidos.forEach(pedido => {
        if (pedido.productos && Array.isArray(pedido.productos)) {
          pedido.productos.forEach(prod => {
            if (prod.estado !== 'void') productos.push(prod);
          });
        }
      });
    }
    
    if (productos.length === 0) return;
    
    // Inicializar revenue en matriz
    if (!matrix[revenue]) {
      matrix[revenue] = {};
    }
    
    // Procesar cada producto para la matriz
    productos.forEach(prod => {
      let categoria = prod.categoria || prod.category || 'General';
      
      if (!matrix[revenue][categoria]) {
        matrix[revenue][categoria] = {};
        [...sortedModos, 'Totales'].forEach(modoName => {
          matrix[revenue][categoria][modoName] = {
            gross: 0, discount: 0, ganancia: 0, subtotal: 0, tax: 0, cs: 0, total: 0, count: 0,
            clientes: 0  // REFERENCIA SOLO - puede tener duplicados
          };
        });
      }
      
      const precio = parseFloat(prod.precio) || 0;
      const cantidad = parseFloat(prod.cantidad) || 1;
      const descuento = parseFloat(prod.monto_descuento) || 0;
      
      let impPct = parseFloat(prod.impuesto) || 0;
      if (impPct <= 1 && impPct > 0) impPct = impPct * 100;
      
      let csPct = 0;
      if (prod.cs) csPct = parseFloat(prod.cs);
      else if (prod.cargo_servicio) csPct = parseFloat(prod.cargo_servicio);
      else if (prod.cargo) csPct = parseFloat(prod.cargo);
      if (csPct <= 1 && csPct > 0) csPct = csPct * 100;
      
      const gross = precio * cantidad;
      const ganancia = Math.max(0, gross - descuento);
      const tax = ganancia * (impPct / 100);
      const cs = ganancia * (csPct / 100);
      const total = ganancia + tax + cs;
      
      // Acumular en modo específico
      let modoData = matrix[revenue][categoria][modo];
      if (!modoData) {
        matrix[revenue][categoria][modo] = { gross: 0, discount: 0, ganancia: 0, subtotal: 0, tax: 0, cs: 0, total: 0, count: 0, clientes: 0 };
        modoData = matrix[revenue][categoria][modo];
      }
      modoData.gross += gross;
      modoData.discount += descuento;
      modoData.ganancia += ganancia;
      modoData.subtotal += ganancia;
      modoData.tax += tax;
      modoData.cs += cs;
      modoData.total += total;
      modoData.count += cantidad;
      // ⚠️ CLIENTES POR CATEGORÍA - SOLO REFERENCIA (puede duplicar)
      modoData.clientes += numClientes;
      
      // Acumular en totales
      const totalesData = matrix[revenue][categoria]['Totales'];
      totalesData.gross += gross;
      totalesData.discount += descuento;
      totalesData.ganancia += ganancia;
      totalesData.subtotal += ganancia;
      totalesData.tax += tax;
      totalesData.cs += cs;
      totalesData.total += total;
      totalesData.count += cantidad;
      totalesData.clientes += numClientes;
      
      // Guardar para clientesPorRevenueCategoria (REFERENCIA SOLO)
      const key = `${revenue}|${categoria}`;
      const currentClientes = clientesPorRevenueCategoria.get(key) || 0;
      clientesPorRevenueCategoria.set(key, currentClientes + numClientes);
    });
  });

  // ==============================================
  // 5. CALCULAR RESUMEN GENERAL (usando clientes ÚNICOS)
  // ==============================================
  let totalVentasBrutas = 0;
  let totalDescuentos = 0;
  let totalGanancia = 0;
  let totalImpuestos = 0;
  let totalCargoServicio = 0;
  let totalFinal = 0;
  let totalAlimentos = 0;
  let totalBebidas = 0;
  
  Object.entries(matrix).forEach(([revenue, categories]) => {
    Object.entries(categories).forEach(([catName, modosData]) => {
      const totales = modosData['Totales'];
      if (totales) {
        totalVentasBrutas += totales.gross;
        totalDescuentos += totales.discount;
        totalGanancia += totales.ganancia;
        totalImpuestos += totales.tax;
        totalCargoServicio += totales.cs;
        totalFinal += totales.total;
        
        // Clasificar por categoría para Alimentos/Bebidas
        const categoriaLower = catName.toLowerCase();
        if (categoriaLower.includes('alim') || categoriaLower.includes('comid') || 
            categoriaLower.includes('plat') || categoriaLower.includes('frijol') ||
            categoriaLower.includes('taco') || categoriaLower.includes('torta')) {
          totalAlimentos += totales.total;
        } else if (categoriaLower.includes('bebi') || categoriaLower.includes('agua') || 
                   categoriaLower.includes('refresco') || categoriaLower.includes('cerveza') ||
                   categoriaLower.includes('vino') || categoriaLower.includes('licor')) {
          totalBebidas += totales.total;
        }
      }
    });
  });
  
  const totalCheques = chequesPorModo['Totales'];
  // ✅ USAR CLIENTES ÚNICOS GLOBALES (sin duplicar)
  const totalClientes = totalClientesUnicosGlobal;
  const chequePromedio = totalClientes > 0 ? totalFinal / totalClientes : 0;
  
  const resumenGeneral = {
    totalClientes,
    totalCheques,
    totalVentasBrutas,
    totalDescuentos,
    totalGanancia,
    totalImpuestos,
    totalCargoServicio,
    totalFinal,
    chequePromedio,
    totalAlimentos,
    totalBebidas
  };
  
  // ==============================================
  // 6. CALCULAR RESUMEN POR MODO (usando clientes ÚNICOS por turno)
  // ==============================================
  const resumenPorModo = sortedModos.map(modo => {
    let totalModo = 0;
    let totalAlimentosModo = 0;
    let totalBebidasModo = 0;
    // ✅ USAR CLIENTES ÚNICOS POR TURNO (sin duplicar)
    let totalClientesModo = clientesPorTurnoGlobal.get(modo) || 0;
    
    Object.entries(matrix).forEach(([revenue, categories]) => {
      Object.entries(categories).forEach(([catName, modosData]) => {
        const data = modosData[modo];
        if (data) {
          totalModo += data.total;
          
          const categoriaLower = catName.toLowerCase();
          if (categoriaLower.includes('alim') || categoriaLower.includes('comid') || 
              categoriaLower.includes('plat')) {
            totalAlimentosModo += data.total;
          } else if (categoriaLower.includes('bebi') || categoriaLower.includes('agua') || 
                     categoriaLower.includes('refresco')) {
            totalBebidasModo += data.total;
          }
        }
      });
    });
    
    // ✅ Cheque promedio por modo basado en CLIENTES ÚNICOS
    const chequePromedioModo = totalClientesModo > 0 ? totalModo / totalClientesModo : 0;
    
    return {
      modo,
      clientes: totalClientesModo,
      cheques: chequesPorModo[modo] || 0,
      chequePromedio: chequePromedioModo,
      totalAlimentos: totalAlimentosModo,
      totalBebidas: totalBebidasModo,
      total: totalModo
    };
  });

  console.log('📊 Resumen General (CORREGIDO):', resumenGeneral);
  console.log('📊 Clientes por Revenue (sin duplicar):', Object.fromEntries(clientesPorRevenue));
  console.log('📊 Clientes por Turno (sin duplicar):', Object.fromEntries(clientesPorTurnoGlobal));
  console.log('📊 Cheques por modo:', chequesPorModo);

  return { 
    matrix, 
    sortedModos, 
    resumenGeneral, 
    resumenPorModo, 
    clientesPorModo,      // REFERENCIA SOLO
    chequesPorModo,
    clientesPorRevenueCategoria, // REFERENCIA SOLO
    // NUEVAS ESTRUCTURAS CORREGIDAS (sin duplicación)
    clientesPorRevenue,
    clientesPorTurnoRevenue,
    clientesPorTurnoGlobal
  };
}, [salesData, selectedDate]);

// ==============================================
// DATOS CONSOLIDADOS POR REVENUE/DEPTO (ÚNICO)
// ==============================================

// ==============================================
// DATOS CONSOLIDADOS POR REVENUE/DEPTO - CORREGIDO
// ==============================================

const revenueConsolidatedData = useMemo(() => {
  const result = {};
  
  if (!corteData.analisis || corteData.analisis.length === 0) {
    console.log('No hay datos de análisis para consolidar');
    return result;
  }

  console.log('Procesando revenueConsolidatedData con cheques:', corteData.analisis.length);

  corteData.analisis.forEach(cheque => {
    const revenue = cheque.revenue || 'Sin Revenue';
    
    if (!result[revenue]) {
      result[revenue] = {
        byProduct: {},
        byCategory: {},
        byGroup: {},
        totalVentas: 0,
        totalSinImpuestos: 0,
        totalImpuestos: 0,
        totalDescuentos: 0,
        totalVentasSinDescuento: 0,
        totalCargoServicio: 0,
        totalCantidad: 0,
        totalCheques: 0,
        totalClientes: 0
      };
    }
    
    const revenueData = result[revenue];
    revenueData.totalCheques += 1;
    revenueData.totalClientes += cheque.numClientes || 1;
    
    (cheque.productos || []).forEach(producto => {
      const nombre = producto.nombre || 'Producto sin nombre';
      
      // ✅ IMPORTANTE: Tomar categoria y grupo DIRECTAMENTE del producto
      // Si no vienen, usar valores por defecto
      const categoria = producto.categoria || producto.category || 'General';
      const grupo = producto.grupo || revenue;  // Usar revenue como grupo por defecto
      
      const cantidad = producto.cantidad || 1;
      const ventaSinDescuento = producto.ventaSinDescuento || 0;
      const sinImpuestos = producto.sinImpuestos || 0;
      const impuestos = producto.impuestos || 0;
      const cargoServicio = producto.cargoServicio || 0;
      const descuentos = producto.descuentos || 0;
      const total = sinImpuestos + impuestos + cargoServicio;
      
      // Log para depuración - ver qué datos vienen
      console.log(`Producto: ${nombre}, Categoría: ${categoria}, Grupo: ${grupo}, Total: ${total}`);
      
      // ==============================================
      // 1. Por PRODUCTO
      // ==============================================
      if (!revenueData.byProduct[nombre]) {
        revenueData.byProduct[nombre] = {
          cantidad: 0, total: 0, ventasSinDescuento: 0,
          impuestos: 0, descuentos: 0, cargoServicio: 0, sinImpuestos: 0,
          categoria: categoria,     // ✅ Guardar categoría
          grupo: grupo              // ✅ Guardar grupo
        };
      }
      revenueData.byProduct[nombre].cantidad += cantidad;
      revenueData.byProduct[nombre].total += total;
      revenueData.byProduct[nombre].ventasSinDescuento += ventaSinDescuento;
      revenueData.byProduct[nombre].impuestos += impuestos;
      revenueData.byProduct[nombre].descuentos += descuentos;
      revenueData.byProduct[nombre].cargoServicio += cargoServicio;
      revenueData.byProduct[nombre].sinImpuestos += sinImpuestos;
      
      // ==============================================
      // 2. Por CATEGORÍA - AGREGAR/ACUMULAR
      // ==============================================
      if (!revenueData.byCategory[categoria]) {
        revenueData.byCategory[categoria] = {
          cantidad: 0, total: 0, ventasSinDescuento: 0,
          impuestos: 0, descuentos: 0, cargoServicio: 0, sinImpuestos: 0
        };
      }
      revenueData.byCategory[categoria].cantidad += cantidad;
      revenueData.byCategory[categoria].total += total;
      revenueData.byCategory[categoria].ventasSinDescuento += ventaSinDescuento;
      revenueData.byCategory[categoria].impuestos += impuestos;
      revenueData.byCategory[categoria].descuentos += descuentos;
      revenueData.byCategory[categoria].cargoServicio += cargoServicio;
      revenueData.byCategory[categoria].sinImpuestos += sinImpuestos;
      
      // ==============================================
      // 3. Por GRUPO - AGREGAR/ACUMULAR
      // ==============================================
      if (!revenueData.byGroup[grupo]) {
        revenueData.byGroup[grupo] = {
          cantidad: 0, total: 0, ventasSinDescuento: 0,
          impuestos: 0, descuentos: 0, cargoServicio: 0, sinImpuestos: 0
        };
      }
      revenueData.byGroup[grupo].cantidad += cantidad;
      revenueData.byGroup[grupo].total += total;
      revenueData.byGroup[grupo].ventasSinDescuento += ventaSinDescuento;
      revenueData.byGroup[grupo].impuestos += impuestos;
      revenueData.byGroup[grupo].descuentos += descuentos;
      revenueData.byGroup[grupo].cargoServicio += cargoServicio;
      revenueData.byGroup[grupo].sinImpuestos += sinImpuestos;
      
      // Métricas generales
      revenueData.totalVentas += total;
      revenueData.totalSinImpuestos += sinImpuestos;
      revenueData.totalImpuestos += impuestos;
      revenueData.totalDescuentos += descuentos;
      revenueData.totalVentasSinDescuento += ventaSinDescuento;
      revenueData.totalCargoServicio += cargoServicio;
      revenueData.totalCantidad += cantidad;
    });
  });
  
  console.log('📊 revenueConsolidatedData result:', 
    Object.keys(result).map(r => ({
      revenue: r,
      totalVentas: result[r].totalVentas,
      categorias: Object.keys(result[r].byCategory),
      grupos: Object.keys(result[r].byGroup),
      productosCount: Object.keys(result[r].byProduct).length,
      // Mostrar primeros productos con sus categorías
      primerosProductos: Object.entries(result[r].byProduct).slice(0, 3).map(([name, data]) => ({
        nombre: name,
        categoria: data.categoria,
        grupo: data.grupo
      }))
    }))
  );
  
  return result;
}, [corteData.analisis]);

// Obtener lista de revenues disponibles
const availableRevenues = useMemo(() => {
  return Object.keys(revenueConsolidatedData).sort();
}, [revenueConsolidatedData]);


// Seleccionar primer revenue por defecto
useEffect(() => {
  if (availableRevenues.length > 0 && !selectedRevenueForCharts) {
    setSelectedRevenueForCharts(availableRevenues[0]);
  }
}, [availableRevenues, selectedRevenueForCharts]);

// ==============================================
// ESTADOS PARA CONSOLIDADAS
// ==============================================
const [expandedGroups, setExpandedGroups] = useState({});
const [expandedCategories, setExpandedCategories] = useState({});

const toggleGroup = (groupName) => {
  setExpandedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }));
};

const toggleCategory = (categoryKey) => {
  setExpandedCategories(prev => ({ ...prev, [categoryKey]: !prev[categoryKey] }));
};

const getCategoriasChartData = (categorias) => {
  if (!categorias || categorias.length === 0) return [];
  return categorias.slice(0, 6).map(c => ({ name: c.nombre, value: c.totalVentas }));
};

const organizarDatosJerarquicos = useMemo(() => {
  const currentRevenueData = selectedRevenueForCharts ? revenueConsolidatedData[selectedRevenueForCharts] : null;
  if (!currentRevenueData) return [];
  
  const gruposMap = new Map();
  
  Object.entries(currentRevenueData.byProduct || {}).forEach(([productoNombre, productoData]) => {
    const grupo = productoData.grupo || 'Otros';
    const categoria = productoData.categoria || 'General';
    
    if (!gruposMap.has(grupo)) {
      gruposMap.set(grupo, {
        nombre: grupo,
        totalVentas: 0,
        totalCantidad: 0,
        categorias: new Map()
      });
    }
    
    const grupoData = gruposMap.get(grupo);
    
    if (!grupoData.categorias.has(categoria)) {
      grupoData.categorias.set(categoria, {
        nombre: categoria,
        totalVentas: 0,
        totalCantidad: 0,
        productos: []
      });
    }
    
    const categoriaData = grupoData.categorias.get(categoria);
    
    categoriaData.productos.push({
      nombre: productoNombre,
      ...productoData
    });
    
    categoriaData.totalVentas += productoData.total || 0;
    categoriaData.totalCantidad += productoData.cantidad || 0;
    grupoData.totalVentas += productoData.total || 0;
    grupoData.totalCantidad += productoData.cantidad || 0;
  });
  
  return Array.from(gruposMap.values())
    .map(grupo => ({
      ...grupo,
      categorias: Array.from(grupo.categorias.values())
        .sort((a, b) => b.totalVentas - a.totalVentas)
    }))
    .sort((a, b) => b.totalVentas - a.totalVentas);
}, [selectedRevenueForCharts, revenueConsolidatedData]);

const gruposChartData = useMemo(() => {
  const top8 = organizarDatosJerarquicos.slice(0, 8).map(g => ({ name: g.nombre, value: g.totalVentas }));
  if (organizarDatosJerarquicos.length <= 8) return top8;
  const otrosTotal = organizarDatosJerarquicos.slice(8).reduce((sum, g) => sum + g.totalVentas, 0);
  return [...top8, { name: 'Otros', value: otrosTotal }];
}, [organizarDatosJerarquicos]);

// ==============================================
// TERMINA ESTADOS PARA CONSOLIDADAS
// ==============================================
// Datos por Categoría
const categorySalesData = useMemo(() => {
  const byCategory = {};
  let totalVentas = 0;
  let totalSinImpuestos = 0;
  let totalImpuestos = 0;
  let totalDescuentos = 0;
  let totalVentasSinDescuento = 0;
  let totalCargoServicio = 0;
  let totalCantidad = 0;

  (corteData.analisis || []).forEach(cheque => {
    (cheque.productos || []).forEach(producto => {
      const categoria = producto.categoria || 'General';
      const cantidad = producto.cantidad || 1;
      const ventaSinDescuento = producto.ventaSinDescuento || 0;
      const total = producto.sinImpuestos + producto.impuestos + (producto.cargoServicio || 0);
      
      if (!byCategory[categoria]) {
        byCategory[categoria] = {
          cantidad: 0,
          total: 0,
          ventasSinDescuento: 0,
          impuestos: 0,
          descuentos: 0,
          cargoServicio: 0
        };
      }
      
      byCategory[categoria].cantidad += cantidad;
      byCategory[categoria].total += total;
      byCategory[categoria].ventasSinDescuento += ventaSinDescuento;
      byCategory[categoria].impuestos += producto.impuestos || 0;
      byCategory[categoria].descuentos += producto.descuentos || 0;
      byCategory[categoria].cargoServicio += producto.cargoServicio || 0;
      
      totalVentas += total;
      totalSinImpuestos += producto.sinImpuestos || 0;
      totalImpuestos += producto.impuestos || 0;
      totalDescuentos += producto.descuentos || 0;
      totalVentasSinDescuento += ventaSinDescuento;
      totalCargoServicio += producto.cargoServicio || 0;
      totalCantidad += cantidad;
    });
  });

  return {
    byCategory,
    totalVentas,
    totalSinImpuestos,
    totalImpuestos,
    totalDescuentos,
    totalVentasSinDescuento,
    totalCargoServicio,
    totalCantidad
  };
}, [corteData.analisis]);

// Datos por Grupo (Revenue Center)
const groupSalesData = useMemo(() => {
  const byGroup = {};
  let totalVentas = 0;
  let totalSinImpuestos = 0;
  let totalImpuestos = 0;
  let totalDescuentos = 0;
  let totalVentasSinDescuento = 0;
  let totalCargoServicio = 0;
  let totalCheques = 0;
  let totalClientes = 0;

  (corteData.analisis || []).forEach(cheque => {
    const grupo = cheque.revenue || 'Desconocido';
    const ventas = cheque.ventas;
    
    if (!byGroup[grupo]) {
      byGroup[grupo] = {
        total: 0,
        ventasSinDescuento: 0,
        impuestos: 0,
        descuentos: 0,
        cargoServicio: 0,
        cheques: 0,
        clientes: 0
      };
    }
    
    byGroup[grupo].total += ventas.total;
    byGroup[grupo].ventasSinDescuento += ventas.ventasSinDescuento;
    byGroup[grupo].impuestos += ventas.impuestos;
    byGroup[grupo].descuentos += ventas.descuentos;
    byGroup[grupo].cargoServicio += ventas.cargoServicio;
    byGroup[grupo].cheques += 1;
    byGroup[grupo].clientes += cheque.numClientes || 1;
    
    totalVentas += ventas.total;
    totalSinImpuestos += ventas.sinImpuestos;
    totalImpuestos += ventas.impuestos;
    totalDescuentos += ventas.descuentos;
    totalVentasSinDescuento += ventas.ventasSinDescuento;
    totalCargoServicio += ventas.cargoServicio;
    totalCheques += 1;
    totalClientes += cheque.numClientes || 1;
  });

  return {
    byGroup,
    totalVentas,
    totalSinImpuestos,
    totalImpuestos,
    totalDescuentos,
    totalVentasSinDescuento,
    totalCargoServicio,
    totalCheques,
    totalClientes
  };
}, [corteData.analisis]);

  const daybookData = useMemo(() => {
    if (!corteData.filteredCheques || corteData.filteredCheques.length === 0) {
      return {
        numeroCheques: 0,
        chequeInicial: 0,
        chequeFinal: 0,
        totalVentas: 0,
        totalVentasSinDescuento: 0,
        totalDescuentos: 0,
        totalImpuestos: 0,
        totalCargoServicio: 0,
        totalPropinas: 0,
        totalNeto: 0
      };
    }

    const chequesOrdenados = [...corteData.filteredCheques].sort((a, b) => {
      const idA = parseInt(a.id) || 0;
      const idB = parseInt(b.id) || 0;
      return idA - idB;
    });

    const chequeInicial = chequesOrdenados[0]?.id || 0;
    const chequeFinal = chequesOrdenados[chequesOrdenados.length - 1]?.id || 0;
    const numeroCheques = chequesOrdenados.length;
    
    const totalVentas = corteData.totals.totalVentas || 0;
    const totalVentasSinDescuento = corteData.totals.totalVentasSinDescuento || 0;
    const totalDescuentos = corteData.totals.totalDescuentos || 0;
    const totalImpuestos = corteData.totals.totalImpuestos || 0;
    const totalCargoServicio = corteData.totals.totalCargoServicio || 0;
    const totalPropinas = corteData.totals.totalPropinas || 0;
    const totalNeto = totalVentas + totalCargoServicio + totalPropinas;

    return {
      numeroCheques,
      chequeInicial,
      chequeFinal,
      totalVentas,
      totalVentasSinDescuento,
      totalDescuentos,
      totalImpuestos,
      totalCargoServicio,
      totalPropinas,
      totalNeto
    };
  }, [corteData.filteredCheques, corteData.totals]);

  const filteredUsers = useMemo(() => {
    const vendedoresActivos = new Set();
    const cajerosActivos = new Set();
    
    corteData.filteredCheques.forEach(cheque => {
      if (cheque.id_vendedor && 
          cheque.id_vendedor !== 'unknown' && 
          cheque.id_vendedor !== null &&
          parseFloat(cheque.total_cheque) > 0) {
        vendedoresActivos.add(cheque.id_vendedor);
      }
      
      if (cheque.nombre_cajero && 
          cheque.nombre_cajero !== 'Sin cajero' &&
          cheque.nombre_cajero !== null) {
        const pagos = Array.isArray(cheque.pagos) ? cheque.pagos : [];
        if (pagos.length > 0) {
          cajerosActivos.add(cheque.nombre_cajero);
        }
      }
    });

    console.log('Vendedores activos IDs:', Array.from(vendedoresActivos));
    console.log('Cajeros activos nombres:', Array.from(cajerosActivos));

    return corteData.users
      .filter(user => {
        const textMatch = user.nombre.toLowerCase().includes(filterText.toLowerCase()) ||
                         user.tipo.toLowerCase().includes(filterText.toLowerCase());
        
        if (!textMatch) return false;

        if (userTypeFilter !== 'all') {
          if (userTypeFilter === 'vendedor' && user.tipo !== 'Vendedor') return false;
          if (userTypeFilter === 'cajero' && user.tipo !== 'Cajero') return false;
        }

        if (activeTab === 'vendedores') {
          if (user.tipo === 'Vendedor') {
            return true;
          } else if (user.tipo === 'Cajero') {
            return cajerosActivos.has(user.nombre);
          }
        }
        
        return true;
      })
      .sort((a, b) => {
        if (a.tipo === 'Vendedor' && b.tipo === 'Cajero') return -1;
        if (a.tipo === 'Cajero' && b.tipo === 'Vendedor') return 1;
        return a.nombre.localeCompare(b.nombre);
      });
  }, [corteData.users, filterText, userTypeFilter, activeTab, corteData.filteredCheques]);

  const handlePrintCorte = async (usuario, userData) => {
    if (!deviceConfig) {
      const errorMsg = "Configuración de dispositivo no disponible";
      setError(errorMsg);
      setPrintStatus(prev => ({ ...prev, [usuario]: errorMsg }));
      console.error(errorMsg, { deviceConfig });
      return;
    }

    if (!devices || devices.length === 0) {
      const errorMsg = "No hay dispositivos configurados";
      setError(errorMsg);
      setPrintStatus(prev => ({ ...prev, [usuario]: errorMsg }));
      console.error(errorMsg, { devices });
      return;
    }

    if (!currentUser) {
      const errorMsg = "Usuario no identificado";
      setError(errorMsg);
      setPrintStatus(prev => ({ ...prev, [usuario]: errorMsg }));
      console.error(errorMsg, { currentUser });
      return;
    }

    const printer = devices.find(d => d.name === deviceConfig.PRNTT && d.Rol === "PRNT");
    if (!printer) {
      const errorMsg = `Impresora ${deviceConfig.PRNTT || "No definida"} no encontrada`;
      setError(errorMsg);
      setPrintStatus(prev => ({ ...prev, [usuario]: errorMsg }));
      console.error(errorMsg, { deviceConfigPRNTT: deviceConfig.PRNTT, devices });
      return;
    }

    if (!printer.Config?.[0]?.Tipo) {
      const errorMsg = `Impresora ${printer.name} no tiene tipo definido`;
      setError(errorMsg);
      setPrintStatus(prev => ({ ...prev, [usuario]: errorMsg }));
      console.error(errorMsg, { printer });
      return;
    }

    try {
      setPrintStatus(prev => ({ ...prev, [usuario]: 'Imprimiendo...' }));
      
      const nombreUsuario = usuario === 'all' ? 'TODOS' : userData.nombre;
      const tipoUsuario = usuario === 'all' ? 'Cajero' : userData.tipo;
      
      const printData = {
        property,
        usuario: nombreUsuario,
        nombre_usuario: nombreUsuario,
        tipo_usuario: tipoUsuario,
        fecha_control: selectedDate,
        printerName: printer.name,
        printerType: printer.Config?.[0]?.Tipo || "No definido",
        isCorteGeneral: usuario === 'all',
        currentUser: currentUser.name || 'Usuario desconocido',
        currentUserId: currentUser.id || 'unknown',
        deviceConfig,
        ventas: usuario === 'all' ? corteData.totals.totalVentas : userData.ventas,
        ventasSinImpuestos: usuario === 'all' ? corteData.totals.totalSinImpuestos : userData.ventasSinImpuestos,
        impuestos: usuario === 'all' ? corteData.totals.totalImpuestos : userData.impuestos,
        cobros: usuario === 'all' ? corteData.totals.totalCobros : userData.cobros,
        propinas: usuario === 'all' ? corteData.totals.totalPropinas : userData.propinas,
        cambio: usuario === 'all' ? corteData.totals.totalCambio : userData.cambio,
        enCaja: usuario === 'all' ? corteData.totals.totalEnCaja : userData.enCaja,
        descuentos: usuario === 'all' ? corteData.totals.totalDescuentos : userData.descuentos,
        fueraDeBalance: usuario === 'all' ? corteData.totals.totalFueraDeBalance : userData.fueraDeBalance,
        cargoServicio: usuario === 'all' ? corteData.totals.totalCargoServicio : userData.cargoServicio,
        paymentDetails: usuario === 'all' ? corteData.paymentMethods : userData.paymentDetails,
        chequesVentas: usuario === 'all' ? corteData.filteredCheques : userData.chequesVentas,
        chequesCobros: usuario === 'all' ? corteData.filteredCheques : userData.chequesCobros,
        generalTotals: usuario === 'all' ? corteData.totals : null,
        paymentMethods: usuario === 'all' ? corteData.paymentMethods : null,
        ingresos: usuario === 'all' ? corteData.ingresos : null,
        analisis: usuario === 'all' ? corteData.analisis : null,
        vendedores: usuario === 'all' ? corteData.users.filter(u => u.tipo === 'Vendedor') : null,
        cajeros: usuario === 'all' ? corteData.users.filter(u => u.tipo === 'Cajero') : null,
        cheques: usuario === 'all' ? corteData.filteredCheques : null,
        timezoneData: usuario === 'all' ? timezoneData : null,
        daybookData: usuario === 'all' ? daybookData : null
      };

      const printOptions = {
        isCorte: true,
        isCorteGeneral: usuario === 'all',
        fecha_control: selectedDate,
        property,
        usuario: nombreUsuario,
        nombre_usuario: nombreUsuario,
        tipo_usuario: tipoUsuario,
        printerName: printer.name,
        printerType: printer.Config?.[0]?.Tipo || "No definido",
        currentUser: currentUser.name || 'Usuario desconocido',
        currentUserId: currentUser.id || 'unknown',
        corteData: JSON.stringify(printData),
        USB: {
          DeviceID: printer.name
        }
      };

      console.log('Datos enviados a impresión:', printData);

      const printResult = await printTicket(
        null,
        null,
        null,
        property,
        deviceConfig,
        null,
        null,
        null,
        currentUser,
        devices,
        printOptions
      );

      if (printResult.success) {
        setPrintStatus(prev => ({ ...prev, [usuario]: 'Impresión exitosa' }));
        console.log('Print successful:', printResult);
      } else {
        throw new Error(printResult.message || 'Error al imprimir');
      }
    } catch (error) {
      console.error("Error al imprimir:", error);
      const errorMsg = `Error: ${error.message.substring(0, 50)}...`;
      setPrintStatus(prev => ({ ...prev, [usuario]: errorMsg }));
      setError(errorMsg);
    }
  };



  const handleExpandUser = (userId) => {
    setExpandedUser(expandedUser === userId ? null : userId);
  };

  const handleExpandPaymentMethod = (method) => {
    setExpandedPaymentMethod(expandedPaymentMethod === method ? null : method);
  };

  const handleExpandRevenue = (revenue) => {
    setExpandedRevenue(expandedRevenue === revenue ? null : revenue);
  };

  const handleExpandCheque = (chequeId) => {
    setExpandedCheque(expandedCheque === chequeId ? null : chequeId);
  };

  const handleOpenAdjustDialog = (numeroCheque) => {
    setSelectedCheque(numeroCheque);
    setAdjustDialogOpen(true);
  };

  const handleCloseAdjustDialog = () => {
    setAdjustDialogOpen(false);
    setSelectedCheque(null);
  };

  const handleAdjustSuccess = () => {
    setLoading(true);
    setError(null);
    Promise.all([
      fetch(`${BASE_API_URL}/lectura_cheques_corte.php?property=${encodeURIComponent(property)}`),
      fetch(`${BASE_API_URL}/lectura_cheques.php?property=${encodeURIComponent(property)}`)
    ])
      .then(([corteResponse, salesResponse]) => Promise.all([corteResponse.json(), salesResponse.json()]))
      .then(([corteData, salesData]) => {
        if (corteData.success && salesData.success) {
          setApiData(corteData);
          setSalesData(salesData);

          const corteDateCounts = corteData.data.cheques.reduce((acc, cheque) => {
            if (cheque.fecha_control && cheque.fecha_control !== '0000-00-00') {
              acc[cheque.fecha_control] = (acc[cheque.fecha_control] || 0) + 1;
            }
            return acc;
          }, {});

          const salesDateCounts = salesData.data.reduce((acc, cheque) => {
            if (cheque.fecha_control && cheque.fecha_control !== '0000-00-00') {
              acc[cheque.fecha_control] = (acc[cheque.fecha_control] || 0) + 1;
            }
            return acc;
          }, {});

          // Asegurar que salesData.data tenga fecha_hora_creacion
          salesData.data = salesData.data.map(cheque => {
            if (!cheque.fecha_hora_creacion) {
              if (cheque.fecha_creacion && cheque.hora_creacion) {
                cheque.fecha_hora_creacion = `${cheque.fecha_creacion} ${cheque.hora_creacion}`;
              } else if (cheque.fecha_control && cheque.hora_creacion) {
                cheque.fecha_hora_creacion = `${cheque.fecha_control} ${cheque.hora_creacion}`;
              } else if (cheque.fecha_control) {
                cheque.fecha_hora_creacion = `${cheque.fecha_control} 00:00:00`;
              }
            }
            
            if (!cheque.fecha_control) {
              cheque.fecha_control = '0000-00-00';
            } else {
              try {
                const parsedDate = parse(cheque.fecha_control, 'yyyy-MM-dd', new Date());
                cheque.fecha_control = format(parsedDate, 'yyyy-MM-dd');
              } catch (error) {
                cheque.fecha_control = '0000-00-00';
              }
            }
            
            return cheque;
          });

          const uniqueDates = [...new Set([
            ...Object.keys(corteDateCounts),
            ...Object.keys(salesDateCounts)
          ])].sort((a, b) => new Date(b) - new Date(a));

          setAvailableDates(uniqueDates);
          if (uniqueDates.length > 0 && !selectedDate) {
            setSelectedDate(uniqueDates[0]);
          }
        } else {
          throw new Error(corteData.message || salesData.message || 'Error in received data');
        }
      })
      .catch(error => {
        console.error('Error reloading data:', error);
        setError(error.message);
      })
      .finally(() => {
        setLoading(false);
        setAdjustDialogOpen(false);
        setSelectedCheque(null);
      });
  };

  const formatCurrency = (value) => {
  const numericValue = parseFloat(value) || 0;
  return new Intl.NumberFormat(numberFormat, {
    style: 'currency',
    currency: currency
  }).format(numericValue);
};

// ==============================================
// FUNCIONES DE EXPORTACIÓN A CSV Y PDF (MEJORADO - USA DATOS EXISTENTES)
// ==============================================

const handleExport = async () => {
  if (exportFormat === 'csv') {
    exportAllToCSV();
  } else if (exportFormat === 'pdf') {
    exportAllToPDF();
  }
  setExportDialogOpen(false);
};

// ==============================================
// EXPORTAR TODO A CSV (usa datos ya calculados)
// ==============================================
const exportAllToCSV = () => {
  let fullCSV = `"CORTE DE CAJA COMPLETO - ${property} - ${selectedDate}"\n`;
  fullCSV += `"Generado: ${new Date().toLocaleString()}"\n`;
  fullCSV += `"Usuario: ${currentUser?.name || 'Desconocido'}"\n\n`;
  
  // ========== 1. RESUMEN GENERAL (de corteData.totals) ==========
  fullCSV += `"=== 1. RESUMEN GENERAL ===\n`;
  fullCSV += `"Concepto","Total"\n`;
  fullCSV += `"Ventas Totales (antes de descuentos)","${formatCurrency(corteData.totals.totalVentas + corteData.totals.totalDescuentos)}"\n`;
  fullCSV += `"Descuentos Totales","${formatCurrency(corteData.totals.totalDescuentos)}"\n`;
  fullCSV += `"Ventas después de descuentos","${formatCurrency(corteData.totals.totalVentas)}"\n`;
  fullCSV += `"Ventas Netas (sin impuestos)","${formatCurrency(corteData.totals.totalSinImpuestos)}"\n`;
  fullCSV += `"Impuestos Totales","${formatCurrency(corteData.totals.totalImpuestos)}"\n`;
  fullCSV += `"Cargo por Servicio (CS)","${formatCurrency(corteData.totals.totalCargoServicio)}"\n`;
  fullCSV += `"Propinas Totales","${formatCurrency(corteData.totals.totalPropinas)}"\n`;
  fullCSV += `"Cobros Totales","${formatCurrency(corteData.totals.totalCobros)}"\n`;
  fullCSV += `"Cambio Total","${formatCurrency(corteData.totals.totalCambio)}"\n`;
  fullCSV += `"Total en Caja","${formatCurrency(corteData.totals.totalCobros - corteData.totals.totalPropinas - corteData.totals.totalCambio)}"\n`;
  fullCSV += `"Cheques Totales","${corteData.totals.totalCheques}"\n`;
  fullCSV += `"Fuera de Balance Total","${formatCurrency(corteData.totals.totalFueraDeBalance)}"\n\n`;
  
  // ========== 2. VENDEDORES Y CAJEROS (de corteData.users) ==========
  fullCSV += `"=== 2. VENDEDORES Y CAJEROS ===\n`;
  fullCSV += `"Nombre","Tipo","Ventas","Ventas sin Impuestos","Impuestos","Cargo Servicio","Propinas","Cobros","Cambio","En Caja","Fuera de Balance"\n`;
  corteData.users.forEach(user => {
    fullCSV += `"${user.nombre}","${user.tipo}","${formatCurrency(user.ventas)}","${formatCurrency(user.ventasSinImpuestos)}","${formatCurrency(user.impuestos)}","${formatCurrency(user.cargoServicio)}","${formatCurrency(user.propinas)}","${formatCurrency(user.cobros)}","${formatCurrency(user.cambio)}","${formatCurrency(user.enCaja)}","${formatCurrency(user.fueraDeBalance)}"\n`;
  });
  fullCSV += `\n`;
  
  // ========== 3. MÉTODOS DE PAGO (de corteData.paymentMethods) ==========
  fullCSV += `"=== 3. MÉTODOS DE PAGO ===\n`;
  fullCSV += `"Método de Pago","Transacciones","Total Cobrado","Total Propinas","% del Total"\n`;
  Object.entries(corteData.paymentMethods).forEach(([method, details]) => {
    const porcentaje = corteData.totals.totalCobros > 0 ? ((details.cobros / corteData.totals.totalCobros) * 100).toFixed(2) + '%' : '0%';
    fullCSV += `"${method}","${details.count}","${formatCurrency(details.cobros)}","${formatCurrency(details.propinas)}","${porcentaje}"\n`;
  });
  fullCSV += `\n`;
  
  // ========== 4. INGRESOS POR REVENUE (de corteData.ingresos.byRevenue) ==========
  fullCSV += `"=== 4. INGRESOS POR REVENUE ===\n`;
  fullCSV += `"Centro de Ingresos","Ventas Totales","Impuestos","Descuentos","Cargo Servicio","Cheques"\n`;
  Object.entries(corteData.ingresos.byRevenue).forEach(([revenue, details]) => {
    fullCSV += `"${revenue}","${formatCurrency(details.total)}","${formatCurrency(details.impuestos)}","${formatCurrency(details.descuentos)}","${formatCurrency(details.cargoServicio)}","${details.cheques.length}"\n`;
  });
  fullCSV += `\n`;
  
  // ========== 5. TOP PRODUCTOS (de corteData.ingresos.byProduct) ==========
  fullCSV += `"=== 5. TOP 100 PRODUCTOS ===\n`;
  fullCSV += `"Producto","Revenue","Cantidad","Ventas Totales","Impuestos","Descuentos","Cargo Servicio"\n`;
  Object.entries(corteData.ingresos.byProduct)
    .sort((a, b) => b[1].total - a[1].total)
    .slice(0, 100)
    .forEach(([nombre, data]) => {
      fullCSV += `"${nombre.replace(/"/g, '""')}","${data.revenue}","${data.cantidad}","${formatCurrency(data.total)}","${formatCurrency(data.impuestos)}","${formatCurrency(data.descuentos)}","${formatCurrency(data.cargoServicio)}"\n`;
    });
  fullCSV += `\n`;
  
  // ========== 6. VENTAS POR TURNO (de timezoneData) ==========
  fullCSV += `"=== 6. VENTAS POR TURNO ===\n`;
  fullCSV += `"Turno","Ventas Netas","Ventas con Descuento","Descuentos","Impuestos","Cargo Servicio","Propinas","Ventas Totales","Clientes","Cheques","Cheque Promedio"\n`;
  timezoneData.forEach(tz => {
    fullCSV += `"${tz.name}","${formatCurrency(tz.subtotal)}","${formatCurrency(tz.ventasSinDescuento)}","${formatCurrency(tz.descuentos)}","${formatCurrency(tz.impuestos)}","${formatCurrency(tz.cargoServicio)}","${formatCurrency(tz.propinas)}","${formatCurrency(tz.total)}","${tz.clientes}","${tz.cheques.length}","${formatCurrency(tz.chequePromedio)}"\n`;
  });
  fullCSV += `\n`;
  
  // ========== 7. CHEQUES FUERA DE BALANCE (de corteData.totals.unbalancedCheques) ==========
  if (corteData.totals.unbalancedCheques && corteData.totals.unbalancedCheques.length > 0) {
    fullCSV += `"=== 7. CHEQUES FUERA DE BALANCE ===\n`;
    fullCSV += `"ID Cheque","Fuera de Balance","Vendedor","Cajero"\n`;
    corteData.totals.unbalancedCheques.forEach(cheque => {
      fullCSV += `"${cheque.id}","${formatCurrency(cheque.fueraDeBalance)}","${cheque.nombre_vendedor || 'N/A'}","${cheque.nombre_cajero || 'N/A'}"\n`;
    });
    fullCSV += `\n`;
  }
  
  // ========== 8. DATOS CONSOLIDADOS - TODOS LOS REVENUES (de revenueConsolidatedData) ==========
  fullCSV += `"=== 8. DATOS CONSOLIDADOS POR REVENUE ===\n`;
  
  // Mostrar resumen de cada Revenue
  fullCSV += `\n"--- RESUMEN POR REVENUE ---\n`;
  fullCSV += `"Revenue","Ventas Totales","Cantidad Items","Cheques","Clientes","Ticket Promedio"\n`;
  Object.entries(revenueConsolidatedData).forEach(([revenue, data]) => {
    const ticketPromedio = data.totalClientes > 0 ? data.totalVentas / data.totalClientes : 0;
    fullCSV += `"${revenue}","${formatCurrency(data.totalVentas)}","${data.totalCantidad || 0}","${data.totalCheques || 0}","${data.totalClientes || 0}","${formatCurrency(ticketPromedio)}"\n`;
  });
  
  // Detalle de productos por Revenue (top 50 por cada uno)
  fullCSV += `\n"--- DETALLE DE PRODUCTOS POR REVENUE (Top 50 por Revenue) ---\n`;
  fullCSV += `"Revenue","Producto","Categoría","Grupo","Cantidad","Ventas Brutas","Descuentos","Impuestos","Cargo Servicio","Ventas Totales","% del Revenue"\n`;
  
  Object.entries(revenueConsolidatedData).forEach(([revenue, data]) => {
    const productosOrdenados = Object.entries(data.byProduct)
      .sort((a, b) => b[1].total - a[1].total)
      .slice(0, 50);
    
    productosOrdenados.forEach(([nombre, prodData]) => {
      const porcentaje = data.totalVentas > 0 ? (prodData.total / data.totalVentas) * 100 : 0;
      fullCSV += `"${revenue}","${nombre.replace(/"/g, '""')}","${prodData.categoria || 'General'}","${prodData.grupo || revenue}","${prodData.cantidad || 0}","${formatCurrency(prodData.ventasSinDescuento || 0)}","${formatCurrency(prodData.descuentos || 0)}","${formatCurrency(prodData.impuestos || 0)}","${formatCurrency(prodData.cargoServicio || 0)}","${formatCurrency(prodData.total || 0)}","${porcentaje.toFixed(2)}%"\n`;
    });
  });
  fullCSV += `\n`;
  
  // ========== 9. DAYBOOK MATRIX (de daybookMatrixData) ==========
  if (daybookMatrixData.matrix && Object.keys(daybookMatrixData.matrix).length > 0) {
    fullCSV += `"=== 9. DAYBOOK MATRIX (Revenue × Categoría × Turno) ===\n`;
    fullCSV += `"Revenue","Categoría","Turno","Ventas Brutas","Descuentos","Ventas Netas","Impuestos","Cargo Servicio","Total","Items Vendidos","Clientes (Ref)"\n`;
    
    Object.entries(daybookMatrixData.matrix).forEach(([revenue, categories]) => {
      Object.entries(categories).forEach(([categoria, modosData]) => {
        daybookMatrixData.sortedModos.forEach(modo => {
          const data = modosData[modo];
          if (data && (data.ganancia > 0 || data.total > 0)) {
            fullCSV += `"${revenue}","${categoria}","${modo}","${formatCurrency(data.gross || 0)}","${formatCurrency(data.discount || 0)}","${formatCurrency(data.ganancia || 0)}","${formatCurrency(data.tax || 0)}","${formatCurrency(data.cs || 0)}","${formatCurrency(data.total || 0)}","${data.count || 0}","${data.clientes || 0}"\n`;
          }
        });
      });
    });
    fullCSV += `\n`;
    
    // Totales por Turno
    fullCSV += `"--- TOTALES POR TURNO ---\n`;
    fullCSV += `"Turno","Ventas Netas","Clientes Únicos","Cheque Promedio","Items Vendidos"\n`;
    daybookMatrixData.sortedModos.forEach(modo => {
      let totalGanancia = 0;
      let totalItems = 0;
      Object.values(daybookMatrixData.matrix).forEach(categories => {
        Object.values(categories).forEach(modosData => {
          const data = modosData[modo];
          if (data) {
            totalGanancia += data.ganancia || 0;
            totalItems += data.count || 0;
          }
        });
      });
      const clientesTurno = daybookMatrixData.clientesPorTurnoGlobal?.get(modo) || 0;
      const chequePromedio = clientesTurno > 0 ? totalGanancia / clientesTurno : 0;
      fullCSV += `"${modo}","${formatCurrency(totalGanancia)}","${clientesTurno}","${formatCurrency(chequePromedio)}","${totalItems}"\n`;
    });
    fullCSV += `\n`;
  }
  
  // ========== 10. RESUMEN GENERAL DEL DAYBOOK ==========
  if (daybookMatrixData.resumenGeneral) {
    fullCSV += `"=== 10. RESUMEN DAYBOOK ===\n`;
    fullCSV += `"Concepto","Valor"\n`;
    fullCSV += `"Total Clientes Únicos","${daybookMatrixData.resumenGeneral.totalClientes || 0}"\n`;
    fullCSV += `"Total Cheques","${daybookMatrixData.resumenGeneral.totalCheques || 0}"\n`;
    fullCSV += `"Ventas Brutas","${formatCurrency(daybookMatrixData.resumenGeneral.totalVentasBrutas || 0)}"\n`;
    fullCSV += `"Descuentos","${formatCurrency(daybookMatrixData.resumenGeneral.totalDescuentos || 0)}"\n`;
    fullCSV += `"Ventas Netas (sin impuestos)","${formatCurrency(daybookMatrixData.resumenGeneral.totalGanancia || 0)}"\n`;
    fullCSV += `"Impuestos","${formatCurrency(daybookMatrixData.resumenGeneral.totalImpuestos || 0)}"\n`;
    fullCSV += `"Cargo Servicio","${formatCurrency(daybookMatrixData.resumenGeneral.totalCargoServicio || 0)}"\n`;
    fullCSV += `"Total Final","${formatCurrency(daybookMatrixData.resumenGeneral.totalFinal || 0)}"\n`;
    fullCSV += `"Cheque Promedio (por cliente)","${formatCurrency(daybookMatrixData.resumenGeneral.chequePromedio || 0)}"\n`;
    fullCSV += `"Alimentos","${formatCurrency(daybookMatrixData.resumenGeneral.totalAlimentos || 0)}"\n`;
    fullCSV += `"Bebidas","${formatCurrency(daybookMatrixData.resumenGeneral.totalBebidas || 0)}"\n`;
  }
  
  // ========== 11. ANÁLISIS DE CHEQUES (de corteData.analisis) ==========
  if (corteData.analisis && corteData.analisis.length > 0) {
    fullCSV += `\n"=== 11. ANÁLISIS DE CHEQUES ===\n`;
    fullCSV += `"ID","Revenue","Timezone","Clientes","Ventas Totales","Sin Impuestos","Impuestos","Descuentos","Cargo Servicio","Fuera de Balance","Balanceado"\n`;
    corteData.analisis.forEach(cheque => {
      fullCSV += `"${cheque.id}","${cheque.revenue}","${cheque.timezone}","${cheque.numClientes || 1}","${formatCurrency(cheque.ventas?.total || 0)}","${formatCurrency(cheque.ventas?.sinImpuestos || 0)}","${formatCurrency(cheque.ventas?.impuestos || 0)}","${formatCurrency(cheque.ventas?.descuentos || 0)}","${formatCurrency(cheque.ventas?.cargoServicio || 0)}","${formatCurrency(cheque.fueraDeBalance || 0)}","${cheque.isBalanced ? 'Si' : 'No'}"\n`;
    });
  }
  
  // Descargar archivo
  const blob = new Blob(["\uFEFF" + fullCSV], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `corte_caja_completo_${property}_${selectedDate}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
};

// ==============================================
// EXPORTAR TODO A PDF (usa datos ya calculados)
// ==============================================
const exportAllToPDF = () => {
  try {
    if (typeof jsPDF === 'undefined') {
      alert('Error: jsPDF no está cargado. Intenta recargar la página.');
      return;
    }
    
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });
    
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPos = 20;
    
    // ========== TÍTULO PRINCIPAL ==========
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(`CORTE DE CAJA COMPLETO - ${property}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Fecha: ${selectedDate} | Generado: ${new Date().toLocaleString()}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;
    
    // ========== 1. RESUMEN GENERAL ==========
    yPos = addPDFSection(doc, yPos, '1. RESUMEN GENERAL', [
      { Concepto: 'Ventas Totales (antes de descuentos)', Total: formatCurrency(corteData.totals.totalVentas + corteData.totals.totalDescuentos) },
      { Concepto: 'Descuentos Totales', Total: formatCurrency(corteData.totals.totalDescuentos) },
      { Concepto: 'Ventas después de descuentos', Total: formatCurrency(corteData.totals.totalVentas) },
      { Concepto: 'Ventas Netas (sin impuestos)', Total: formatCurrency(corteData.totals.totalSinImpuestos) },
      { Concepto: 'Impuestos Totales', Total: formatCurrency(corteData.totals.totalImpuestos) },
      { Concepto: 'Cargo por Servicio (CS)', Total: formatCurrency(corteData.totals.totalCargoServicio) },
      { Concepto: 'Propinas Totales', Total: formatCurrency(corteData.totals.totalPropinas) },
      { Concepto: 'Cobros Totales', Total: formatCurrency(corteData.totals.totalCobros) },
      { Concepto: 'Cambio Total', Total: formatCurrency(corteData.totals.totalCambio) },
      { Concepto: 'Total en Caja', Total: formatCurrency(corteData.totals.totalCobros - corteData.totals.totalPropinas - corteData.totals.totalCambio), isBold: true },
      { Concepto: 'Cheques Totales', Total: corteData.totals.totalCheques },
      { Concepto: 'Fuera de Balance Total', Total: formatCurrency(corteData.totals.totalFueraDeBalance), isWarning: Math.abs(corteData.totals.totalFueraDeBalance) >= 0.01 }
    ]);
    
    // ========== 2. VENDEDORES Y CAJEROS ==========
    yPos = addPDFTable(doc, yPos, '2. VENDEDORES Y CAJEROS', 
      ['Nombre', 'Tipo', 'Ventas', 'Ventas sin Imp', 'Impuestos', 'CS', 'Propinas', 'Cobros', 'En Caja'],
      corteData.users.slice(0, 30).map(user => [
        user.nombre.length > 20 ? user.nombre.substring(0, 17) + '...' : user.nombre,
        user.tipo,
        formatCurrency(user.ventas),
        formatCurrency(user.ventasSinImpuestos),
        formatCurrency(user.impuestos),
        formatCurrency(user.cargoServicio),
        formatCurrency(user.propinas),
        formatCurrency(user.cobros),
        formatCurrency(user.enCaja)
      ])
    );
    
    // ========== 3. MÉTODOS DE PAGO ==========
    yPos = addPDFTable(doc, yPos, '3. MÉTODOS DE PAGO',
      ['Método', 'Transacciones', 'Total Cobrado', 'Propinas', '%'],
      Object.entries(corteData.paymentMethods).map(([method, details]) => [
        method,
        details.count,
        formatCurrency(details.cobros),
        formatCurrency(details.propinas),
        corteData.totals.totalCobros > 0 ? ((details.cobros / corteData.totals.totalCobros) * 100).toFixed(1) + '%' : '0%'
      ])
    );
    
    // ========== 4. INGRESOS POR REVENUE ==========
    yPos = addPDFTable(doc, yPos, '4. INGRESOS POR REVENUE',
      ['Centro de Ingresos', 'Ventas', 'Impuestos', 'Descuentos', 'CS', 'Cheques'],
      Object.entries(corteData.ingresos.byRevenue).map(([revenue, details]) => [
        revenue.length > 25 ? revenue.substring(0, 22) + '...' : revenue,
        formatCurrency(details.total),
        formatCurrency(details.impuestos),
        formatCurrency(details.descuentos),
        formatCurrency(details.cargoServicio),
        details.cheques.length
      ])
    );
    
    // ========== 5. TOP PRODUCTOS ==========
    const topProductos = Object.entries(corteData.ingresos.byProduct)
      .sort((a, b) => b[1].total - a[1].total)
      .slice(0, 30)
      .map(([nombre, data]) => [
        nombre.length > 25 ? nombre.substring(0, 22) + '...' : nombre,
        data.cantidad,
        formatCurrency(data.total),
        formatCurrency(data.impuestos)
      ]);
    
    yPos = addPDFTable(doc, yPos, '5. TOP 30 PRODUCTOS',
      ['Producto', 'Cantidad', 'Ventas', 'Impuestos'],
      topProductos
    );
    
    // ========== 6. VENTAS POR TURNO ==========
    yPos = addPDFTable(doc, yPos, '6. VENTAS POR TURNO',
      ['Turno', 'Ventas Netas', 'Impuestos', 'CS', 'Propinas', 'Total', 'Clientes', 'Cheque Promedio'],
      timezoneData.map(tz => [
        tz.name,
        formatCurrency(tz.subtotal),
        formatCurrency(tz.impuestos),
        formatCurrency(tz.cargoServicio),
        formatCurrency(tz.propinas),
        formatCurrency(tz.total),
        tz.clientes,
        formatCurrency(tz.chequePromedio)
      ])
    );
    
    // ========== 7. CHEQUES FUERA DE BALANCE ==========
    if (corteData.totals.unbalancedCheques && corteData.totals.unbalancedCheques.length > 0) {
      yPos = addPDFTable(doc, yPos, '7. CHEQUES FUERA DE BALANCE',
        ['ID Cheque', 'Fuera de Balance', 'Vendedor', 'Cajero'],
        corteData.totals.unbalancedCheques.slice(0, 20).map(cheque => [
          cheque.id,
          formatCurrency(cheque.fueraDeBalance),
          cheque.nombre_vendedor || 'N/A',
          cheque.nombre_cajero || 'N/A'
        ])
      );
    }
    
    // ========== 8. CONSOLIDADOS - TODOS LOS REVENUES ==========
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('8. CONSOLIDADOS POR REVENUE', 14, yPos);
    yPos += 6;
    
    // Tabla resumen de todos los revenues
    const revenueResumen = Object.entries(revenueConsolidatedData).map(([revenue, data]) => [
      revenue.length > 20 ? revenue.substring(0, 17) + '...' : revenue,
      formatCurrency(data.totalVentas),
      data.totalCantidad || 0,
      data.totalCheques || 0,
      data.totalClientes || 0,
      formatCurrency(data.totalClientes > 0 ? data.totalVentas / data.totalClientes : 0)
    ]);
    
    yPos = addPDFTable(doc, yPos, 'RESUMEN POR REVENUE',
      ['Revenue', 'Ventas', 'Items', 'Cheques', 'Clientes', 'Ticket Promedio'],
      revenueResumen
    );
    
    // Top productos del revenue con mayores ventas
    const topRevenuePorVentas = Object.entries(revenueConsolidatedData)
      .sort((a, b) => b[1].totalVentas - a[1].totalVentas)
      .slice(0, 3);
    
    for (const [revenue, data] of topRevenuePorVentas) {
      if (yPos > 220) {
        doc.addPage();
        yPos = 20;
      }
      
      const topProductosRevenue = Object.entries(data.byProduct)
        .sort((a, b) => b[1].total - a[1].total)
        .slice(0, 15)
        .map(([nombre, prodData]) => [
          nombre.length > 22 ? nombre.substring(0, 19) + '...' : nombre,
          prodData.categoria || 'General',
          prodData.cantidad,
          formatCurrency(prodData.total),
          data.totalVentas > 0 ? ((prodData.total / data.totalVentas) * 100).toFixed(1) + '%' : '0%'
        ]);
      
      yPos = addPDFTable(doc, yPos, `TOP 15 PRODUCTOS - ${revenue}`,
        ['Producto', 'Categoría', 'Cantidad', 'Ventas', '% del Revenue'],
        topProductosRevenue
      );
    }
    
    // ========== 9. DAYBOOK MATRIX (resumen) ==========
    if (daybookMatrixData.resumenGeneral) {
      if (yPos > 220) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('9. RESUMEN DAYBOOK', 14, yPos);
      yPos += 6;
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      const daybookItems = [
        { Concepto: 'Total Clientes Únicos', Total: daybookMatrixData.resumenGeneral.totalClientes || 0 },
        { Concepto: 'Total Cheques', Total: daybookMatrixData.resumenGeneral.totalCheques || 0 },
        { Concepto: 'Ventas Netas (sin impuestos)', Total: formatCurrency(daybookMatrixData.resumenGeneral.totalGanancia || 0) },
        { Concepto: 'Total Final (con impuestos y CS)', Total: formatCurrency(daybookMatrixData.resumenGeneral.totalFinal || 0) },
        { Concepto: 'Cheque Promedio (por cliente)', Total: formatCurrency(daybookMatrixData.resumenGeneral.chequePromedio || 0) },
        { Concepto: 'Alimentos', Total: formatCurrency(daybookMatrixData.resumenGeneral.totalAlimentos || 0) },
        { Concepto: 'Bebidas', Total: formatCurrency(daybookMatrixData.resumenGeneral.totalBebidas || 0) }
      ];
      
      daybookItems.forEach(item => {
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(item.Concepto, 14, yPos);
        doc.text(item.Total.toString(), pageWidth - 20, yPos, { align: 'right' });
        yPos += 6;
      });
      yPos += 4;
    }
    
    // ========== 10. TOTALES POR TURNO ==========
    if (daybookMatrixData.sortedModos && daybookMatrixData.sortedModos.length > 0) {
      if (yPos > 220) {
        doc.addPage();
        yPos = 20;
      }
      
      const turnosData = daybookMatrixData.sortedModos.map(modo => {
        let totalGanancia = 0;
        let totalItems = 0;
        Object.values(daybookMatrixData.matrix || {}).forEach(categories => {
          Object.values(categories || {}).forEach(modosData => {
            const data = modosData[modo];
            if (data) {
              totalGanancia += data.ganancia || 0;
              totalItems += data.count || 0;
            }
          });
        });
        const clientesTurno = daybookMatrixData.clientesPorTurnoGlobal?.get(modo) || 0;
        return [
          modo,
          formatCurrency(totalGanancia),
          clientesTurno,
          formatCurrency(clientesTurno > 0 ? totalGanancia / clientesTurno : 0),
          totalItems
        ];
      });
      
      yPos = addPDFTable(doc, yPos, '10. TOTALES POR TURNO',
        ['Turno', 'Ventas Netas', 'Clientes', 'Cheque Promedio', 'Items Vendidos'],
        turnosData
      );
    }
    
    // Pie de página
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.text(
        `Corte de Caja - ${property} - ${selectedDate} | Página ${i} de ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }
    
    doc.save(`corte_caja_completo_${property}_${selectedDate}.pdf`);
    console.log('PDF generado correctamente');
    
  } catch (error) {
    console.error('Error al generar PDF:', error);
    alert('Error al generar el PDF: ' + error.message);
  }
};

// ==============================================
// FUNCIONES AUXILIARES PARA PDF
// ==============================================
const addPDFSection = (doc, yPos, title, items) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  
  if (yPos > 250) {
    doc.addPage();
    yPos = 20;
  }
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 14, yPos);
  yPos += 6;
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  
  items.forEach(item => {
    if (yPos > 270) {
      doc.addPage();
      yPos = 20;
      doc.setFontSize(9);
    }
    
    if (item.isBold) doc.setFont('helvetica', 'bold');
    if (item.isWarning) doc.setTextColor(255, 0, 0);
    
    doc.text(item.Concepto, 14, yPos);
    doc.text(item.Total.toString(), pageWidth - 20, yPos, { align: 'right' });
    
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    yPos += 6;
  });
  
  yPos += 4;
  return yPos;
};

const addPDFTable = (doc, yPos, title, headers, data) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  
  if (yPos > 250) {
    doc.addPage();
    yPos = 20;
  }
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 14, yPos);
  yPos += 6;
  
  const colWidth = (pageWidth - 28) / headers.length;
  let x = 14;
  
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  headers.forEach(header => {
    doc.text(header, x, yPos);
    x += colWidth;
  });
  yPos += 4;
  
  doc.line(14, yPos, pageWidth - 14, yPos);
  yPos += 3;
  
  doc.setFont('helvetica', 'normal');
  
  data.forEach(row => {
    if (yPos > 270) {
      doc.addPage();
      yPos = 20;
    }
    
    x = 14;
    row.forEach((cell, idx) => {
      const cellStr = cell?.toString() || '';
      if (idx === 0 && cellStr.length > 22) {
        doc.text(cellStr.substring(0, 19) + '...', x, yPos);
      } else {
        doc.text(cellStr, x, yPos);
      }
      x += colWidth;
    });
    yPos += 5;
  });
  
  yPos += 4;
  return yPos;
};
/*fin de exportacion*/

  const analisisByRevenue = useMemo(() => {
    const byRevenue = {};
    corteData.analisis.forEach(cheque => {
      if (!byRevenue[cheque.revenue]) {
        byRevenue[cheque.revenue] = [];
      }
      byRevenue[cheque.revenue].push(cheque);
    });
    return byRevenue;
  }, [corteData.analisis]);

// Cheque promedio por revenue
const promedioPorRevenue = useMemo(() => {
  const promedios = {};
  Object.entries(analisisByRevenue).forEach(([revenue, cheques]) => {
    const totalSinImpuestos = cheques.reduce((sum, ch) => sum + ch.ventas.sinImpuestos, 0);
    const totalClientes = cheques.reduce((sum, ch) => sum + ch.numClientes, 0);
    promedios[revenue] = totalClientes > 0 ? totalSinImpuestos / totalClientes : 0;
  });
  return promedios;
}, [analisisByRevenue]);

// Cheque promedio por turno
// Cheque promedio por turno - USANDO corteData.analisis
const promedioPorTurno = useMemo(() => {
  const porTurno = {};
  
  // ✅ Usar corteData.analisis en lugar de analisis
  (corteData.analisis || []).forEach(cheque => {
    const turno = cheque.timezone || cheque.turno || 'No especificado';
    if (!porTurno[turno]) {
      porTurno[turno] = { totalSinImpuestos: 0, totalClientes: 0 };
    }
    porTurno[turno].totalSinImpuestos += cheque.ventas?.sinImpuestos || 0;
    porTurno[turno].totalClientes += cheque.numClientes || 1;
  });

  const promedios = {};
  Object.entries(porTurno).forEach(([turno, data]) => {
    promedios[turno] = data.totalClientes > 0 ? data.totalSinImpuestos / data.totalClientes : 0;
  });
  return promedios;
}, [corteData.analisis]);

  return (
    <Dialog 
      open={show} 
      onClose={onClose} 
      fullWidth 
      maxWidth="xl"
      PaperProps={{ sx: { height: '90vh' } }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">Corte de Caja - {property}</Typography>
          <IconButton onClick={onClose}>
            <ArrowBack />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {loading || isLoadingDevices ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>
              {isLoadingDevices ? "Cargando configuración de dispositivos..." : "Cargando datos del corte..."}
            </Typography>
          </Box>
        ) : error || dateError ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <Typography color="error">{error || dateError}</Typography>
          </Box>
        ) : (
          <>
            <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center">
                <DatePicker
                  selected={selectedDate ? parse(selectedDate, 'yyyy-MM-dd', new Date()) : null}
                  onChange={(date) => setSelectedDate(format(date, 'yyyy-MM-dd'))}
                  dateFormat="yyyy-MM-dd"
                  customInput={
                    <TextField
                      label="Fecha"
                      variant="outlined"
                      size="small"
                      error={!!dateError}
                      helperText={dateError}
                      InputProps={{
                        startAdornment: <DateRange color="action" />,
                      }}
                    />
                  }
                  includeDates={availableDates.map(date => parse(date, 'yyyy-MM-dd', new Date()))}
                />
                <Box ml={2}>
                  <Chip 
                    label={`Total: ${formatCurrency(corteData.totals.totalSinImpuestos)} (${corteData.totals.totalCheques} cheques)`} 
                    color="primary" 
                    icon={<AttachMoney />}
                  />
                </Box>
              </Box>
              
              <Box>
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<Print />}
                  onClick={() => handlePrintCorte('all', { 
                    id: 'all', 
                    nombre: 'TODOS',
                    tipo: 'Cajero',
                    ventas: corteData.totals.totalVentas,
                    ventasSinImpuestos: corteData.totals.totalSinImpuestos,
                    impuestos: corteData.totals.totalImpuestos,
                    cobros: corteData.totals.totalCobros,
                    propinas: corteData.totals.totalPropinas,
                    cambio: corteData.totals.totalCambio,
                    enCaja: corteData.totals.totalEnCaja,
                    descuentos: corteData.totals.totalDescuentos,
                    fueraDeBalance: corteData.totals.totalFueraDeBalance,
                    cargoServicio: corteData.totals.totalCargoServicio,
                    paymentDetails: corteData.paymentMethods,
                    chequesVentas: corteData.filteredCheques,
                    chequesCobros: corteData.filteredCheques,
                    property
                  })}
                  disabled={printStatus['all']?.includes('Imprimiendo')}
                >
                  {printStatus['all']?.includes('Imprimiendo') ? 'Imprimiendo...' : 'Imprimir Todo'}
                </Button>
                <Button 
                  variant="outlined" 
                  color="primary" 
                  startIcon={<Summarize />}
                  onClick={() => setExportDialogOpen(true)}
                  sx={{ ml: 2 }}
                >
                  Exportar
                </Button>
              </Box>
            </Box>

            <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{ mb: 3 }}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Resumen" value="corte" icon={<Summarize />} />
              <Tab label="Vendedores" value="vendedores" icon={<People />} />
              <Tab label="Métodos de Pago" value="pagos" icon={<Receipt />} />
              <Tab label="Ingresos" value="ingresos" icon={<MonetizationOn />} />
              <Tab label="Análisis" value="analisis" icon={<Analytics />} />
              <Tab label="Consolidadas" value="consolidadas" icon={<AccessTime />} />
              <Tab label="Daybook" value="daybook" icon={<Assessment />} />
            </Tabs>
            
            {activeTab === 'corte' && (
              <Box>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Concepto</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>Ventas Totales (antes de descuentos)</strong></TableCell>
                        <TableCell align="right"><strong>{formatCurrency(corteData.totals.totalVentas + corteData.totals.totalDescuentos)}</strong></TableCell>
                      </TableRow>
                      
                      <TableRow>
                        <TableCell>Descuentos Totales</TableCell>
                        <TableCell align="right">{formatCurrency(corteData.totals.totalDescuentos)}</TableCell>
                      </TableRow>
                      
                      <TableRow>
                        <TableCell>Ventas después de descuentos</TableCell>
                        <TableCell align="right">{formatCurrency(corteData.totals.totalVentas)}</TableCell>
                      </TableRow>
                      
                      <TableRow>
                        <TableCell>Ventas Netas (sin impuestos)</TableCell>
                        <TableCell align="right">{formatCurrency(corteData.totals.totalSinImpuestos)}</TableCell>
                      </TableRow>
                      
                      <TableRow>
                        <TableCell>Impuestos Totales</TableCell>
                        <TableCell align="right">{formatCurrency(corteData.totals.totalImpuestos)}</TableCell>
                      </TableRow>
                      
                      <TableRow>
                        <TableCell><strong>Cargo por Servicio (CS)</strong></TableCell>
                        <TableCell align="right"><strong>{formatCurrency(corteData.totals.totalCargoServicio)}</strong></TableCell>
                      </TableRow>
                      
                      <TableRow>
                        <TableCell><strong>Propinas Totales</strong></TableCell>
                        <TableCell align="right"><strong>{formatCurrency(corteData.totals.totalPropinas)}</strong></TableCell>
                      </TableRow>
                      
                      <TableRow>
                        <TableCell>Cobros Totales</TableCell>
                        <TableCell align="right">{formatCurrency(corteData.totals.totalCobros)}</TableCell>
                      </TableRow>
                      
                      <TableRow>
                        <TableCell>Cambio Total</TableCell>
                        <TableCell align="right">{formatCurrency(corteData.totals.totalCambio)}</TableCell>
                      </TableRow>
                      
                      <TableRow>
                        <TableCell><strong>Total en Caja</strong></TableCell>
                        <TableCell align="right"><strong>{formatCurrency(corteData.totals.totalCobros - (corteData.totals.totalPropinas + corteData.totals.totalCambio))}</strong></TableCell>
                      </TableRow>
                      
                      <TableRow>
                        <TableCell>Cheques Totales</TableCell>
                        <TableCell align="right">{corteData.totals.totalCheques}</TableCell>
                      </TableRow>
                      
                      <TableRow>
                        <TableCell sx={{ color: Math.abs(corteData.totals.totalFueraDeBalance) >= 0.01 ? 'error.main' : 'inherit' }}>
                          <strong>Fuera de Balance Total</strong>
                        </TableCell>
                        <TableCell align="right" sx={{ color: Math.abs(corteData.totals.totalFueraDeBalance) >= 0.01 ? 'error.main' : 'inherit' }}>
                          <strong>{formatCurrency(corteData.totals.totalFueraDeBalance)}</strong>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                {corteData.totals.unbalancedCheques.length > 0 && (
                  <Box mt={3}>
                    <Typography variant="subtitle1" gutterBottom>
                      Cheques Fuera de Balance
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Fuera de Balance</TableCell>
                            <TableCell>Vendedor</TableCell>
                            <TableCell>Cajero</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {corteData.totals.unbalancedCheques.map((cheque, index) => (
                            <TableRow key={index}>
                              <TableCell>{cheque.id}</TableCell>
                              <TableCell sx={{ color: 'error.main' }}>{formatCurrency(cheque.fueraDeBalance)}</TableCell>
                              <TableCell>{cheque.nombre_vendedor}</TableCell>
                              <TableCell>{cheque.nombre_cajero}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Box>
            )}

            {activeTab === 'vendedores' && (
              <>
                <Box display="flex" alignItems="center" mb={2}>
                  <TextField
                    label="Buscar vendedor/cajero"
                    variant="outlined"
                    size="small"
                    sx={{ width: 300 }}
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                  />
                  
                  <Tooltip title="Filtros avanzados">
                    <IconButton 
                      onClick={() => setShowFilters(!showFilters)}
                      sx={{ ml: 1 }}
                    >
                      <FilterList color={showFilters ? "primary" : "inherit"} />
                    </IconButton>
                  </Tooltip>
                  
                  <Box ml={2} display="flex" gap={1}>
                    <Chip 
                      label={`Vendedores: ${filteredUsers.filter(u => u.tipo === 'Vendedor').length}`} 
                      color="primary" 
                      variant="outlined"
                    />
                    <Chip 
                      label={`Cajeros activos: ${filteredUsers.filter(u => u.tipo === 'Cajero').length}`} 
                      color="secondary" 
                      variant="outlined"
                    />
                  </Box>
                </Box>
                
                <Collapse in={showFilters}>
                  <Box mb={2} p={2} bgcolor="#f5f5f5" borderRadius={1}>
                    <Typography variant="subtitle2" gutterBottom>Filtros avanzados</Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                      <TextField
                        select
                        label="Tipo de usuario"
                        value={userTypeFilter}
                        onChange={(e) => setUserTypeFilter(e.target.value)}
                        size="small"
                        sx={{ minWidth: 150 }}
                      >
                        <MenuItem value="all">Todos</MenuItem>
                        <MenuItem value="vendedor">Vendedores</MenuItem>
                        <MenuItem value="cajero">Cajeros</MenuItem>
                      </TextField>
                    </Box>
                  </Box>
                </Collapse>
                
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Nombre</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell align="right">Ventas</TableCell>
                        <TableCell align="right">Ventas sin Impuestos</TableCell>
                        <TableCell align="right">Impuestos</TableCell>
                        <TableCell align="right">Cargo Servicio (CS)</TableCell>
                        <TableCell align="right">Propinas</TableCell>
                        <TableCell align="right">Cobros</TableCell>
                        <TableCell align="right">Cambio</TableCell>
                        <TableCell align="right">En Caja</TableCell>
                        <TableCell align="right">Fuera de Balance</TableCell>
                        <TableCell align="right">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredUsers.map((user) => (
                        <React.Fragment key={user.id}>
                          <TableRow 
                            sx={{ 
                              '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                              '&:hover': { backgroundColor: '#f0f0f0' }
                            }}
                          >
                            <TableCell>
                              <Box display="flex" alignItems="center">
                                {user.tipo === 'Vendedor' ? 
                                  <People color="primary" /> : 
                                  <AttachMoney color="secondary" />}
                                <Box ml={1}>{user.nombre}</Box>
                                {((user.tipo === 'Vendedor' && corteData.users.some(u => u.tipo === 'Cajero' && u.nombre === user.nombre)) ||
                                  (user.tipo === 'Cajero' && corteData.users.some(u => u.tipo === 'Vendedor' && u.nombre === user.nombre))) && (
                                  <Tooltip title="También actúa como cajero/vendedor">
                                    <Chip 
                                      label="Ambos" 
                                      size="small" 
                                      color="warning" 
                                      sx={{ ml: 1, height: 20, '& .MuiChip-label': { px: 1, fontSize: '0.7rem' } }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={user.tipo} 
                                color={user.tipo === 'Vendedor' ? 'primary' : 'secondary'} 
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right">{formatCurrency(user.ventas)}</TableCell>
                            <TableCell align="right">{formatCurrency(user.ventasSinImpuestos)}</TableCell>
                            <TableCell align="right">{formatCurrency(user.impuestos)}</TableCell>
                            <TableCell align="right">{formatCurrency(user.cargoServicio)}</TableCell>
                            <TableCell align="right">{formatCurrency(user.propinas)}</TableCell>
                            <TableCell align="right">{formatCurrency(user.cobros)}</TableCell>
                            <TableCell align="right">{formatCurrency(user.cambio)}</TableCell>
                            <TableCell align="right">
                              <strong>{formatCurrency(user.enCaja)}</strong>
                            </TableCell>
                            <TableCell align="right" sx={{ color: Math.abs(user.fueraDeBalance) >= 0.01 ? 'error.main' : 'inherit' }}>
                              <strong>{formatCurrency(user.fueraDeBalance)}</strong>
                            </TableCell>
                            <TableCell align="right">
                              <Box display="flex" alignItems="center" justifyContent="flex-end">
                                <Tooltip title="Ver detalles">
                                  <IconButton 
                                    onClick={() => handleExpandUser(user.id)} 
                                    size="small"
                                  >
                                    {expandedUser === user.id ? <ExpandLess /> : <ExpandMore />}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Imprimir reporte">
                                  <IconButton 
                                    onClick={() => handlePrintCorte(user.id, user)}
                                    disabled={printStatus[user.id]?.includes('Imprimiendo')}
                                    color="primary"
                                  >
                                    <Print />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              {printStatus[user.id] && (
                                <Typography variant="caption" color="textSecondary" display="block">
                                  {printStatus[user.id]}
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                          
                          <TableRow>
                            <TableCell colSpan={12} sx={{ p: 0, borderBottom: expandedUser === user.id ? '1px solid rgba(224, 224, 224, 1)' : 0 }}>
                              <Collapse in={expandedUser === user.id} timeout="auto" unmountOnExit>
                                <Box p={2} bgcolor="#fafafa">
                                  <Typography variant="subtitle1" gutterBottom>
                                    Detalles de {user.nombre} - {selectedDate}
                                  </Typography>
                                  <Divider sx={{ mb: 2 }} />
                                  
                                  <Box mb={3}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Métodos de Pago
                                    </Typography>
                                    <Box display="flex" flexWrap="wrap" gap={2}>
                                      {Object.entries(user.paymentDetails).map(([method, details]) => (
                                        <Paper key={method} sx={{ p: 2, minWidth: 200 }}>
                                          <Typography variant="subtitle2">{method}</Typography>
                                          <Typography>Cobros: {formatCurrency(details.cobros)}</Typography>
                                          <Typography>Propinas: {formatCurrency(details.propinas)}</Typography>
                                          <Typography>Transacciones: {details.count}</Typography>
                                        </Paper>
                                      ))}
                                    </Box>
                                  </Box>
                                  
                                  {user.chequesVentas.length > 0 && (
                                    <Box mb={3}>
                                      <Typography variant="subtitle2" gutterBottom>
                                        Cheques Vendidos ({user.chequesVentas.length})
                                      </Typography>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell>ID</TableCell>
                                            <TableCell>Fecha</TableCell>
                                            <TableCell>Total</TableCell>
                                            <TableCell>Propina</TableCell>
                                            <TableCell>CS</TableCell>
                                            <TableCell>Impuestos</TableCell>
                                            <TableCell>Fuera de Balance</TableCell>
                                            <TableCell>Vendedor</TableCell>
                                            <TableCell>Cajero</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {user.chequesVentas.map((cheque, index) => (
                                            <TableRow key={index}>
                                              <TableCell>
                                                {cheque.id || 'N/A'}
                                                {!cheque.isBalanced && (
                                                  <Warning color="error" sx={{ ml: 1, verticalAlign: 'middle' }} />
                                                )}
                                              </TableCell>
                                              <TableCell>{cheque.fecha_control || 'N/A'}</TableCell>
                                              <TableCell>{formatCurrency(cheque.total_cheque)}</TableCell>
                                              <TableCell>{formatCurrency(cheque.propinas)}</TableCell>
                                              <TableCell>{formatCurrency(cheque.cargoServicio)}</TableCell>
                                              <TableCell>{formatCurrency(cheque.impuestos)}</TableCell>
                                              <TableCell sx={{ color: !cheque.isBalanced ? 'error.main' : 'inherit' }}>
                                                {formatCurrency(cheque.fueraDeBalance)}
                                              </TableCell>
                                              <TableCell>{cheque.nombre_vendedor}</TableCell>
                                              <TableCell>{cheque.nombre_cajero}</TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </Box>
                                  )}
                                  
                                  {user.chequesCobros.length > 0 && (
                                    <Box>
                                      <Typography variant="subtitle2" gutterBottom>
                                        Cheques Cobrados ({user.chequesCobros.length})
                                      </Typography>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell>ID</TableCell>
                                            <TableCell>Fecha</TableCell>
                                            <TableCell>Total</TableCell>
                                            <TableCell>Propina</TableCell>
                                            <TableCell>CS</TableCell>
                                            <TableCell>Impuestos</TableCell>
                                            <TableCell>Fuera de Balance</TableCell>
                                            <TableCell>Vendedor</TableCell>
                                            <TableCell>Cajero</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {user.chequesCobros.map((cheque, index) => (
                                            <TableRow key={index}>
                                              <TableCell>
                                                {cheque.id || 'N/A'}
                                                {!cheque.isBalanced && (
                                                  <Warning color="error" sx={{ ml: 1, verticalAlign: 'middle' }} />
                                                )}
                                              </TableCell>
                                              <TableCell>{cheque.fecha_control || 'N/A'}</TableCell>
                                              <TableCell>{formatCurrency(cheque.total_cheque)}</TableCell>
                                              <TableCell>{formatCurrency(cheque.propinas)}</TableCell>
                                              <TableCell>{formatCurrency(cheque.cargoServicio)}</TableCell>
                                              <TableCell>{formatCurrency(cheque.impuestos)}</TableCell>
                                              <TableCell sx={{ color: !cheque.isBalanced ? 'error.main' : 'inherit' }}>
                                                {formatCurrency(cheque.fueraDeBalance)}
                                              </TableCell>
                                              <TableCell>{cheque.nombre_vendedor}</TableCell>
                                              <TableCell>{cheque.nombre_cajero}</TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </Box>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {activeTab === 'pagos' && (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Método de Pago</TableCell>
                      <TableCell align="right">Transacciones</TableCell>
                      <TableCell align="right">Total Cobrado</TableCell>
                      <TableCell align="right">Total Propinas</TableCell>
                      <TableCell align="right">% del Total</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(corteData.paymentMethods).map(([method, details]) => (
                      <React.Fragment key={method}>
                        <TableRow>
                          <TableCell>{method}</TableCell>
                          <TableCell align="right">{details.count}</TableCell>
                          <TableCell align="right">{formatCurrency(details.cobros)}</TableCell>
                          <TableCell align="right">{formatCurrency(details.propinas)}</TableCell>
                          <TableCell align="right">
                            {corteData.totals.totalCobros > 0 ? 
                              `${((details.cobros / corteData.totals.totalCobros) * 100).toFixed(2)}%` : 
                              '0%'}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Ver detalles">
                              <IconButton 
                                onClick={() => handleExpandPaymentMethod(method)} 
                                size="small"
                              >
                                {expandedPaymentMethod === method ? <ExpandLess /> : <ExpandMore />}
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={6} sx={{ p: 0, borderBottom: expandedPaymentMethod === method ? '1px solid rgba(224, 224, 224, 1)' : 0 }}>
                            <Collapse in={expandedPaymentMethod === method} timeout="auto" unmountOnExit>
                              <Box p={2} bgcolor="#fafafa">
                                <Typography variant="subtitle2" gutterBottom>
                                  Detalles de Cheques - {method}
                                </Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>ID</TableCell>
                                      <TableCell>Fecha</TableCell>
                                      <TableCell>Total</TableCell>
                                      <TableCell>Propina</TableCell>
                                      <TableCell>CS</TableCell>
                                      <TableCell>Impuestos</TableCell>
                                      <TableCell>Fuera de Balance</TableCell>
                                      <TableCell>Vendedor</TableCell>
                                      <TableCell>Cajero</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {details.cheques.map((cheque, index) => (
                                      <TableRow key={index}>
                                        <TableCell>
                                          {cheque.id || 'N/A'}
                                          {!cheque.isBalanced && (
                                            <Warning color="error" sx={{ ml: 1, verticalAlign: 'middle' }} />
                                          )}
                                        </TableCell>
                                        <TableCell>{cheque.fecha_control || 'N/A'}</TableCell>
                                        <TableCell>{formatCurrency(cheque.total_cheque)}</TableCell>
                                        <TableCell>{formatCurrency(cheque.propinas)}</TableCell>
                                        <TableCell>{formatCurrency(cheque.cargoServicio)}</TableCell>
                                        <TableCell>{formatCurrency(cheque.impuestos)}</TableCell>
                                        <TableCell sx={{ color: !cheque.isBalanced ? 'error.main' : 'inherit' }}>
                                          {formatCurrency(cheque.fueraDeBalance)}
                                        </TableCell>
                                        <TableCell>{cheque.nombre_vendedor}</TableCell>
                                        <TableCell>{cheque.nombre_cajero}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell><strong>Total</strong></TableCell>
                      <TableCell align="right"><strong>
                        {Object.values(corteData.paymentMethods).reduce((sum, method) => sum + method.count, 0)}
                      </strong></TableCell>
                      <TableCell align="right"><strong>
                        {formatCurrency(corteData.totals.totalCobros)}
                      </strong></TableCell>
                      <TableCell align="right"><strong>
                        {formatCurrency(corteData.totals.totalPropinas)}
                      </strong></TableCell>
                      <TableCell align="right"><strong>100%</strong></TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {activeTab === 'ingresos' && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Ingresos por Centro de Ingresos - {selectedDate}
                </Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Centro de Ingresos</TableCell>
                        <TableCell align="right">Ventas Totales</TableCell>
                        <TableCell align="right">Impuestos</TableCell>
                        <TableCell align="right">Descuentos</TableCell>
                        <TableCell align="right">Cargo Servicio (CS)</TableCell>
                        <TableCell align="right">Cheques</TableCell>
                        <TableCell align="right">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(corteData.ingresos.byRevenue).map(([revenue, details]) => (
                        <React.Fragment key={revenue}>
                          <TableRow>
                            <TableCell>{revenue}</TableCell>
                            <TableCell align="right">{formatCurrency(details.total)}</TableCell>
                            <TableCell align="right">{formatCurrency(details.impuestos)}</TableCell>
                            <TableCell align="right">{formatCurrency(details.descuentos)}</TableCell>
                            <TableCell align="right">{formatCurrency(details.cargoServicio)}</TableCell>
                            <TableCell align="right">{details.cheques.length}</TableCell>
                            <TableCell align="right">
                              <Tooltip title="Ver detalles">
                                <IconButton 
                                  onClick={() => handleExpandRevenue(revenue)} 
                                  size="small"
                                >
                                  {expandedRevenue === revenue ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={7} sx={{ p: 0, borderBottom: expandedRevenue === revenue ? '1px solid rgba(224, 224, 224, 1)' : 0 }}>
                              <Collapse in={expandedRevenue === revenue} timeout="auto" unmountOnExit>
                                <Box p={2} bgcolor="#fafafa">
                                  <Typography variant="subtitle1" gutterBottom>
                                    Detalles de {revenue}
                                  </Typography>
                                  <Divider sx={{ mb: 2 }} />
                                  <Typography variant="subtitle2" gutterBottom>
                                    Productos Vendidos
                                  </Typography>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Producto</TableCell>
                                        <TableCell align="right">Cantidad</TableCell>
                                        <TableCell align="right">Total</TableCell>
                                        <TableCell align="right">Impuestos</TableCell>
                                        <TableCell align="right">Descuentos</TableCell>
                                        <TableCell align="right">CS</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {Object.entries(details.productos).map(([nombre, producto]) => (
                                        <TableRow key={nombre}>
                                          <TableCell>{nombre}</TableCell>
                                          <TableCell align="right">{producto.cantidad}</TableCell>
                                          <TableCell align="right">{formatCurrency(producto.total)}</TableCell>
                                          <TableCell align="right">{formatCurrency(producto.impuestos)}</TableCell>
                                          <TableCell align="right">{formatCurrency(producto.descuentos)}</TableCell>
                                          <TableCell align="right">{formatCurrency(producto.cargoServicio)}</TableCell>
                                        </TableRow>
                                      ))}
                                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableCell><strong>Total</strong></TableCell>
                                        <TableCell align="right">
                                          <strong>
                                            {Object.values(details.productos).reduce((sum, p) => sum + p.cantidad, 0)}
                                          </strong>
                                        </TableCell>
                                        <TableCell align="right">
                                          <strong>{formatCurrency(details.total)}</strong>
                                        </TableCell>
                                        <TableCell align="right">
                                          <strong>{formatCurrency(details.impuestos)}</strong>
                                        </TableCell>
                                        <TableCell align="right">
                                          <strong>{formatCurrency(details.descuentos)}</strong>
                                        </TableCell>
                                        <TableCell align="right">
                                          <strong>{formatCurrency(details.cargoServicio)}</strong>
                                        </TableCell>
                                      </TableRow>
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))}
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell><strong>Total General</strong></TableCell>
                        <TableCell align="right">
                          <strong>{formatCurrency(corteData.ingresos.totalVentas)}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{formatCurrency(corteData.ingresos.totalImpuestos)}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{formatCurrency(corteData.ingresos.totalDescuentos)}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{formatCurrency(corteData.ingresos.totalCargoServicio)}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>
                            {Object.values(corteData.ingresos.byRevenue).reduce((sum, rev) => sum + rev.cheques.length, 0)}
                          </strong>
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {activeTab === 'analisis' && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Análisis de Cheques - {selectedDate}
                </Typography>
                {Object.entries(analisisByRevenue).map(([revenue, cheques]) => (
                  <Box key={revenue} mb={4}>
                    <Typography variant="subtitle1" gutterBottom>
                      {revenue}
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>ID Cheque</TableCell>
                            <TableCell>Timezone</TableCell>
                            <TableCell align="right">Total</TableCell>
                            <TableCell align="right">Impuestos</TableCell>
                            <TableCell align="right">Descuentos</TableCell>
                            <TableCell align="right">CS</TableCell>
                            <TableCell align="right">Fuera de Balance</TableCell>
                            <TableCell align="right">Acciones</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {cheques.map((cheque) => (
                            <React.Fragment key={cheque.id}>
                              <TableRow>
                                <TableCell>
                                  {cheque.id}
                                  {!cheque.isBalanced && (
                                    <Warning color="error" sx={{ ml: 1, verticalAlign: 'middle' }} />
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Chip label={cheque.timezone} size="small" variant="outlined" />
                                </TableCell>
                                <TableCell align="right">{formatCurrency(cheque.ventas.total)}</TableCell>
                                <TableCell align="right">{formatCurrency(cheque.ventas.impuestos)}</TableCell>
                                <TableCell align="right">{formatCurrency(cheque.ventas.descuentos)}</TableCell>
                                <TableCell align="right">{formatCurrency(cheque.ventas.cargoServicio)}</TableCell>
                                <TableCell align="right" sx={{ color: !cheque.isBalanced ? 'error.main' : 'inherit' }}>
                                  {formatCurrency(cheque.fueraDeBalance)}
                                </TableCell>
                                <TableCell align="right">
                                  <Box display="flex" alignItems="center" justifyContent="flex-end">
                                    <Tooltip title="Ver detalles">
                                      <IconButton 
                                        onClick={() => handleExpandCheque(cheque.id)} 
                                        size="small"
                                      >
                                        {expandedCheque === cheque.id ? <ExpandLess /> : <ExpandMore />}
                                      </IconButton>
                                    </Tooltip>
                                    {!cheque.isBalanced && (
                                      <Tooltip title="Ajustar cheque">
                                        <IconButton 
                                          onClick={() => handleOpenAdjustDialog(cheque.id)}
                                          color="primary"
                                        >
                                          <Adjust />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell colSpan={8} sx={{ p: 0, borderBottom: expandedCheque === cheque.id ? '1px solid rgba(224, 224, 224, 1)' : 0 }}>
                                  <Collapse in={expandedCheque === cheque.id} timeout="auto" unmountOnExit>
                                    <Box p={2} bgcolor="#fafafa">
                                      <Typography variant="subtitle2" gutterBottom>
                                        Detalles del Cheque {cheque.id}
                                      </Typography>
                                      <Divider sx={{ mb: 2 }} />
                                      <Typography variant="subtitle2" gutterBottom>
                                        Productos
                                      </Typography>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell>Producto</TableCell>
                                            <TableCell align="right">Cantidad</TableCell>
                                            <TableCell align="right">Total</TableCell>
                                            <TableCell align="right">Impuestos</TableCell>
                                            <TableCell align="right">Descuentos</TableCell>
                                            <TableCell align="right">CS</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {cheque.productos.map((producto, index) => (
                                            <TableRow key={index}>
                                              <TableCell>{producto.nombre}</TableCell>
                                              <TableCell align="right">{producto.cantidad}</TableCell>
                                              <TableCell align="right">{formatCurrency(producto.sinImpuestos + producto.impuestos + (producto.cargoServicio || 0))}</TableCell>
                                              <TableCell align="right">{formatCurrency(producto.impuestos)}</TableCell>
                                              <TableCell align="right">{formatCurrency(producto.descuentos)}</TableCell>
                                              <TableCell align="right">{formatCurrency(producto.cargoServicio || 0)}</TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                                        Pagos
                                      </Typography>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell>Método</TableCell>
                                            <TableCell align="right">Monto</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {cheque.pagos.map((pago, index) => (
                                            <TableRow key={index}>
                                              <TableCell>{pago.nombre_pago}</TableCell>
                                              <TableCell align="right">{formatCurrency(pago.monto)}</TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ))}
              </Box>
            )}

{activeTab === 'consolidadas' && (
  <Box>
    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
      📊 Ventas Consolidadas por Revenue/Depto - {selectedDate}
    </Typography>
    
    {/* Selector de Revenue */}
    <Box mb={3} display="flex" alignItems="center" gap={2} flexWrap="wrap">
      <TextField
        select
        label="Seleccionar Revenue / Depto"
        value={selectedRevenueForCharts || ''}
        onChange={(e) => setSelectedRevenueForCharts(e.target.value)}
        size="small"
        sx={{ minWidth: 280, bgcolor: 'white', borderRadius: 1 }}
        SelectProps={{
          renderValue: (selected) => {
            const revenue = revenueConsolidatedData[selected];
            return (
              <Box display="flex" alignItems="center" gap={1}>
                <StorefrontIcon fontSize="small" color="primary" />
                <span>{selected}</span>
                {revenue && (
                  <Chip 
                    label={formatCurrency(revenue.totalVentas)} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                )}
              </Box>
            );
          }
        }}
      >
        {availableRevenues.map(revenue => (
          <MenuItem key={revenue} value={revenue}>
            <Box display="flex" justifyContent="space-between" width="100%" gap={2}>
              <span>{revenue}</span>
              <span style={{ color: '#2e7d32', fontWeight: 'bold' }}>
                {formatCurrency(revenueConsolidatedData[revenue]?.totalVentas || 0)}
              </span>
            </Box>
          </MenuItem>
        ))}
      </TextField>
      
      {selectedRevenueForCharts && revenueConsolidatedData[selectedRevenueForCharts] && (
        <Box display="flex" gap={1} flexWrap="wrap">
          <Chip label={`Cheques: ${revenueConsolidatedData[selectedRevenueForCharts].totalCheques}`} variant="outlined" size="small" />
          <Chip label={`Clientes: ${revenueConsolidatedData[selectedRevenueForCharts].totalClientes}`} variant="outlined" color="secondary" size="small" />
          <Chip label={`Items: ${revenueConsolidatedData[selectedRevenueForCharts].totalCantidad}`} variant="outlined" size="small" />
          <Chip label={`Ticket promedio: ${formatCurrency(
            revenueConsolidatedData[selectedRevenueForCharts].totalClientes > 0 
              ? revenueConsolidatedData[selectedRevenueForCharts].totalVentas / revenueConsolidatedData[selectedRevenueForCharts].totalClientes 
              : 0
          )}`} variant="outlined" color="info" size="small" />
        </Box>
      )}
    </Box>
    
    {!selectedRevenueForCharts || !revenueConsolidatedData[selectedRevenueForCharts] ? (
      <Paper sx={{ p: 4, textAlign: 'center', bgcolor: '#fafafa', borderRadius: 2 }}>
        <Typography color="textSecondary">
          Selecciona un Revenue/Depto para ver el dashboard consolidado
        </Typography>
      </Paper>
    ) : (
      <>
        {/* ============================================ */}
        {/* GRÁFICAS PROFESIONALES - 4 gráficas */}
        {/* ============================================ */}
        {organizarDatosJerarquicos.length > 0 && (
          <>
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 1, mb: 2, fontWeight: 'bold', color: '#37474f', borderLeft: '4px solid #0d47a1', pl: 2 }}>
              📈 ANÁLISIS GRÁFICO
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              
              {/* Gráfica 1: Top 10 Productos */}
              <Grid item xs={12} md={6} lg={3}>
                <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 3, height: '100%', borderTop: '4px solid #d32f2f' }}>
                  <Typography variant="subtitle1" gutterBottom align="center" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                    🏆 TOP 10 PRODUCTOS
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
                    {(() => {
                      const topProductos = Object.entries(revenueConsolidatedData[selectedRevenueForCharts]?.byProduct || {})
                        .map(([name, data]) => ({ name, value: data.total || 0, cantidad: data.cantidad || 0 }))
                        .sort((a, b) => b.value - a.value)
                        .slice(0, 10);
                      const maxValue = topProductos[0]?.value || 1;
                      return topProductos.map((prod, idx) => (
                        <Box key={prod.name} sx={{ mb: 2 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                            <Typography variant="body2" sx={{ width: '50%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: idx < 3 ? 'bold' : 'normal' }}>
                              {idx + 1}. {prod.name}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" sx={{ color: '#d32f2f' }}>{formatCurrency(prod.value)}</Typography>
                            <Typography variant="caption" color="textSecondary">({prod.cantidad})</Typography>
                          </Box>
                          <Box sx={{ width: '100%', bgcolor: '#e0e0e0', borderRadius: 1, height: 6 }}>
                            <Box sx={{ width: `${(prod.value / maxValue) * 100}%`, bgcolor: '#d32f2f', borderRadius: 1, height: 6 }} />
                          </Box>
                        </Box>
                      ));
                    })()}
                  </Box>
                </Paper>
              </Grid>
              
              {/* Gráfica 2: Ventas por Grupo */}
              <Grid item xs={12} md={6} lg={3}>
                <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 3, height: '100%', borderTop: '4px solid #1565c0' }}>
                  <Typography variant="subtitle1" gutterBottom align="center" sx={{ fontWeight: 'bold', color: '#1565c0' }}>
                    📊 VENTAS POR GRUPO
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
                    {gruposChartData.map((grupo, idx) => {
                      const maxValue = gruposChartData[0]?.value || 1;
                      const porcentajeGlobal = revenueConsolidatedData[selectedRevenueForCharts]?.totalVentas > 0 
                        ? (grupo.value / revenueConsolidatedData[selectedRevenueForCharts].totalVentas) * 100 : 0;
                      return (
                        <Box key={grupo.name} sx={{ mb: 2 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: idx < 3 ? 'bold' : 'normal' }}>{idx + 1}. {grupo.name}</Typography>
                            <Typography variant="body2" fontWeight="bold" sx={{ color: '#1565c0' }}>{formatCurrency(grupo.value)}</Typography>
                            <Typography variant="caption" color="textSecondary">({porcentajeGlobal.toFixed(1)}%)</Typography>
                          </Box>
                          <Box sx={{ width: '100%', bgcolor: '#e0e0e0', borderRadius: 1, height: 6 }}>
                            <Box sx={{ width: `${(grupo.value / maxValue) * 100}%`, bgcolor: '#1565c0', borderRadius: 1, height: 6 }} />
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Paper>
              </Grid>
              
              {/* Gráfica 3: Ventas por Categoría - NUEVA */}
              <Grid item xs={12} md={6} lg={3}>
                <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 3, height: '100%', borderTop: '4px solid #2e7d32' }}>
                  <Typography variant="subtitle1" gutterBottom align="center" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                    🏷️ VENTAS POR CATEGORÍA
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
                    {(() => {
                      // Calcular ventas por categoría
                      const categoriasMap = new Map();
                      organizarDatosJerarquicos.forEach(grupo => {
                        grupo.categorias.forEach(categoria => {
                          const catName = categoria.nombre;
                          const catTotal = categoria.totalVentas;
                          if (!categoriasMap.has(catName)) {
                            categoriasMap.set(catName, { total: 0 });
                          }
                          categoriasMap.get(catName).total += catTotal;
                        });
                      });
                      const categoriasData = Array.from(categoriasMap.entries())
                        .map(([name, data]) => ({ name, value: data.total }))
                        .sort((a, b) => b.value - a.value);
                      const maxValue = categoriasData[0]?.value || 1;
                      const totalGeneral = revenueConsolidatedData[selectedRevenueForCharts]?.totalVentas || 0;
                      
                      return categoriasData.map((cat, idx) => {
                        const porcentaje = totalGeneral > 0 ? (cat.value / totalGeneral) * 100 : 0;
                        return (
                          <Box key={cat.name} sx={{ mb: 2 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: idx < 3 ? 'bold' : 'normal' }}>{idx + 1}. {cat.name}</Typography>
                              <Typography variant="body2" fontWeight="bold" sx={{ color: '#2e7d32' }}>{formatCurrency(cat.value)}</Typography>
                              <Typography variant="caption" color="textSecondary">({porcentaje.toFixed(1)}%)</Typography>
                            </Box>
                            <Box sx={{ width: '100%', bgcolor: '#e0e0e0', borderRadius: 1, height: 6 }}>
                              <Box sx={{ width: `${(cat.value / maxValue) * 100}%`, bgcolor: '#2e7d32', borderRadius: 1, height: 6 }} />
                            </Box>
                          </Box>
                        );
                      });
                    })()}
                  </Box>
                </Paper>
              </Grid>
              
              {/* Gráfica 4: Distribución por Grupo (Torta) */}
              <Grid item xs={12} md={6} lg={3}>
                <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 3, height: '100%', borderTop: '4px solid #6a1b9a' }}>
                  <Typography variant="subtitle1" gutterBottom align="center" sx={{ fontWeight: 'bold', color: '#6a1b9a' }}>
                    🥧 DISTRIBUCIÓN POR GRUPO
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie 
                        data={gruposChartData} 
                        cx="50%" cy="50%" 
                        label={({ name, percent }) => percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ''}
                        outerRadius={80} 
                        dataKey="value"
                        labelLine={false}
                      >
                        {gruposChartData.map((entry, idx) => (
                          <Cell key={`cell-${idx}`} fill={chartColors[idx % chartColors.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                      <Legend layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ fontSize: 10 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            </Grid>
          </>
        )}
        
        {/* ============================================ */}
        {/* LISTA JERÁRQUICA: CATEGORÍA → GRUPO → PRODUCTOS */}
        {/* ============================================ */}
        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, mb: 2, fontWeight: 'bold', color: '#37474f', borderLeft: '4px solid #0d47a1', pl: 2 }}>
          📂 DESGLOSE DETALLADO - Haz clic para expandir
        </Typography>
        
        {(() => {
          // Reorganizar datos: primero por CATEGORÍA, luego por GRUPO
          const porCategoria = new Map();
          
          organizarDatosJerarquicos.forEach(grupo => {
            grupo.categorias.forEach(categoria => {
              const catName = categoria.nombre;
              if (!porCategoria.has(catName)) {
                porCategoria.set(catName, {
                  nombre: catName,
                  totalVentasBrutas: 0,
                  totalDescuentos: 0,
                  totalImpuestos: 0,
                  totalCS: 0,
                  totalVentas: 0,
                  totalCantidad: 0,
                  grupos: []
                });
              }
              const catData = porCategoria.get(catName);
              
              // Sumar datos de la categoría desde los productos
              categoria.productos.forEach(prod => {
                catData.totalVentasBrutas += prod.ventasSinDescuento || 0;
                catData.totalDescuentos += prod.descuentos || 0;
                catData.totalImpuestos += prod.impuestos || 0;
                catData.totalCS += prod.cargoServicio || 0;
                catData.totalVentas += prod.total || 0;
                catData.totalCantidad += prod.cantidad || 0;
              });
              
              // Calcular datos del grupo dentro de la categoría
              const grupoTotalVentasBrutas = categoria.productos.reduce((sum, p) => sum + (p.ventasSinDescuento || 0), 0);
              const grupoTotalDescuentos = categoria.productos.reduce((sum, p) => sum + (p.descuentos || 0), 0);
              const grupoTotalImpuestos = categoria.productos.reduce((sum, p) => sum + (p.impuestos || 0), 0);
              const grupoTotalCS = categoria.productos.reduce((sum, p) => sum + (p.cargoServicio || 0), 0);
              const grupoTotalVentas = categoria.productos.reduce((sum, p) => sum + (p.total || 0), 0);
              const grupoTotalCantidad = categoria.productos.reduce((sum, p) => sum + (p.cantidad || 0), 0);
              
              catData.grupos.push({
                nombre: grupo.nombre,
                totalVentasBrutas: grupoTotalVentasBrutas,
                totalDescuentos: grupoTotalDescuentos,
                totalImpuestos: grupoTotalImpuestos,
                totalCS: grupoTotalCS,
                totalVentas: grupoTotalVentas,
                totalCantidad: grupoTotalCantidad,
                productos: categoria.productos
              });
            });
          });
          
          const categoriasOrdenadas = Array.from(porCategoria.values()).sort((a, b) => b.totalVentas - a.totalVentas);
          const totalGeneralVentas = revenueConsolidatedData[selectedRevenueForCharts]?.totalVentas || 0;
          
          return categoriasOrdenadas.map((categoria) => {
            const porcentajeCategoria = totalGeneralVentas > 0 ? (categoria.totalVentas / totalGeneralVentas) * 100 : 0;
            
            return (
              <Paper key={categoria.nombre} sx={{ mb: 2, overflow: 'hidden', borderRadius: 2, boxShadow: 3 }}>
                {/* CABECERA DE CATEGORÍA */}
                <Box 
                  sx={{ 
                    p: 2, 
                    bgcolor: '#1b5e20', 
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    '&:hover': { bgcolor: '#0d5302' }
                  }}
                  onClick={() => toggleCategory(categoria.nombre)}
                >
                  <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>📁 {categoria.nombre}</Typography>
                    <Chip 
                      label={formatCurrency(categoria.totalVentas)} 
                      size="small" 
                      sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }}
                    />
                    <Chip 
                      label={`${categoria.totalCantidad} items`} 
                      size="small" 
                      sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                    />
                    <Chip 
                      label={`${categoria.grupos.length} grupos`} 
                      size="small" 
                      sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }}
                    />
                    <Chip 
                      label={`${porcentajeCategoria.toFixed(1)}%`} 
                      size="small" 
                      sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }}
                    />
                  </Box>
                  {expandedCategories[categoria.nombre] ? <ExpandLess sx={{ fontSize: 30 }} /> : <ExpandMore sx={{ fontSize: 30 }} />}
                </Box>
                
                {/* FILA DE TOTALES DE CATEGORÍA - CON TÍTULOS */}
                <Box sx={{ p: 1.5, bgcolor: '#c8e6c9', borderBottom: '2px solid #1b5e20' }}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={2}><Typography variant="body2" fontWeight="bold" sx={{ color: '#1b5e20' }}>📊 TOTAL {categoria.nombre}</Typography></Grid>
                    <Grid item xs={1.3}><Typography variant="body2" align="right" fontWeight="bold">Ventas Brutas</Typography></Grid>
                    <Grid item xs={1.2}><Typography variant="body2" align="right" sx={{ color: '#d32f2f' }}>Descuentos</Typography></Grid>
                    <Grid item xs={1}><Typography variant="body2" align="right">Impuestos</Typography></Grid>
                    <Grid item xs={1}><Typography variant="body2" align="right">CS</Typography></Grid>
                    <Grid item xs={1.5}><Typography variant="body2" align="right" fontWeight="bold" sx={{ color: '#1b5e20' }}>Ventas Totales</Typography></Grid>
                    <Grid item xs={1}><Typography variant="body2" align="right">Cantidad</Typography></Grid>
                    <Grid item xs={1}><Typography variant="body2" align="right" fontWeight="bold">%</Typography></Grid>
                  </Grid>
                  <Divider sx={{ my: 1 }} />
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={2}><Typography variant="body2" fontWeight="bold">{categoria.nombre}</Typography></Grid>
                    <Grid item xs={1.3}><Typography variant="body2" align="right">{formatCurrency(categoria.totalVentasBrutas)}</Typography></Grid>
                    <Grid item xs={1.2}><Typography variant="body2" align="right" sx={{ color: '#d32f2f' }}>{formatCurrency(categoria.totalDescuentos)}</Typography></Grid>
                    <Grid item xs={1}><Typography variant="body2" align="right">{formatCurrency(categoria.totalImpuestos)}</Typography></Grid>
                    <Grid item xs={1}><Typography variant="body2" align="right">{formatCurrency(categoria.totalCS)}</Typography></Grid>
                    <Grid item xs={1.5}><Typography variant="body2" align="right" fontWeight="bold" sx={{ color: '#1b5e20' }}>{formatCurrency(categoria.totalVentas)}</Typography></Grid>
                    <Grid item xs={1}><Typography variant="body2" align="right">{categoria.totalCantidad}</Typography></Grid>
                    <Grid item xs={1}><Typography variant="body2" align="right" fontWeight="bold">{porcentajeCategoria.toFixed(1)}%</Typography></Grid>
                  </Grid>
                </Box>
                
                {/* CONTENIDO DE CATEGORÍA - GRUPOS */}
                <Collapse in={expandedCategories[categoria.nombre]}>
                  <Box sx={{ p: 2, bgcolor: '#fafafa' }}>
                    
                    {/* LISTA DE GRUPOS dentro de la categoría */}
                    {categoria.grupos.map((grupo) => {
                      const porcentajeGrupo = categoria.totalVentas > 0 ? (grupo.totalVentas / categoria.totalVentas) * 100 : 0;
                      
                      return (
                        <Paper key={`${categoria.nombre}-${grupo.nombre}`} sx={{ mb: 2, overflow: 'hidden', borderRadius: 1, boxShadow: 2 }}>
                          {/* CABECERA DE GRUPO */}
                          <Box 
                            sx={{ 
                              p: 1.5, 
                              bgcolor: '#0d47a1', 
                              color: 'white',
                              cursor: 'pointer',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: 1,
                              '&:hover': { bgcolor: '#0a3b8a' }
                            }}
                            onClick={() => toggleGroup(`${categoria.nombre}-${grupo.nombre}`)}
                          >
                            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>🏢 {grupo.nombre}</Typography>
                              <Chip 
                                label={formatCurrency(grupo.totalVentas)} 
                                size="small" 
                                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }}
                              />
                              <Chip 
                                label={`${grupo.totalCantidad} items`} 
                                size="small" 
                                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                              />
                              <Chip 
                                label={`${grupo.productos.length} productos`} 
                                size="small" 
                                sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }}
                              />
                              <Chip 
                                label={`${porcentajeGrupo.toFixed(1)}%`} 
                                size="small" 
                                sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }}
                              />
                            </Box>
                            {expandedGroups[`${categoria.nombre}-${grupo.nombre}`] ? <ExpandLess sx={{ fontSize: 30 }} /> : <ExpandMore sx={{ fontSize: 30 }} />}
                          </Box>
                          
                          {/* FILA DE TOTALES DEL GRUPO - CON TÍTULOS */}
                          <Box sx={{ p: 1, bgcolor: '#e3f2fd', borderBottom: '2px solid #0d47a1' }}>
                            <Grid container spacing={1} alignItems="center">
                              <Grid item xs={2}><Typography variant="body2" fontWeight="bold" sx={{ color: '#0d47a1' }}>📊 TOTAL {grupo.nombre}</Typography></Grid>
                              <Grid item xs={1.3}><Typography variant="body2" align="right" fontWeight="bold">Ventas Brutas</Typography></Grid>
                              <Grid item xs={1.2}><Typography variant="body2" align="right" sx={{ color: '#d32f2f' }}>Descuentos</Typography></Grid>
                              <Grid item xs={1}><Typography variant="body2" align="right">Impuestos</Typography></Grid>
                              <Grid item xs={1}><Typography variant="body2" align="right">CS</Typography></Grid>
                              <Grid item xs={1.5}><Typography variant="body2" align="right" fontWeight="bold" sx={{ color: '#0d47a1' }}>Ventas Totales</Typography></Grid>
                              <Grid item xs={1}><Typography variant="body2" align="right">Cantidad</Typography></Grid>
                              <Grid item xs={1}><Typography variant="body2" align="right" fontWeight="bold">%</Typography></Grid>
                            </Grid>
                            <Divider sx={{ my: 1 }} />
                            <Grid container spacing={1} alignItems="center">
                              <Grid item xs={2}><Typography variant="body2" fontWeight="bold">{grupo.nombre}</Typography></Grid>
                              <Grid item xs={1.3}><Typography variant="body2" align="right">{formatCurrency(grupo.totalVentasBrutas)}</Typography></Grid>
                              <Grid item xs={1.2}><Typography variant="body2" align="right" sx={{ color: '#d32f2f' }}>{formatCurrency(grupo.totalDescuentos)}</Typography></Grid>
                              <Grid item xs={1}><Typography variant="body2" align="right">{formatCurrency(grupo.totalImpuestos)}</Typography></Grid>
                              <Grid item xs={1}><Typography variant="body2" align="right">{formatCurrency(grupo.totalCS)}</Typography></Grid>
                              <Grid item xs={1.5}><Typography variant="body2" align="right" fontWeight="bold" sx={{ color: '#0d47a1' }}>{formatCurrency(grupo.totalVentas)}</Typography></Grid>
                              <Grid item xs={1}><Typography variant="body2" align="right">{grupo.totalCantidad}</Typography></Grid>
                              <Grid item xs={1}><Typography variant="body2" align="right" fontWeight="bold">{porcentajeGrupo.toFixed(1)}%</Typography></Grid>
                            </Grid>
                          </Box>
                          
                          {/* PRODUCTOS DEL GRUPO - TABLA COMPLETA */}
                          <Collapse in={expandedGroups[`${categoria.nombre}-${grupo.nombre}`]}>
                            <Box sx={{ p: 2 }}>
                              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, borderRadius: 1 }}>
                                <Table size="small" stickyHeader>
                                  <TableHead>
                                    <TableRow sx={{ backgroundColor: '#263238' }}>
                                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Producto</TableCell>
                                      <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Ventas Brutas</TableCell>
                                      <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Descuentos</TableCell>
                                      <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Impuestos</TableCell>
                                      <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>CS</TableCell>
                                      <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Ventas Totales</TableCell>
                                      <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Cantidad</TableCell>
                                      <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>%</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {grupo.productos
                                      .sort((a, b) => (b.total || 0) - (a.total || 0))
                                      .map((producto) => {
                                        const porcentaje = grupo.totalVentas > 0 
                                          ? ((producto.total || 0) / grupo.totalVentas) * 100 
                                          : 0;
                                        return (
                                          <TableRow key={producto.nombre} hover>
                                            <TableCell>{producto.nombre}</TableCell>
                                            <TableCell align="right">{formatCurrency(producto.ventasSinDescuento || 0)}</TableCell>
                                            <TableCell align="right" sx={{ color: '#d32f2f' }}>{formatCurrency(producto.descuentos || 0)}</TableCell>
                                            <TableCell align="right">{formatCurrency(producto.impuestos || 0)}</TableCell>
                                            <TableCell align="right">{formatCurrency(producto.cargoServicio || 0)}</TableCell>
                                            <TableCell align="right">
                                              <strong style={{ color: '#2e7d32' }}>{formatCurrency(producto.total || 0)}</strong>
                                            </TableCell>
                                            <TableCell align="right">{producto.cantidad || 0}</TableCell>
                                            <TableCell align="right">
                                              <Chip label={`${porcentaje.toFixed(1)}%`} size="small" variant="outlined" />
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                  </TableBody>
                                  <TableFooter>
                                    <TableRow sx={{ backgroundColor: '#c8e6c9' }}>
                                      <TableCell><strong>TOTAL {grupo.nombre}</strong></TableCell>
                                      <TableCell align="right"><strong>{formatCurrency(grupo.totalVentasBrutas)}</strong></TableCell>
                                      <TableCell align="right"><strong>{formatCurrency(grupo.totalDescuentos)}</strong></TableCell>
                                      <TableCell align="right"><strong>{formatCurrency(grupo.totalImpuestos)}</strong></TableCell>
                                      <TableCell align="right"><strong>{formatCurrency(grupo.totalCS)}</strong></TableCell>
                                      <TableCell align="right"><strong style={{ color: '#2e7d32' }}>{formatCurrency(grupo.totalVentas)}</strong></TableCell>
                                      <TableCell align="right"><strong>{grupo.totalCantidad}</strong></TableCell>
                                      <TableCell align="right"><strong>100%</strong></TableCell>
                                    </TableRow>
                                  </TableFooter>
                                </Table>
                              </TableContainer>
                            </Box>
                          </Collapse>
                        </Paper>
                      );
                    })}
                  </Box>
                </Collapse>
              </Paper>
            );
          });
        })()}
        
        {/* ============================================ */}
        {/* RESUMEN FINAL PROFESIONAL */}
        {/* ============================================ */}
        <Paper sx={{ p: 2.5, mt: 2, bgcolor: '#0d47a1', color: 'white', borderRadius: 2, boxShadow: 3 }}>
          <Grid container spacing={2} alignItems="center" justifyContent="space-between">
            <Grid item xs={12} md={3}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>🏆 TOTAL GENERAL</Typography>
              <Typography variant="subtitle2">{selectedRevenueForCharts}</Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="caption">Ventas Totales</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{formatCurrency(revenueConsolidatedData[selectedRevenueForCharts]?.totalVentas || 0)}</Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="caption">Cantidad Items</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{revenueConsolidatedData[selectedRevenueForCharts]?.totalCantidad || 0}</Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="caption">Cheques</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{revenueConsolidatedData[selectedRevenueForCharts]?.totalCheques}</Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="caption">Clientes</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{revenueConsolidatedData[selectedRevenueForCharts]?.totalClientes}</Typography>
            </Grid>
          </Grid>
        </Paper>
      </>
    )}
  </Box>
)}

{activeTab === 'daybook' && (
  <Paper sx={{ p: 2, mt: 2, overflow: 'auto' }}>
    <Typography variant="h6" gutterBottom>
      📋 Daybook - Clasificación por Revenue / Categoría / Modo (Turno)
      <Typography component="span" variant="body2" color="textSecondary" sx={{ ml: 2 }}>
        ({selectedDate})
      </Typography>
    </Typography>
    
    {/* ============================================== */}
    {/* CHEQUE INICIAL Y FINAL */}
    {/* ============================================== */}
    <Box mb={3} p={2} sx={{ bgcolor: '#e3f2fd', borderRadius: 2, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      <Box>
        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>🏁 CHEQUE INICIAL</Typography>
        <Typography variant="h5" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#1565c0' }}>
          #{daybookData?.chequeInicial || 'N/A'}
        </Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>🏁 CHEQUE FINAL</Typography>
        <Typography variant="h5" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#1565c0' }}>
          #{daybookData?.chequeFinal || 'N/A'}
        </Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>📊 TOTAL CHEQUES</Typography>
        <Typography variant="h5" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#2e7d32' }}>
          {daybookData?.numeroCheques || 0}
        </Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>👥 CLIENTES ÚNICOS</Typography>
        <Typography variant="h5" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#ed6c02' }}>
          {daybookMatrixData?.resumenGeneral?.totalClientes || 0}
        </Typography>
      </Box>
    </Box>

    {/* ============================================== */}
    {/* VERIFICAR SI HAY DATOS */}
    {/* ============================================== */}
    {!daybookMatrixData.matrix || Object.keys(daybookMatrixData.matrix).length === 0 ? (
      <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#fff3e0' }}>
        <Warning color="warning" sx={{ fontSize: 48, mb: 2 }} />
        <Typography color="textSecondary">No hay datos disponibles para el Daybook en la fecha seleccionada</Typography>
        <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
          Verifica que las ventas tengan productos y categorías asignadas
        </Typography>
      </Paper>
    ) : (
      <>
        {/* ============================================== */}
        {/* TABLA PRINCIPAL DEL DAYBOOK */}
        {/* ============================================== */}
        <TableContainer sx={{ maxHeight: '60vh', border: '1px solid #e0e0e0', borderRadius: 1 }}>
          <Table size="small" stickyHeader sx={{ minWidth: 1400 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell rowSpan={2} sx={{ fontWeight: 'bold', border: '1px solid #ddd', minWidth: 150, backgroundColor: '#f5f5f5' }}>
                  Revenue / Categoría
                </TableCell>
                <TableCell rowSpan={2} sx={{ fontWeight: 'bold', border: '1px solid #ddd', minWidth: 120, backgroundColor: '#f5f5f5' }}>
                  Concepto
                </TableCell>
                
                {daybookMatrixData.sortedModos?.map(modo => (
                  <TableCell key={modo} colSpan={2} align="center" 
                    sx={{ fontWeight: 'bold', border: '1px solid #ddd', backgroundColor: '#e3f2fd' }}>
                    {modo}
                  </TableCell>
                ))}
                <TableCell colSpan={2} align="center" 
                  sx={{ fontWeight: 'bold', border: '1px solid #ddd', backgroundColor: '#bbdefb' }}>
                  TOTALES
                </TableCell>
              </TableRow>
              
              <TableRow sx={{ backgroundColor: '#fafafa' }}>
                {daybookMatrixData.sortedModos?.map((modo, idx) => (
                  <React.Fragment key={`sub-${idx}`}>
                    <TableCell align="right" sx={{ border: '1px solid #ddd', fontSize: '0.7rem', p: 0.5 }}>Valor</TableCell>
                    <TableCell align="right" sx={{ border: '1px solid #ddd', fontSize: '0.7rem', p: 0.5 }}>%</TableCell>
                  </React.Fragment>
                ))}
                <TableCell align="right" sx={{ border: '1px solid #ddd', fontSize: '0.7rem', p: 0.5 }}>Valor</TableCell>
                <TableCell align="right" sx={{ border: '1px solid #ddd', fontSize: '0.7rem', p: 0.5 }}>%</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {Object.entries(daybookMatrixData.matrix).map(([revenueName, categories]) => {
                // Calcular totales del Revenue Center (suma de todas sus categorías)
                let revenueTotalGanancia = 0;
                // IMPORTANTE: Los clientes del Revenue NO se suman por categoría
                // Se obtienen de clientesPorRevenue que viene del Map
                let revenueTotalClientes = daybookMatrixData.clientesPorRevenue?.get(revenueName) || 0;
                let revenueTotalCheques = 0;
                
                // Totales por turno dentro del Revenue
                const revenuePorTurno = {};
                
                Object.entries(categories).forEach(([categoriaName, modosData]) => {
                  const totalesData = modosData['Totales'];
                  if (totalesData) {
                    revenueTotalGanancia += totalesData.ganancia || 0;
                    revenueTotalCheques += totalesData.count || 0;
                  }
                  // Acumular por turno (solo ganancia, NO clientes)
                  daybookMatrixData.sortedModos?.forEach(modo => {
                    const modoData = modosData[modo];
                    if (modoData) {
                      if (!revenuePorTurno[modo]) {
                        revenuePorTurno[modo] = { ganancia: 0 };
                      }
                      revenuePorTurno[modo].ganancia += modoData.ganancia || 0;
                    }
                  });
                });
                
                // Agregar clientes por turno desde clientesPorTurnoRevenue
                daybookMatrixData.sortedModos?.forEach(modo => {
                  const clientesTurno = daybookMatrixData.clientesPorTurnoRevenue?.get(`${revenueName}|${modo}`) || 0;
                  if (revenuePorTurno[modo]) {
                    revenuePorTurno[modo].clientes = clientesTurno;
                  } else {
                    revenuePorTurno[modo] = { ganancia: 0, clientes: clientesTurno };
                  }
                });
                
                const revenueChequePromedio = revenueTotalClientes > 0 ? revenueTotalGanancia / revenueTotalClientes : 0;
                
                return (
                  <React.Fragment key={revenueName}>
                    {/* Título del Revenue Center */}
                    <TableRow sx={{ backgroundColor: '#d1c4e9', '& td': { fontWeight: 'bold', fontSize: '0.9rem', border: '1px solid #ddd' } }}>
                      <TableCell colSpan={2 + (daybookMatrixData.sortedModos?.length || 0) * 2 + 2}>
                        🏢 {revenueName.toUpperCase()}
                      </TableCell>
                    </TableRow>

                    {/* Renderizar CADA categoría dinámicamente */}
                    {Object.entries(categories).map(([categoriaName, modosData]) => {
                      const totalesData = modosData['Totales'];
                      const totalValue = totalesData?.total || 0;
                      const gananciaValue = totalesData?.ganancia || 0;
                      // NOTA: Los clientes por categoría son solo REFERENCIA, NO se usan para el cheque promedio del Revenue
                      const clientesCategoria = totalesData?.clientes || 0;
                      const chequePromedioCategoria = clientesCategoria > 0 ? gananciaValue / clientesCategoria : 0;
                      
                      return (
                        <React.Fragment key={`${revenueName}-${categoriaName}`}>
                          {/* Subtítulo de categoría */}
                          <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                            <TableCell colSpan={2 + (daybookMatrixData.sortedModos?.length || 0) * 2 + 2} sx={{ fontWeight: 'bold', border: '1px solid #ddd' }}>
                              📂 {categoriaName.toUpperCase()}
                            </TableCell>
                          </TableRow>
                          
                          {/* Fila: Ventas Brutas */}
                          <TableRow hover>
                            <TableCell sx={{ border: '1px solid #ddd' }}>{categoriaName}</TableCell>
                            <TableCell sx={{ border: '1px solid #ddd' }}>💰 Ventas Brutas</TableCell>
                            {daybookMatrixData.sortedModos?.map(modo => {
                              const modoData = modosData[modo];
                              const val = modoData?.gross || 0;
                              const pct = totalValue > 0 ? (val / totalValue) * 100 : 0;
                              return (
                                <React.Fragment key={modo}>
                                  <TableCell align="right" sx={{ border: '1px solid #ddd' }}>{formatCurrency(val)}</TableCell>
                                  <TableCell align="right" sx={{ border: '1px solid #ddd', fontSize: '0.75rem', color: 'text.secondary' }}>{pct.toFixed(1)}%</TableCell>
                                </React.Fragment>
                              );
                            })}
                            <TableCell align="right" sx={{ border: '1px solid #ddd', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>{formatCurrency(totalesData?.gross || 0)}</TableCell>
                            <TableCell align="right" sx={{ border: '1px solid #ddd', backgroundColor: '#f5f5f5' }}>100%</TableCell>
                          </TableRow>
                          
                          {/* Fila: Descuentos */}
                          <TableRow hover>
                            <TableCell sx={{ border: '1px solid #ddd' }}>{categoriaName}</TableCell>
                            <TableCell sx={{ border: '1px solid #ddd' }}>📉 Descuentos</TableCell>
                            {daybookMatrixData.sortedModos?.map(modo => {
                              const modoData = modosData[modo];
                              const val = modoData?.discount || 0;
                              const pct = totalValue > 0 ? (val / totalValue) * 100 : 0;
                              return (
                                <React.Fragment key={modo}>
                                  <TableCell align="right" sx={{ border: '1px solid #ddd' }}>{formatCurrency(val)}</TableCell>
                                  <TableCell align="right" sx={{ border: '1px solid #ddd', fontSize: '0.75rem', color: 'text.secondary' }}>{pct.toFixed(1)}%</TableCell>
                                </React.Fragment>
                              );
                            })}
                            <TableCell align="right" sx={{ border: '1px solid #ddd', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>{formatCurrency(totalesData?.discount || 0)}</TableCell>
                            <TableCell align="right" sx={{ border: '1px solid #ddd', backgroundColor: '#f5f5f5' }}>100%</TableCell>
                          </TableRow>
                          
                          {/* Fila: GANANCIA (Ventas Netas sin impuestos) - DESTACADA */}
                          <TableRow hover sx={{ backgroundColor: '#fff8e1' }}>
                            <TableCell sx={{ border: '1px solid #ddd' }}>{categoriaName}</TableCell>
                            <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>💰 {categoriaName} (Neto sin Impuestos)</TableCell>
                            {daybookMatrixData.sortedModos?.map(modo => {
                              const modoData = modosData[modo];
                              const val = modoData?.ganancia || 0;
                              const pct = totalValue > 0 ? (val / totalValue) * 100 : 0;
                              return (
                                <React.Fragment key={modo}>
                                  <TableCell align="right" sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>{formatCurrency(val)}</TableCell>
                                  <TableCell align="right" sx={{ border: '1px solid #ddd', fontSize: '0.75rem', color: 'text.secondary' }}>{pct.toFixed(1)}%</TableCell>
                                </React.Fragment>
                              );
                            })}
                            <TableCell align="right" sx={{ border: '1px solid #ddd', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>{formatCurrency(totalesData?.ganancia || 0)}</TableCell>
                            <TableCell align="right" sx={{ border: '1px solid #ddd', backgroundColor: '#f5f5f5' }}>100%</TableCell>
                          </TableRow>
                          
                          {/* Fila: Impuestos */}
                          <TableRow hover>
                            <TableCell sx={{ border: '1px solid #ddd' }}>{categoriaName}</TableCell>
                            <TableCell sx={{ border: '1px solid #ddd' }}>📊 Impuestos</TableCell>
                            {daybookMatrixData.sortedModos?.map(modo => {
                              const modoData = modosData[modo];
                              const val = modoData?.tax || 0;
                              const pct = totalValue > 0 ? (val / totalValue) * 100 : 0;
                              return (
                                <React.Fragment key={modo}>
                                  <TableCell align="right" sx={{ border: '1px solid #ddd' }}>{formatCurrency(val)}</TableCell>
                                  <TableCell align="right" sx={{ border: '1px solid #ddd', fontSize: '0.75rem', color: 'text.secondary' }}>{pct.toFixed(1)}%</TableCell>
                                </React.Fragment>
                              );
                            })}
                            <TableCell align="right" sx={{ border: '1px solid #ddd', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>{formatCurrency(totalesData?.tax || 0)}</TableCell>
                            <TableCell align="right" sx={{ border: '1px solid #ddd', backgroundColor: '#f5f5f5' }}>100%</TableCell>
                          </TableRow>
                          
                          {/* Fila: Cargo Servicio */}
                          <TableRow hover>
                            <TableCell sx={{ border: '1px solid #ddd' }}>{categoriaName}</TableCell>
                            <TableCell sx={{ border: '1px solid #ddd' }}>🎯 Cargo Servicio</TableCell>
                            {daybookMatrixData.sortedModos?.map(modo => {
                              const modoData = modosData[modo];
                              const val = modoData?.cs || 0;
                              const pct = totalValue > 0 ? (val / totalValue) * 100 : 0;
                              return (
                                <React.Fragment key={modo}>
                                  <TableCell align="right" sx={{ border: '1px solid #ddd' }}>{formatCurrency(val)}</TableCell>
                                  <TableCell align="right" sx={{ border: '1px solid #ddd', fontSize: '0.75rem', color: 'text.secondary' }}>{pct.toFixed(1)}%</TableCell>
                                </React.Fragment>
                              );
                            })}
                            <TableCell align="right" sx={{ border: '1px solid #ddd', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>{formatCurrency(totalesData?.cs || 0)}</TableCell>
                            <TableCell align="right" sx={{ border: '1px solid #ddd', backgroundColor: '#f5f5f5' }}>100%</TableCell>
                          </TableRow>
                          
                          {/* Fila: TOTAL por categoría (con impuestos y CS) */}
                          <TableRow hover sx={{ backgroundColor: '#c8e6c9' }}>
                            <TableCell sx={{ border: '1px solid #ddd' }}>{categoriaName}</TableCell>
                            <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>💵 TOTAL {categoriaName.toUpperCase()}</TableCell>
                            {daybookMatrixData.sortedModos?.map(modo => {
                              const modoData = modosData[modo];
                              const val = modoData?.total || 0;
                              const pct = totalValue > 0 ? (val / totalValue) * 100 : 0;
                              return (
                                <React.Fragment key={modo}>
                                  <TableCell align="right" sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>{formatCurrency(val)}</TableCell>
                                  <TableCell align="right" sx={{ border: '1px solid #ddd', fontSize: '0.75rem', color: 'text.secondary' }}>{pct.toFixed(1)}%</TableCell>
                                </React.Fragment>
                              );
                            })}
                            <TableCell align="right" sx={{ border: '1px solid #ddd', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>{formatCurrency(totalesData?.total || 0)}</TableCell>
                            <TableCell align="right" sx={{ border: '1px solid #ddd', backgroundColor: '#f5f5f5' }}>100%</TableCell>
                          </TableRow>
                          
                          {/* Fila: Clientes por categoría - SOLO REFERENCIA (no suma para el Revenue) */}
                          <TableRow hover sx={{ backgroundColor: '#e8eaf6' }}>
                            <TableCell sx={{ border: '1px solid #ddd' }}>{categoriaName}</TableCell>
                            <TableCell sx={{ border: '1px solid #ddd' }}>👥 Clientes (referencia)</TableCell>
                            {daybookMatrixData.sortedModos?.map(modo => {
                              const modoData = modosData[modo];
                              const val = modoData?.clientes || 0;
                              return (
                                <React.Fragment key={modo}>
                                  <TableCell align="right" sx={{ border: '1px solid #ddd' }}>
                                    <strong>{val}</strong>
                                    <Typography variant="caption" display="block" color="textSecondary">
                                      comensales
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right" sx={{ border: '1px solid #ddd', fontSize: '0.75rem', color: 'text.secondary' }}>-</TableCell>
                                </React.Fragment>
                              );
                            })}
                            <TableCell align="right" sx={{ border: '1px solid #ddd', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                              <strong>{clientesCategoria}</strong>
                              <Typography variant="caption" display="block" color="error" sx={{ fontSize: '0.6rem' }}>
                                (puede tener duplicados)
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ border: '1px solid #ddd', backgroundColor: '#f5f5f5' }}>-</TableCell>
                          </TableRow>
                          
                          {/* Fila: CHEQUE PROMEDIO por categoría */}
                          <TableRow hover sx={{ backgroundColor: '#fff3e0' }}>
                            <TableCell sx={{ border: '1px solid #ddd' }}>{categoriaName}</TableCell>
                            <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>💵 CHEQUE PROMEDIO (referencia)</TableCell>
                            {daybookMatrixData.sortedModos?.map(modo => {
                              const modoData = modosData[modo];
                              const gananciaModo = modoData?.ganancia || 0;
                              const clientesModo = modoData?.clientes || 0;
                              const promedioModo = clientesModo > 0 ? gananciaModo / clientesModo : 0;
                              return (
                                <React.Fragment key={modo}>
                                  <TableCell align="right" sx={{ border: '1px solid #ddd', fontWeight: 'bold', color: '#ed6c02' }}>
                                    {formatCurrency(promedioModo)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ border: '1px solid #ddd', fontSize: '0.75rem', color: 'text.secondary' }}>-</TableCell>
                                </React.Fragment>
                              );
                            })}
                            <TableCell align="right" sx={{ border: '1px solid #ddd', fontWeight: 'bold', backgroundColor: '#f5f5f5', color: '#ed6c02' }}>
                              {formatCurrency(chequePromedioCategoria)}
                            </TableCell>
                            <TableCell align="right" sx={{ border: '1px solid #ddd', backgroundColor: '#f5f5f5' }}>-</TableCell>
                          </TableRow>
                          
                          {/* Fila: Items Vendidos */}
                          <TableRow hover>
                            <TableCell sx={{ border: '1px solid #ddd' }}>{categoriaName}</TableCell>
                            <TableCell sx={{ border: '1px solid #ddd' }}>📦 Items Vendidos</TableCell>
                            {daybookMatrixData.sortedModos?.map(modo => {
                              const modoData = modosData[modo];
                              const val = modoData?.count || 0;
                              return (
                                <React.Fragment key={modo}>
                                  <TableCell align="right" sx={{ border: '1px solid #ddd' }}>{val.toLocaleString()}</TableCell>
                                  <TableCell align="right" sx={{ border: '1px solid #ddd', fontSize: '0.75rem', color: 'text.secondary' }}>-</TableCell>
                                </React.Fragment>
                              );
                            })}
                            <TableCell align="right" sx={{ border: '1px solid #ddd', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>{(totalesData?.count || 0).toLocaleString()}</TableCell>
                            <TableCell align="right" sx={{ border: '1px solid #ddd', backgroundColor: '#f5f5f5' }}>-</TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                    
                    {/* ============================================== */}
                    {/* RESUMEN DEL REVENUE CENTER - CORREGIDO */}
                    {/* ============================================== */}
                    <TableRow sx={{ backgroundColor: '#f3e5f5', '& td': { fontWeight: 'bold', border: '1px solid #ddd' } }}>
                      <TableCell colSpan={2 + (daybookMatrixData.sortedModos?.length || 0) * 2 + 2}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                          <span>📊 RESUMEN {revenueName.toUpperCase()}</span>
                          <span>💰 Ventas Netas: {formatCurrency(revenueTotalGanancia)}</span>
                          <span>👥 Clientes: {revenueTotalClientes}</span>
                          <span>💵 Cheque Promedio: {formatCurrency(revenueChequePromedio)}</span>
                        </Box>
                      </TableCell>
                    </TableRow>
                    
                    {/* Resumen por Turno dentro del Revenue - CORREGIDO */}
                    <TableRow sx={{ backgroundColor: '#ede7f6' }}>
                      <TableCell colSpan={2 + (daybookMatrixData.sortedModos?.length || 0) * 2 + 2}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                          <span style={{ fontWeight: 'bold' }}>🕒 Por Turno en {revenueName}:</span>
                          {daybookMatrixData.sortedModos?.map(modo => {
                            const turnoData = revenuePorTurno[modo];
                            const gananciaTurno = turnoData?.ganancia || 0;
                            const clientesTurno = turnoData?.clientes || 0;
                            const promedioTurno = clientesTurno > 0 ? gananciaTurno / clientesTurno : 0;
                            return (
                              <span key={modo} style={{ fontSize: '0.85rem' }}>
                                <strong>{modo}</strong>: {formatCurrency(gananciaTurno)} | 👥 {clientesTurno} | 💵 {formatCurrency(promedioTurno)}
                              </span>
                            );
                          })}
                        </Box>
                      </TableCell>
                    </TableRow>
                    
                    {/* Separador entre revenues */}
                    <TableRow>
                      <TableCell colSpan={2 + (daybookMatrixData.sortedModos?.length || 0) * 2 + 2} sx={{ p: 0 }}>
                        <Divider sx={{ my: 1 }} />
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
              
              {/* ============================================== */}
              {/* FILA DE TOTALES GENERALES */}
              {/* ============================================== */}
              <TableRow sx={{ backgroundColor: '#ffecb3', fontWeight: 'bold' }}>
                <TableCell colSpan={2} sx={{ fontWeight: 'bold', border: '1px solid #ddd' }}>
                  🏆 TOTALES GENERALES
                </TableCell>
                
                {daybookMatrixData.sortedModos?.map(modo => {
                  let totalModoGanancia = 0;
                  let totalModoClientes = 0;
                  Object.values(daybookMatrixData.matrix).forEach(categories => {
                    Object.values(categories).forEach(modosData => {
                      const data = modosData[modo];
                      if (data) {
                        totalModoGanancia += data.ganancia || 0;
                      }
                    });
                  });
                  // Clientes por turno a nivel global
                  totalModoClientes = daybookMatrixData.clientesPorTurnoGlobal?.get(modo) || 0;
                  const pctModo = daybookMatrixData.resumenGeneral?.totalGanancia > 0 
                    ? (totalModoGanancia / daybookMatrixData.resumenGeneral.totalGanancia) * 100 
                    : 0;
                  
                  return (
                    <React.Fragment key={`total-${modo}`}>
                      <TableCell align="right" sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>
                        {formatCurrency(totalModoGanancia)}
                      </TableCell>
                      <TableCell align="right" sx={{ border: '1px solid #ddd', fontSize: '0.75rem' }}>
                        {pctModo.toFixed(1)}%
                      </TableCell>
                    </React.Fragment>
                  );
                })}
                
                <TableCell align="right" sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>
                  {formatCurrency(daybookMatrixData.resumenGeneral?.totalGanancia || 0)}
                </TableCell>
                <TableCell align="right" sx={{ border: '1px solid #ddd' }}>100%</TableCell>
              </TableRow>
              
              {/* Fila: Cheque Promedio General */}
              <TableRow sx={{ backgroundColor: '#ffecb3' }}>
                <TableCell colSpan={2} sx={{ fontWeight: 'bold', border: '1px solid #ddd' }}>
                  💵 CHEQUE PROMEDIO GENERAL
                </TableCell>
                
                {daybookMatrixData.sortedModos?.map(modo => {
                  let totalModoGanancia = 0;
                  let totalModoClientes = daybookMatrixData.clientesPorTurnoGlobal?.get(modo) || 0;
                  Object.values(daybookMatrixData.matrix).forEach(categories => {
                    Object.values(categories).forEach(modosData => {
                      const data = modosData[modo];
                      if (data) {
                        totalModoGanancia += data.ganancia || 0;
                      }
                    });
                  });
                  const promedioModo = totalModoClientes > 0 ? totalModoGanancia / totalModoClientes : 0;
                  return (
                    <React.Fragment key={`promedio-${modo}`}>
                      <TableCell align="right" sx={{ border: '1px solid #ddd', fontWeight: 'bold', color: '#ed6c02' }} colSpan={2}>
                        {formatCurrency(promedioModo)}
                      </TableCell>
                    </React.Fragment>
                  );
                })}
                
                <TableCell align="right" sx={{ border: '1px solid #ddd', fontWeight: 'bold', color: '#ed6c02' }} colSpan={2}>
                  {formatCurrency(daybookMatrixData.resumenGeneral?.chequePromedio || 0)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* ============================================== */}
        {/* RESUMEN POR MODO (TURNO) - AHORA POR REVENUE */}
        {/* ============================================== */}
        <Box mt={4}>
          <Typography variant="h6" gutterBottom>
            📊 Resumen por Turno - Desglosado por Revenue Center
            <Typography component="span" variant="caption" color="textSecondary" sx={{ ml: 2 }}>
              (Cheque Promedio = Ventas Netas sin impuestos / Clientes SIN duplicar)
            </Typography>
          </Typography>
          
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Revenue Center</TableCell>
                  {daybookMatrixData.sortedModos?.map(modo => (
                    <TableCell key={modo} align="center" colSpan={3} sx={{ backgroundColor: '#e3f2fd', fontWeight: 'bold' }}>
                      {modo}
                    </TableCell>
                  ))}
                  <TableCell align="center" colSpan={3} sx={{ backgroundColor: '#bbdefb', fontWeight: 'bold' }}>
                    TOTALES
                  </TableCell>
                </TableRow>
                <TableRow sx={{ backgroundColor: '#fafafa' }}>
                  <TableCell></TableCell>
                  {daybookMatrixData.sortedModos?.map(modo => (
                    <React.Fragment key={`sub-${modo}`}>
                      <TableCell align="right" sx={{ fontSize: '0.7rem' }}>Ventas Netas</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.7rem' }}>Clientes</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.7rem' }}>Cheque Promedio</TableCell>
                    </React.Fragment>
                  ))}
                  <TableCell align="right" sx={{ fontSize: '0.7rem' }}>Ventas Netas</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.7rem' }}>Clientes</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.7rem' }}>Cheque Promedio</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(daybookMatrixData.matrix).map(([revenueName, categories]) => {
                  // Calcular totales por turno para este Revenue
                  const revenuePorTurno = {};
                  let revenueTotalGanancia = 0;
                  let revenueTotalClientes = daybookMatrixData.clientesPorRevenue?.get(revenueName) || 0;
                  
                  Object.values(categories).forEach(modosData => {
                    daybookMatrixData.sortedModos?.forEach(modo => {
                      const data = modosData[modo];
                      if (data) {
                        if (!revenuePorTurno[modo]) {
                          revenuePorTurno[modo] = { ganancia: 0 };
                        }
                        revenuePorTurno[modo].ganancia += data.ganancia || 0;
                      }
                    });
                    const totalesData = modosData['Totales'];
                    if (totalesData) {
                      revenueTotalGanancia += totalesData.ganancia || 0;
                    }
                  });
                  
                  // Agregar clientes por turno
                  daybookMatrixData.sortedModos?.forEach(modo => {
                    const clientesTurno = daybookMatrixData.clientesPorTurnoRevenue?.get(`${revenueName}|${modo}`) || 0;
                    if (revenuePorTurno[modo]) {
                      revenuePorTurno[modo].clientes = clientesTurno;
                    } else {
                      revenuePorTurno[modo] = { ganancia: 0, clientes: clientesTurno };
                    }
                  });
                  
                  const revenueChequePromedio = revenueTotalClientes > 0 ? revenueTotalGanancia / revenueTotalClientes : 0;
                  
                  return (
                    <TableRow key={revenueName} hover>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f3e5f5' }}>
                        {revenueName}
                      </TableCell>
                      {daybookMatrixData.sortedModos?.map(modo => {
                        const turnoData = revenuePorTurno[modo] || { ganancia: 0, clientes: 0 };
                        const promedioTurno = turnoData.clientes > 0 ? turnoData.ganancia / turnoData.clientes : 0;
                        return (
                          <React.Fragment key={modo}>
                            <TableCell align="right">{formatCurrency(turnoData.ganancia)}</TableCell>
                            <TableCell align="right">{turnoData.clientes}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', color: '#ed6c02' }}>
                              {formatCurrency(promedioTurno)}
                            </TableCell>
                          </React.Fragment>
                        );
                      })}
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(revenueTotalGanancia)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>{revenueTotalClientes}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: '#ed6c02' }}>
                        {formatCurrency(revenueChequePromedio)}
                      </TableCell>
                    </TableRow>
                  );
                })}
                
                {/* Total General */}
                <TableRow sx={{ backgroundColor: '#ffecb3', fontWeight: 'bold' }}>
                  <TableCell><strong>🏆 TOTAL GENERAL</strong></TableCell>
                  {daybookMatrixData.sortedModos?.map(modo => {
                    let totalModoGanancia = 0;
                    let totalModoClientes = daybookMatrixData.clientesPorTurnoGlobal?.get(modo) || 0;
                    Object.values(daybookMatrixData.matrix).forEach(categories => {
                      Object.values(categories).forEach(modosData => {
                        const data = modosData[modo];
                        if (data) {
                          totalModoGanancia += data.ganancia || 0;
                        }
                      });
                    });
                    const promedioModo = totalModoClientes > 0 ? totalModoGanancia / totalModoClientes : 0;
                    return (
                      <React.Fragment key={`total-${modo}`}>
                        <TableCell align="right"><strong>{formatCurrency(totalModoGanancia)}</strong></TableCell>
                        <TableCell align="right"><strong>{totalModoClientes}</strong></TableCell>
                        <TableCell align="right" sx={{ color: '#ed6c02' }}><strong>{formatCurrency(promedioModo)}</strong></TableCell>
                      </React.Fragment>
                    );
                  })}
                  <TableCell align="right">
                    <strong>{formatCurrency(daybookMatrixData.resumenGeneral?.totalGanancia || 0)}</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{daybookMatrixData.resumenGeneral?.totalClientes || 0}</strong>
                  </TableCell>
                  <TableCell align="right" sx={{ color: '#ed6c02' }}>
                    <strong>{formatCurrency(daybookMatrixData.resumenGeneral?.chequePromedio || 0)}</strong>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </>
    )}
  </Paper>
)}

   <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)}>
              <DialogTitle>Exportar Corte de Caja</DialogTitle>
              <DialogContent>
                <TextField
                  select
                  label="Formato"
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value)}
                  fullWidth
                  margin="normal"
                >
                  <MenuItem value="csv">CSV</MenuItem>
                  <MenuItem value="pdf">PDF</MenuItem>
                </TextField>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setExportDialogOpen(false)}>Cancelar</Button>
                <Button onClick={handleExport} variant="contained" color="primary">
                  Exportar
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog open={adjustDialogOpen} onClose={handleCloseAdjustDialog}>
              <DialogTitle>Ajustar Cheque</DialogTitle>
              <DialogContent>
                <AjusteChequeForm 
                  numeroCheque={selectedCheque}
                  property={property}
                  onSuccess={handleAdjustSuccess}
                  onClose={handleCloseAdjustDialog}
                />
              </DialogContent>
            </Dialog>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

CorteDeCaja.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  t: PropTypes.object
};

export default CorteDeCaja;